import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useSyncExternalStore } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  BookOpen,
  ChevronDown,
  Download,
  Focus,
  FolderOpen,
  History,
  Info,
  Maximize2,
  Minus,
  Plus,
  Redo2,
  Search,
  Send,
  Undo2,
} from 'lucide-react';
import Tooltip from '@mui/material/Tooltip';
import { PageHeader } from '@ravenappdev/raven-ui';
import { AppIcon } from '../components/LucideIcon';
import './drawing-workspace-rail.css';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import PidCanvas, { type CanvasFocusRequest, type CanvasZoomRequest, type PendingBoxKind } from '../components/PidCanvas';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import PnidMarkDialog, { PnidDialogKind } from '../components/PnidMarkDialog';
import SlidePanel from '../components/SlidePanel';
import VersionHistoryModal from '../components/VersionHistoryModal';
import DrawingContextPanel, { type NoteTarget } from '../components/DrawingContextPanel';
import PublishCheckPanel from '../components/PublishCheckPanel';
// NodeOverviewDialog removed - functionality consolidated into PnidMarkDialog
// EquipmentOverviewDialog removed - functionality consolidated into PnidMarkDialog
import { useAuth } from '../contexts/AuthContext';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';
import { useInvalidateRegisterContext } from '../hooks/useRegisterContext';
import {
  findRedundancySet,
  redundancySetConflictOf,
  usePutRedundancySetTags,
} from '../hooks/useRedundancySets';
import { equipmentOnlySelectionIds, resolveRedundancySetSelection } from '../utils/redundancySetSelection';
import { splitCompoundEquipment } from '../utils/splitEquipment';
import type { CompoundSplitPart } from '../utils/splitEquipment';
import { useActionTracker } from '../hooks/useActionTracker';
import { useFormSchema } from '../hooks/useFormSchemas';
import { useAiData } from '../hooks/useAiData';
import { useSetPnidSetup } from '../hooks/useSetPnidSetup';
import { useLegendSheets } from '../hooks/useLegendSheets';
import { legendSheetRecordRoute } from '../plant/legendSheetRoutes';
import { useComments } from '../hooks/useComments';
import { usePublishableInstrumentIds } from '../hooks/usePublishableInstrumentIds';
import { useClickTargets } from '../hooks/useClickTargets';
import { copyIntoField, useFocusedEditorField } from './focusedEditorField';
import { canEditUser } from '../lib/auth';
import { documentApi, pnidApi, commentsApi, onDrawingWrite } from '../api';
import type { MarkingKind } from '../types';
import type { FormSchemaDefinition } from '../types/formSchema';
import { 
  BoundingBox, 
  PidDocument, 
  AnnotationInfo,
  PnidWizardState,
  DrawingClass,
  MarkingStatus,
  InstrumentCategory,
  PnidNode,
  NodePointCandidate,
  NodePointCandidateLabel,
  CanvasComment,
  PnidHyperlinkRef,
  instrumentRegions,
} from "../types";
import {
  appendClickTargetRegion,
  appendRegion,
  armDraft,
  canFinish,
  committedRegions,
  identifyRegion,
  removeLastRegion,
  withRegionAt,
} from "./boxDraft";
import type { BoxDraft, GeometryTarget } from "./boxDraft";
import { hideContainedClickTargets, retireUnderSavedRegions } from "./clickTargetGeometry";
import { DRAW_TOOL_KEY, DRAW_TOOL_LABEL, type ArmedTool } from "./armedTool";
import DrawToolLabel from "./DrawToolLabel";
import { activeAssetKind } from "./activeAssetKind";
import {
  attachEndInstrumentsToLine,
  buildEquipmentUpdatePayload,
  buildNodeUpdatePayload,
  getNodeLinePropertyPayload,
  getNodeLineTypePayload,
  nodeGeometryBbox,
  polylineToBbox,
} from "./elementSavePayloads";
import CommentPopover from "../components/CommentPopover";
import { refForEntity } from './entityRef';
import type { LineEnds, VertexBinding } from './lineDraft';
import LineSegmentReviewPopover from "../components/LineSegmentReviewPopover";
import type { DraftRequest } from "../hooks/useDraftValidation";
import InstrumentEditor from "../components/InstrumentEditor";
import { assetApi, instrumentApi } from "../api";
import type {
  ClickTarget,
  DrawingValidation,
  Finding,
  PnidInstrument,
  PnidInstrumentUpdate,
  PnidStateResponse,
} from "../types";
import { NO_FINDINGS } from "../types";
import {
  polylineIntersectsBbox,
} from "../utils/geometry";
import type { MergedLineSegment } from "../utils/geometry";
import { formatInstrumentIdentity } from "../utils/instrumentIdentity";
import {
  EDIT_EQUIPMENT_PARAM,
  EDIT_INSTRUMENT_PARAM,
  STEP_PARAM,
  buildPnidPath,
  parseEditEquipmentId,
  parseEditInstrumentId,
  parseTargetEquipmentId,
  parseTargetInstrumentId,
  parseTargetNodeId,
  parseStepParam,
} from "../utils/pnidDeepLink";
import { queryKeys } from '../queries/keys';
import {
  opensWithNeedsReview,
  opensWithSetupDock,
  resolveInitialRailTab,
  tabForInstrumentContext,
  winningRouteTarget,
} from './resolveInitialRailTab';
import WorkspaceSkeleton from './WorkspaceSkeleton';
import { navigationBoxOf } from './navigationBoxOf';
import DrawingSearchPalette, { type SearchPickAction } from './search/DrawingSearchPalette';
import { buildSearchIndex, searchHits, type SearchHit } from './search/searchIndex';
import { readRecentSearches, rememberRecentSearch } from './search/recentSearches';
import WorkspaceAssetRail from './WorkspaceAssetRail';
import { flaggedRowOrder, type AssetRailTab, type RailAssets } from './assetRailTabs';
import { agentReviewItems, agentReviewKey, parseAgentReviewKey } from './agentReviewItems';
import { isEventInsideEditorDock } from './dock/isEventInsideEditorDock';
import EditorDock from './dock/EditorDock';
import DockFooter from './dock/DockFooter';
import type { DockView } from './dock/dockView';
import { EditorDockSlotContext } from './dock/dockSlots';
import {
  diagramTypeLabel,
  documentMatchesSearch,
  getDocumentDiagramType,
  getDocumentPrimaryLabel,
  getDocumentSecondaryLabel,
} from '../utils/documentLabels';
import { documentDomainPathLabel } from '../utils/domainTree';
import { useDomainIndex } from '../hooks/useDomainIndex';
import { useEquipmentTypes } from '../hooks/useEquipmentTypes';
import {
  getDirectNodeContextInstruments,
  getEquipmentContextInstruments,
  getInstrumentEffectiveContext,
  getNodeContextInstruments,
} from '../utils/unifiedInstruments';

type PnidInstrumentKind = "equipmentInstrument" | "nodeInstrument";
type WizardSelectionKind = PnidDialogKind | PnidInstrumentKind | "controlLoop" | "relationship";
type WizardTargetKind = PnidDialogKind | PnidInstrumentKind;
type EditableWizardTargetKind = WizardTargetKind | "controlLoop";
// "node" targets adjust a duct node's persisted duct_bbox.
type PersistedBboxTarget = { kind: "equipment" | PnidInstrumentKind | "controlLoop" | "node"; id: string };
type PnidAnnotationBackendTarget = {
  kind: "equipment" | "instrument" | "node" | "controlLoop" | "relationship";
  id: string;
};
type PnidUndoEntry = {
  docId: string;
  label: string;
  state: PnidWizardState;
};
type AnnotationImportAdjustment = {
  sourceLabel: string;
  copiedSummary: string;
  replacedSummary: string;
  overwroteExisting: boolean;
  skippedRelationships: number;
  geometryAdjustmentText?: string;
};
type ImportGeometryTransform = {
  applied: boolean;
  reason: string;
  scale_x: number;
  scale_y: number;
  offset_x: number;
  offset_y: number;
  source_reference?: string | null;
  target_reference?: string | null;
  source_width?: number | null;
  source_height?: number | null;
  target_width?: number | null;
  target_height?: number | null;
};

const WIZARD_HISTORY_LIMIT = 50;

const clonePnidState = (state: PnidWizardState): PnidWizardState =>
  JSON.parse(JSON.stringify(state)) as PnidWizardState;

/** The id of the saved line or equipment the mark dialog edits, or undefined for a new one. */
const markDialogEditedId = (dialog: {
  editTarget?: { id: string } | null;
  initialValues?: { _markExistingId?: string };
}): string | undefined => dialog.editTarget?.id ?? dialog.initialValues?._markExistingId;

const wizardItemKey = (target: { kind: WizardSelectionKind; id: string }) =>
  `${target.kind}:${target.id}`;

const parseWizardItemKey = (key: string): { kind: WizardSelectionKind; id: string } | null => {
  const index = key.indexOf(":");
  if (index <= 0 || index === key.length - 1) return null;
  const kind = key.slice(0, index) as WizardSelectionKind;
  const id = key.slice(index + 1);
  if (
    kind !== "equipment" &&
    kind !== "equipmentInstrument" &&
    kind !== "nodeInstrument" &&
    kind !== "node" &&
    kind !== "controlLoop" &&
    kind !== "relationship"
  ) {
    return null;
  }
  return { kind, id };
};

const toBackendAnnotationTarget = (
  target: { kind: WizardSelectionKind; id: string }
): PnidAnnotationBackendTarget => {
  if (target.kind === "equipmentInstrument" || target.kind === "nodeInstrument") {
    return { kind: "instrument", id: target.id };
  }
  return { kind: target.kind, id: target.id } as PnidAnnotationBackendTarget;
};

const getInstrumentSelectionKind = (instrument: Pick<PnidInstrument, "context_type">): PnidInstrumentKind =>
  instrument.context_type === "node" ? "nodeInstrument" : "equipmentInstrument";

const getAllSavedAnnotationKeys = (state: PnidWizardState): Set<string> => {
  const keys = new Set<string>();
  for (const equipment of state.equipments) keys.add(wizardItemKey({ kind: "equipment", id: equipment.id }));
  for (const node of state.nodes) keys.add(wizardItemKey({ kind: "node", id: node.id }));
  for (const instrument of state.instruments) {
    keys.add(wizardItemKey({ kind: getInstrumentSelectionKind(instrument), id: instrument.id }));
  }
  for (const loop of state.control_loops) keys.add(wizardItemKey({ kind: "controlLoop", id: loop.id }));
  return keys;
};

const getNodeLineProperties = (node?: Partial<PnidNode> | null) => ({
  line_type: node?.line_type ?? "process",
  instrument_line_subtype:
    node?.line_type === "instrument" ? node.instrument_line_subtype ?? null : null,
  installation_type: node?.installation_type ?? null,
  is_jacketed: node?.is_jacketed ?? false,
  tracing_type: node?.tracing_type ?? null,
  flow_profile: node?.flow_profile ?? null,
  slope_ratio: node?.slope_ratio ?? null,
  is_two_phase: node?.is_two_phase ?? false,
  flow_direction: node?.flow_direction ?? null,
  primary_direction: node?.primary_direction ?? null,
});

/** The bound entity ids a saved line carries into the form (D8). */
const getNodeEndIds = (node?: Partial<PnidNode> | null) => ({
  source_id: node?.source_id ?? null,
  dest_id: node?.dest_id ?? null,
});

/** The line form's fields for `node`, or the blank form for a new line. */
const getLineFormValues = (node?: Partial<PnidNode> | null) => ({
  line_no: node?.line_no?.trim() ? node.line_no : "",
  source_ref: node?.source_ref ?? "",
  source_pnid: node?.source_pnid ?? "",
  dest_ref: node?.dest_ref ?? "",
  dest_pnid: node?.dest_pnid ?? "",
  ...getNodeLineProperties(node),
  ...getNodeEndIds(node),
  drain_type: node?.drain_type ?? null,
});

const PFD_EQUIPMENT_FIELD_KEYS = [
  "equipment_no",
  "equipment_type",
  "description",
  "notes",
  "connected_equipments",
  "linked_pnids",
] as const;

const PFD_NODE_FIELD_KEYS = [
  "line_no",
  "source_ref",
  "source_pnid",
  "dest_ref",
  "dest_pnid",
  "linked_pfd_streams",
  "instrument_line_subtype",
  "line_spec.description",
  "line_spec.continuity",
  "line_spec.phase",
  "line_spec.flow_rate",
  "line_spec.temperature",
  "line_spec.pressure",
] as const;

function buildPfdPayload(
  values: Record<string, any>,
  schema: FormSchemaDefinition | null,
  fallbackFieldKeys: readonly string[],
  baseBuckets: Record<string, Record<string, any>> = {}
) {
  const payload: Record<string, any> = {};
  const fieldKeys = schema?.sections.flatMap((section) => section.fields.map((field) => field.key)) ?? fallbackFieldKeys;
  for (const [bucket, bucketValue] of Object.entries(baseBuckets)) {
    payload[bucket] = { ...(bucketValue ?? {}) };
  }

  for (const key of fieldKeys) {
    if (!(key in values)) continue;
    const value = values[key];
    if (key.includes(".")) {
      const [bucket, subKey] = key.split(".", 2);
      payload[bucket] = { ...(payload[bucket] ?? {}), [subKey]: value };
    } else {
      payload[key] = value;
    }
  }

  return payload;
}

const ENABLE_NODE_POINT_CLASSIFICATION = false;

// Helper to check if two bounding boxes overlap
function bboxesOverlap(a: BoundingBox, b: BoundingBox): boolean {
  return a.x1 < b.x2 && a.x2 > b.x1 && a.y1 < b.y2 && a.y2 > b.y1;
}

function polylineToPoints(
  points: number[],
  minDistance: number = 2
): { x: number; y: number }[] {
  const output: { x: number; y: number }[] = [];
  for (let i = 0; i < points.length; i += 2) {
    const x = points[i];
    const y = points[i + 1];
    if (typeof x !== "number" || typeof y !== "number") continue;
    const last = output[output.length - 1];
    if (!last) {
      output.push({ x, y });
      continue;
    }
    const dist = Math.hypot(x - last.x, y - last.y);
    if (dist >= minDistance) {
      output.push({ x, y });
    }
  }
  return output;
}

function pointToBbox(
  point: { x: number; y: number },
  size: number = 18
): BoundingBox {
  const half = size / 2;
  return {
    x1: point.x - half,
    y1: point.y - half,
    x2: point.x + half,
    y2: point.y + half,
  };
}

type AnnotationCountLike = {
  equipments: number;
  nodes: number;
  instruments: number;
  controlLoops?: number;
  control_loops?: number;
  relationships: number;
};

function formatAnnotationCountSummary(counts: AnnotationCountLike): string {
  const controlLoops = counts.controlLoops ?? counts.control_loops ?? 0;
  return [
    { count: counts.equipments, singular: "equipment", plural: "equipment" },
    { count: counts.nodes, singular: "line", plural: "lines" },
    { count: counts.instruments, singular: "instrument", plural: "instruments" },
    { count: controlLoops, singular: "control loop", plural: "control loops" },
    { count: counts.relationships, singular: "relationship", plural: "relationships" },
  ]
    .filter((entry) => entry.count > 0)
    .map((entry) => `${entry.count} ${entry.count === 1 ? entry.singular : entry.plural}`)
    .join(", ");
}

function formatImportGeometryAdjustment(transform?: ImportGeometryTransform | null): string {
  if (!transform?.applied) return "";
  const sourceSize = transform.source_width && transform.source_height
    ? `${Math.round(transform.source_width)}x${Math.round(transform.source_height)}`
    : "";
  const targetSize = transform.target_width && transform.target_height
    ? `${Math.round(transform.target_width)}x${Math.round(transform.target_height)}`
    : "";
  if (sourceSize && targetSize) {
    return `Auto-scaled geometry from ${sourceSize} to ${targetSize}.`;
  }
  return "Auto-scaled geometry to match the target P&ID image.";
}

interface DrawingWorkspaceProps {
  /** The drawing being annotated. The shell owns the selection. */
  selectedDoc: PidDocument;
  /** Every drawing in the user's group, for hyperlinks and annotation import. */
  documents: PidDocument[];
  annotations: Record<string, AnnotationInfo>;
  effectiveUserName: string;
  isAdmin: boolean;
  /** Applies a field change to the shell's copy of a document. */
  onPatchDocument: (docId: string, patch: Partial<PidDocument>) => void;
  onOpenDocument: (
    doc: PidDocument,
    options?: { replace?: boolean; targetNodeId?: string | null; targetEquipmentId?: string | null },
  ) => void;
  onReloadDocuments: () => Promise<void>;
  onOpenFileManager: () => void;
}

/**
 * The drawing editor: canvas, marking wizard, review panels, and every dialog
 * that acts on one drawing. Mount it keyed on the document id — every piece of
 * per-drawing state below returns to its initial value by remounting.
 */

/**
 * Whether the draft marks an instrument: a new one, or a redraw or an added region of
 * a saved one. These drafts reach identify; every other redraw replaces one box.
 */
function marksAnInstrument(draft: BoxDraft): boolean {
  const target = draft.target;
  return !target || target.kind === 'equipmentInstrument' || target.kind === 'nodeInstrument';
}

/** The toolbar's draw buttons, in toolbar order. */
const DRAW_TOOL_BUTTONS: ReadonlyArray<{ tool: ArmedTool; title: string }> = [
  { tool: 'equipment', title: 'Draw equipment: each box you drag opens the equipment form' },
  { tool: 'instrument', title: 'Draw instruments: drag each region, then Done' },
  { tool: 'line', title: 'Draw a line: click each vertex, then Enter' },
  { tool: 'duct', title: 'Draw a duct: drag its box, then fill the line form' },
];

/**
 * Hands an Escape to the open editor dock's form, as if the SME typed it there. The form
 * and the dock read Escape on their own elements, so the key is dispatched on the form's
 * root and takes the path a key typed in the dock takes: a form nobody edited closes, an
 * edited form asks before it discards, and a dock without a confirm closes as its ✕ does.
 */
function escapeIntoOpenEditorDock() {
  const dockBody = document.querySelector('[data-editor-dock]:not([hidden]) > .pnid-editor-dock-body');
  const dockContent = dockBody?.firstElementChild ?? dockBody;
  dockContent?.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true, cancelable: true }));
}

/** One action in the workspace's left rail; states styled in drawing-workspace-rail.css. */
function WorkspaceRailItem({ icon, label, sub, variant, iconOnly, flagged, title, ...buttonProps }: {
  icon: LucideIcon;
  label: string;
  /** Second line, e.g. the import-replace warning. */
  sub?: string;
  /** CTAs take the shared DS button recipe; plain items stay action buttons. */
  variant?: 'contained' | 'outlined';
  /** Secondary actions collapse to a glyph so the band stays one row. */
  iconOnly?: boolean;
  /** Marks a collapsed glyph that carries a warning, e.g. import-replace. */
  flagged?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const button = (
    <button
      type="button"
      className={variant
        ? `pnid-btn pnid-btn--${variant} pnid-btn--sm pnid-ws-rail-cta`
        : iconOnly
        ? `pnid-ws-rail-item pnid-ws-rail-item--icon${flagged ? ' pnid-ws-rail-item--flagged' : ''}`
        : 'pnid-ws-rail-item'}
      aria-label={iconOnly ? label : undefined}
      /* MUI refuses a native title on a Tooltip child — the tooltip IS the
         title in icon-only mode, and the native one stays otherwise. */
      title={iconOnly ? undefined : title}
      {...buttonProps}
    >
      <AppIcon icon={icon} size={16} />
      {!iconOnly && (
        <span className="pnid-ws-rail-item-label">
          <span>{label}</span>
          {sub && <span className="pnid-ws-rail-item-sub">{sub}</span>}
        </span>
      )}
    </button>
  );
  /* An icon-only control must still name itself on hover and to a screen
     reader; the aria-label above covers AT, the tooltip covers sighted use. */
  if (!iconOnly) return button;
  /* A disabled button fires no pointer events, so the tooltip that says why it
     is disabled listens on a wrapper instead. */
  return (
    <Tooltip title={title || (sub ? `${label} — ${sub}` : label)} placement="bottom">
      {buttonProps.disabled ? <span className="pnid-ws-rail-item-disabled">{button}</span> : button}
    </Tooltip>
  );
}

/** A drawing a search pick opens, and the primary it was found as, for the recent searches. */
type PickedDrawing = { drawing: PidDocument; primary: string };

const IS_APPLE_PLATFORM = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
/** The palette's shortcut, as the toolbar shows it and as assistive tech announces it. */
const SEARCH_SHORTCUT = IS_APPLE_PLATFORM
  ? { label: '⌘F', aria: 'Meta+F' }
  : { label: 'Ctrl+F', aria: 'Control+F' };

/** The canvas's committed stage scale, readable by a component that subscribes to it. */
type CanvasScaleSource = {
  read: () => number;
  subscribe: (listener: () => void) => () => void;
  write: (scale: number) => void;
};

function createCanvasScaleSource(): CanvasScaleSource {
  let scale = 1;
  const listeners = new Set<() => void>();
  return {
    read: () => scale,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    write: (next) => {
      scale = next;
      listeners.forEach((listener) => listener());
    },
  };
}

/**
 * Zoom out, the zoom percent (a press resets to 100%), zoom in, and fit. The percent
 * subscribes to the scale itself: a wheel commits a new scale on every tick, and
 * holding it in the workspace's state would re-render the whole workspace per tick.
 */
function CanvasZoomControls({ scaleSource, onZoom }: {
  scaleSource: CanvasScaleSource;
  onZoom: (kind: CanvasZoomRequest['kind']) => void;
}) {
  const percent = Math.round(useSyncExternalStore(scaleSource.subscribe, scaleSource.read) * 100);
  return (
    <div className="pnid-ws-rail-pair" role="group" aria-label="Zoom">
      <WorkspaceRailItem icon={Minus} label="Zoom out" iconOnly onClick={() => onZoom('out')} />
      <Tooltip title="Reset zoom to 100%" placement="bottom">
        <button type="button" className="pnid-ws-rail-item pnid-ws-zoom-percent"
          aria-label={`Zoom ${percent}%. Reset to 100%`} onClick={() => onZoom('reset')}>
          {percent}%
        </button>
      </Tooltip>
      <WorkspaceRailItem icon={Plus} label="Zoom in" iconOnly onClick={() => onZoom('in')} />
      <WorkspaceRailItem icon={Maximize2} label="Fit to screen" iconOnly onClick={() => onZoom('fit')} />
    </div>
  );
}

const DrawingWorkspace: React.FC<DrawingWorkspaceProps> = ({
  selectedDoc,
  documents,
  annotations,
  effectiveUserName,
  isAdmin,
  onPatchDocument,
  onOpenDocument,
  onReloadDocuments,
  onOpenFileManager,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { index: domainIndex } = useDomainIndex();
  const invalidateRegisterContext = useInvalidateRegisterContext();
  const routeTargetNodeId = useMemo(() => parseTargetNodeId(location.search), [location.search]);
  const routeTargetEquipmentId = useMemo(() => parseTargetEquipmentId(location.search), [location.search]);
  const routeTargetInstrumentId = useMemo(() => parseTargetInstrumentId(location.search), [location.search]);
  const routeEditEquipmentId = useMemo(() => parseEditEquipmentId(location.search), [location.search]);
  const routeEditInstrumentId = useMemo(() => parseEditInstrumentId(location.search), [location.search]);
  /**
   * Drops one deep-link parameter the workspace has just acted on, so a reload
   * after the panel is closed shows the drawing as the reviewer left it.
   */
  const consumeRouteParam = useCallback((param: string) => {
    const remaining = new URLSearchParams(location.search);
    if (!remaining.has(param)) return;
    remaining.delete(param);
    const query = remaining.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);
  const [linkedDocumentStates, setLinkedDocumentStates] = useState<Record<string, PnidWizardState>>({});
  const [diagramMode, setDiagramMode] = useState<'pid' | 'pfd'>(
    selectedDoc.diagram_type === 'pfd' ? 'pfd' : 'pid'
  );
  const pfdEquipmentSchema = useFormSchema('pfd_equipment');
  const pfdNodeSchema = useFormSchema('pfd_node');

  /** The asset rail's tab. It names the kind of asset in hand when no draw tool is armed. */
  const [assetRailTab, setAssetRailTab] = useState<AssetRailTab>('equipment');
  useActivityHeartbeat(selectedDoc?.id ?? null);
  const { trackAction } = useActionTracker(selectedDoc?.id ?? null);
  const [pnidState, setPnidState] = useState<PnidWizardState | null>(null);
  /** The findings of the last drawing read, kept beside the state; the rail and the line form read them. */
  // Before the first read, and after a read finds no stored state, there is nothing to find.
  const [drawingValidation, setDrawingValidation] = useState<DrawingValidation>(NO_FINDINGS);
  const [pnidStateLoadedDocId, setPnidStateLoadedDocId] = useState<string | null>(null);
  const lastAppliedStateRef = useRef<PnidStateResponse | null>(null);
  const takeDrawingResponse = useCallback(({ state, validation }: PnidStateResponse): PnidWizardState => {
    setDrawingValidation(validation);
    return state;
  }, []);
  const resolvedTabForDocRef = useRef<string | null>(null);

  const pnidStateQuery = useQuery({
    queryKey: queryKeys.pnidState(selectedDoc.id),
    queryFn: () => pnidApi.get(selectedDoc.id),
  });
  const { hasAIData, aiData } = useAiData(selectedDoc.id);
  const commentsQuery = useComments(selectedDoc.id);
  const comments = commentsQuery.comments;

  const isBooting = pnidStateLoadedDocId !== selectedDoc.id;
  // Every EditorDock in the workspace renders into this element, after the canvas column.
  const [editorDockSlot, setEditorDockSlot] = useState<HTMLDivElement | null>(null);
  /** The Drawing details dock: the drawing's number, title, class and legend. */
  const [setupDockOpen, setSetupDockOpen] = useState(false);

  const selectedDocIdRef = useRef<string | null>(null);
  const lastRouteTargetNodeKeyRef = useRef<string | null>(null);
  const lastRouteTargetEquipmentKeyRef = useRef<string | null>(null);
  const lastRouteTargetInstrumentKeyRef = useRef<string | null>(null);
  const lastRouteEditEquipmentKeyRef = useRef<string | null>(null);
  const lastRouteEditInstrumentKeyRef = useRef<string | null>(null);
  const [selectedWizardItemKeys, setSelectedWizardItemKeys] = useState<Set<string>>(new Set());
  const [annotationImportAdjustment, setAnnotationImportAdjustment] =
    useState<AnnotationImportAdjustment | null>(null);
  const [undoStack, setUndoStack] = useState<PnidUndoEntry[]>([]);
  const [redoStack, setRedoStack] = useState<PnidUndoEntry[]>([]);
  const [historyApplying, setHistoryApplying] = useState(false);
  const [pnidNoDraft, setPnidNoDraft] = useState<string>("");
  const [activeEquipmentId, setActiveEquipmentId] = useState<string | null>(
    null
  );
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);
  // The rail's search, which carries over a tab change and narrows the Needs review advance.
  const [railQuery, setRailQuery] = useState('');
  useEffect(() => {
    setAssetRailTab('equipment');
    setRailQuery('');
  }, [selectedDoc.id]);
  const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
  /** The toolbar's Isolate toggle: the canvas draws only the active asset and its instruments. */
  const [isolateActiveAsset, setIsolateActiveAsset] = useState(false);
  const [canvasFocusRequest, setCanvasFocusRequest] = useState<CanvasFocusRequest | null>(null);
  const canvasFocusRequestCountRef = useRef(0);
  const [canvasZoomRequest, setCanvasZoomRequest] = useState<CanvasZoomRequest | null>(null);
  const canvasZoomRequestCountRef = useRef(0);
  const requestCanvasZoom = useCallback((kind: CanvasZoomRequest['kind']) => {
    canvasZoomRequestCountRef.current += 1;
    setCanvasZoomRequest({ kind, requestId: canvasZoomRequestCountRef.current });
  }, []);
  const [canvasScaleSource] = useState(createCanvasScaleSource);
  /** Moves the canvas to a target. Only explicit navigation calls this; a save or a drag keeps the view. */
  const navigateCanvasTo = useCallback((bbox: BoundingBox | null) => {
    canvasFocusRequestCountRef.current += 1;
    setCanvasFocusRequest(bbox ? { bbox, requestId: canvasFocusRequestCountRef.current } : null);
  }, []);
  // Review policy (pnid_review_policy.md) UI state
  // The drawing's pending agent proposals. The asset rail flags their rows on every
  // tab, and its Needs review filter lists only them. The backend reviews a pending
  // proposal on a save by a caller with review permission (`_save_is_the_review`), so
  // the saved element leaves this set with the save's response or the refresh after it,
  // and, with the filter on, the next flagged row opens.
  const pendingAgentItems = useMemo(() => agentReviewItems(pnidState), [pnidState]);
  const [needsReviewOn, setNeedsReviewOn] = useState(false);
  const pendingAgentIds = useMemo(() => {
    const ids: Record<MarkingKind, Set<string>> = { equipment: new Set(), instrument: new Set(), node: new Set() };
    for (const item of pendingAgentItems) ids[item.kind].add(item.id);
    return ids;
  }, [pendingAgentItems]);
  // The rail groups equipment by type label, so the flagged-row walk orders them the same way.
  const { labelFor: equipmentTypeLabel } = useEquipmentTypes();
  /** The rows the rail's current tab shows after its Type and Subtype, as the rail last reported them. */
  const railShownIdsRef = useRef<string[]>([]);
  const reportRailShownIds = useCallback((ids: string[]) => { railShownIdsRef.current = ids; }, []);
  /** The flagged rows in the rail's display order now, or null with Needs review off. */
  const flaggedRowOrderNowRef = useRef<() => string[] | null>(() => null);
  flaggedRowOrderNowRef.current = () => (needsReviewOn
    ? flaggedRowOrder({
      fromTab: assetRailTab, shownIds: railShownIdsRef.current, assets: railAssets,
      equipmentTypeLabel, query: railQuery, pendingIds: pendingAgentIds,
      publishableInstrumentIds: railPublishableInstrumentIds,
    })
    : null);
  /** Set by a save with Needs review on; the advance below acts once the save shows in the drawing. */
  const agentReviewAdvanceFromRef = useRef<string | null>(null);
  /** The flagged rows when that save was made, saved key included. The saved row leaves the
   *  filtered rail with the refresh, so the order after it is read from this copy. */
  const agentReviewOrderAtSaveRef = useRef<string[]>([]);
  const noteAgentReviewSave = useCallback((kind: MarkingKind, id: string) => {
    const order = flaggedRowOrderNowRef.current();
    if (!order) return;
    agentReviewAdvanceFromRef.current = agentReviewKey(kind, id);
    agentReviewOrderAtSaveRef.current = order;
  }, []);
  /** An Agent review link that names no element opens the first flagged row once the drawing loads. */
  const reviewOpensFirstRowRef = useRef(false);
  /** The flag's open-item path, for the deep-link effects declared above it. */
  const selectAgentReviewItemRef = useRef<(key: string) => void>(() => {});
  const [drawingContextOpen, setDrawingContextOpen] = useState(false);
  // Publication (publish the working copy as a drawing version)
  const [publishPanelOpen, setPublishPanelOpen] = useState(false);
  const [publishedVersion, setPublishedVersion] = useState<number | null>(null);
  const [loadingAI, setLoadingAI] = useState<boolean>(false);
  const [_selectedAIInstrumentTag, _setSelectedAIInstrumentTag] = useState<
    string | null
  >(null);
  const [pnidTitleDraft, setPnidTitleDraft] = useState<string>("");
  const [drawingClassDraft, setDrawingClassDraft] = useState<DrawingClass | "">("");
  const [legendSheetIdDraft, setLegendSheetIdDraft] = useState<string>("");
  const setupMutation = useSetPnidSetup(selectedDoc.id);
  const legendSheets = useLegendSheets(selectedDoc?.domain_id ?? "");
  const legendSheetOptions = useMemo(() => {
    const options = (legendSheets.data ?? []).map(({ id, name }) => ({ id, name }));
    // A deleted legend stays visible so unrelated setup edits preserve its reference.
    if (legendSheetIdDraft && !options.some(({ id }) => id === legendSheetIdDraft)) {
      options.push({ id: legendSheetIdDraft, name: `Unavailable legend (${legendSheetIdDraft})` });
    }
    return options.sort((left, right) => left.name.localeCompare(right.name));
  }, [legendSheets.data, legendSheetIdDraft]);
  // A plant with no plant id never fetches, and reads as a plant with no sheets.
  const legendSheetsStatus: "loading" | "error" | "empty" | "ready" = legendSheets.isError
    ? "error"
    : legendSheets.isLoading
      ? "loading"
      : (legendSheets.data?.length ?? 0) > 0
        ? "ready"
        : "empty";
  const [markDialog, setMarkDialog] = useState<{
    isOpen: boolean;
    kind: PnidDialogKind;
    bbox: BoundingBox | null;
    anchor: { x: number; y: number } | null;
    editTarget?: { kind: PnidDialogKind; id: string } | null;
    initialValues?: Record<string, any>;
    /** Set while the line form saves a path or duct just drawn: how many of its vertices bound. */
    drawnLine?: { boundCount: number };
  }>({
    isOpen: false,
    kind: "equipment",
    bbox: null,
    anchor: null,
    editTarget: null,
    initialValues: {},
  });

  const instrumentSelectionContextRef = useRef<{
    activeEquipmentId: string | null;
    activeNodeId: string | null;
    docId: string | null;
  }>({
    activeEquipmentId,
    activeNodeId,
    docId: selectedDoc?.id ?? null,
  });

  useEffect(() => {
    const previous = instrumentSelectionContextRef.current;
    const nextDocId = selectedDoc?.id ?? null;
    const shouldClearSelection =
      previous.activeEquipmentId !== activeEquipmentId ||
      previous.activeNodeId !== activeNodeId ||
      previous.docId !== nextDocId;

    if (shouldClearSelection) {
      setSelectedInstrumentId(null);
    }

    instrumentSelectionContextRef.current = {
      activeEquipmentId,
      activeNodeId,
      docId: nextDocId,
    };
  }, [activeEquipmentId, activeNodeId, selectedDoc?.id]);

  /**
   * Open one instrument: the rail tab that manages it, its parent context active,
   * the instrument selected and the canvas framed on it. Every path that goes
   * to an instrument — a drawing-context note, a deep link — goes through here,
   * so one instrument opens one way.
   *
   * The move also carries the selection guard's baseline above, because the
   * context change this makes is exactly what that guard treats as a signal to
   * drop the selection.
   */
  const focusInstrument = useCallback((instrumentId: string): boolean => {
    const instrument = pnidState?.instruments.find((candidate) => candidate.id === instrumentId);
    if (!instrument) return false;

    const context = getInstrumentEffectiveContext(instrument, pnidState);
    const nextEquipmentId = context.context_type === 'equipment' ? context.context_id : null;
    const nextNodeId = context.context_type === 'node' ? context.context_id : null;

    instrumentSelectionContextRef.current = {
      activeEquipmentId: nextEquipmentId,
      activeNodeId: nextNodeId,
      docId: selectedDoc?.id ?? null,
    };
    // With Needs review on, the instrument opens on the Instruments tab, where its flag is.
    setAssetRailTab(needsReviewOn ? 'instruments' : tabForInstrumentContext(context));
    setActiveEquipmentId(nextEquipmentId);
    setActiveNodeId(nextNodeId);
    setSelectedInstrumentId(instrument.id);
    navigateCanvasTo(navigationBoxOf('instrument', instrument));
    return true;
  }, [navigateCanvasTo, needsReviewOn, pnidState, selectedDoc?.id]);

  /**
   * The regions collected so far and what they are being collected for. Armed by the
   * instrument draw buttons, by Rebox, and by "Add region" in the instrument editor; the
   * canvas appends to it on every drag and nothing is written until Done.
   */
  const [boxDraft, setBoxDraft] = useState<BoxDraft | null>(null);
  const isMarkingInstrument = boxDraft !== null && marksAnInstrument(boxDraft);
  /** The dock field a click on a text target copies into, while it has focus. */
  const copyField = useFocusedEditorField();
  // Read on the first instrument marking or the first focus on a dock field, not on
  // sheet open: most visits do neither.
  const clickTargets = useClickTargets(selectedDoc.id, isMarkingInstrument || copyField !== null);
  const savedInstruments = pnidState?.instruments;
  // A save or a delete changes the saved regions; a pan or a hover changes neither.
  const unmarkedClickTargets = useMemo(
    () => retireUnderSavedRegions(clickTargets, (savedInstruments ?? []).flatMap(instrumentRegions)),
    [clickTargets, savedInstruments],
  );
  const visibleClickTargets = useMemo(
    () => hideContainedClickTargets(unmarkedClickTargets),
    [unmarkedClickTargets],
  );
  // A copy wants text, and the text it wants most often sits under a saved region: the tag
  // of the instrument being edited. So the copy list keeps every text target, retired or
  // contained, and drops the symbols, which carry no text.
  const copyClickTargets = useMemo(
    () => clickTargets.filter((clickTarget) => clickTarget.kind === 'text'),
    [clickTargets],
  );
  const instrumentContextOverrideRef = useRef<{ contextType: 'equipment' | 'node'; contextId: string } | null>(null);
  /** The existing element the armed draft redraws or extends. Absent while drawing something new. */
  const geometryTarget = boxDraft?.target ?? null;
  /** The canvas holds a shape in flight: a box being dragged or a line with vertices. */
  const [canvasHasDraft, setCanvasHasDraft] = useState(false);
  /** The canvas's kind pill is asking what a drawn box is. */
  const [kindPillOpen, setKindPillOpen] = useState(false);
  /** The draw tool a draw button armed. None armed: a drag asks its kind with the pill. */
  const [armedTool, setArmedTool] = useState<ArmedTool | null>(null);
  // Ducts are P&ID-only geometry, so a PFD has no Duct tool.
  const offeredDrawToolButtons = useMemo(
    () => DRAW_TOOL_BUTTONS.filter(({ tool }) => tool !== 'duct' || diagramMode === 'pid'),
    [diagramMode],
  );
  const offeredDrawTools = useMemo(() => offeredDrawToolButtons.map(({ tool }) => tool), [offeredDrawToolButtons]);
  /** The kind of asset in hand, from the armed tool and the rail tab. The canvas takes the same value. */
  const currentAssetKind = useMemo(() => activeAssetKind({ armedTool, railTab: assetRailTab }), [armedTool, assetRailTab]);
  /** Arms the instrument tool and a fresh draft with no context override, from a draw button. */
  const armInstrumentDraw = useCallback(() => {
    setArmedTool('instrument');
    setBoxDraft(armDraft('instrument'));
    instrumentContextOverrideRef.current = null;
  }, []);
  // The instrument tool is armed through its draft, so whatever ends the draft — Escape
  // on an empty draft, a redraw's save, a deleted marking — lets go of the tool too.
  useEffect(() => {
    if (boxDraft === null) setArmedTool((tool) => (tool === 'instrument' ? null : tool));
  }, [boxDraft]);

  // The version history's preview: the version on the canvas, or null for the live drawing.
  const [previewingVersion, setPreviewingVersion] = useState<number | null>(
    null
  );
  /** The findings the screen shows. A version preview shows a stored version, and the findings
   *  describe the working copy, so it shows none; the working copy's return when it ends. */
  const shownValidation = previewingVersion === null ? drawingValidation : NO_FINDINGS;
  const railAssets = useMemo<RailAssets | undefined>(() => (pnidState
    ? { equipments: pnidState.equipments, instruments: pnidState.instruments, nodes: pnidState.nodes, validation: shownValidation }
    : undefined), [pnidState, shownValidation]);
  // Unified instrument editor state (right-click any instrument on canvas)
  const [highlightedEntityId, setHighlightedEntityId] = useState<string | null>(null);
  const [instrumentEditorState, setInstrumentEditorState] = useState<{
    instrument: PnidInstrument;
    /** The instrument's findings: the instrument GET's, or while the editor shows the local copy,
     *  the drawing's. */
    findings: Finding[];
    /** Opened from the equipment or line form, which stays open underneath. */
    nested?: boolean;
    isUsingLocalSnapshot?: boolean;
    latestLoadError?: string | null;
    /** The draw-time identify answer, set only when the editor opens on a drawn box. */
    identifyPrefill?: Record<string, unknown>;
  } | null>(null);

  const handleRightClickInstrument = useCallback(async (id: string, nested = false) => {
    if (!selectedDoc?.id) return;
    // Toggle off if clicking the instrument the editor shows. The canvas selection can
    // name another instrument while the editor stays open, so it does not decide this.
    if (instrumentEditorState?.instrument.id === id) {
      setSelectedInstrumentId(null);
      setInstrumentEditorState(null);
      return;
    }
    // Opened from anywhere but the equipment or line form, the editor replaces what the dock
    // shows, except a finished line waiting in its form, which it covers.
    if (!nested) {
      setMarkDialog((prev) => (prev.drawnLine ? prev : { ...prev, isOpen: false }));
      setLineSegmentReviewPopover(null);
    }
    const localInstrument = pnidState?.instruments.find((instrument) => instrument.id === id);
    // A previewed version is on the canvas: the editor shows that version's copy, locked,
    // and never fetches the live instrument, so a Save or Delete cannot reach the drawing.
    if (previewingVersion !== null) {
      if (!localInstrument) return;
      setSelectedInstrumentId(id);
      setInstrumentEditorState({
        instrument: localInstrument,
        // The drawing's findings describe the working copy, not this version.
        findings: [],
        nested,
        isUsingLocalSnapshot: true,
        latestLoadError: `This is version ${previewingVersion}'s copy. Exit Preview to edit the instrument.`,
      });
      return;
    }
    setSelectedInstrumentId(id);
    if (localInstrument) {
      setInstrumentEditorState({
        instrument: localInstrument,
        // An element with no finding has no entry.
        findings: drawingValidation.elements[id]?.findings ?? [],
        nested,
        isUsingLocalSnapshot: true,
        latestLoadError: null,
      });
    }
    try {
      const { instrument, validation } = await instrumentApi.getInstrument(selectedDoc.id, id);
      setInstrumentEditorState((prev) => {
        // Closed (Back, ✕) before the backend copy arrived: stay closed.
        if (prev ? prev.instrument.id !== id : localInstrument) return prev;
        return {
          instrument,
          findings: validation.findings,
          nested: prev?.nested ?? nested,
          isUsingLocalSnapshot: false,
          latestLoadError: null,
        };
      });
    } catch (err) {
      console.error('Failed to fetch instrument for editor:', err);
      if (!localInstrument) {
        setSelectedInstrumentId((prev) => (prev === id ? null : prev));
      } else {
        setInstrumentEditorState((prev) =>
          prev?.instrument.id === id
            ? {
                ...prev,
                isUsingLocalSnapshot: true,
                latestLoadError: 'Could not load the latest backend copy. Editing is disabled until you close and retry.',
              }
            : prev
        );
      }
    }
  }, [selectedDoc?.id, instrumentEditorState, pnidState?.instruments, previewingVersion, drawingValidation]);

  const closeInstrumentEditor = () => {
    setInstrumentEditorState(null);
    setSelectedInstrumentId(null);
    setHighlightedEntityId(null);
  };

  const refreshPnidState = useCallback(async (docId: string) => {
    // Shares the read that the write before it already started (onDrawingWrite, below).
    const fetched = await queryClient.fetchQuery({
      queryKey: queryKeys.pnidState(docId),
      queryFn: () => pnidApi.get(docId),
      staleTime: 0,
    });
    const refreshed = queryClient.getQueryData<PnidStateResponse>(queryKeys.pnidState(docId)) ?? fetched;
    lastAppliedStateRef.current = refreshed;
    const state = takeDrawingResponse(refreshed);
    setPnidState(state);
    setPnidStateLoadedDocId(docId);
    return state;
  }, [queryClient, takeDrawingResponse]);

  // Every write to this drawing can change what the rules read, so the drawing is read again
  // and its findings always come from a server read, never from a local copy paired with the
  // findings of an older read. A drag or an optimistic patch sends no request, so it waits for
  // the write that commits it.
  useEffect(() => onDrawingWrite(selectedDoc.id, () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.pnidState(selectedDoc.id) });
  }), [queryClient, selectedDoc.id]);

  // Any element write or delete can review an agent proposal, so the plant's
  // agent review queue is stale the moment the request resolves. The editor and form
  // paths say so right after their request, before the refresh that follows:
  // a failed refresh or a reviewer who leaves for the queue page must still
  // find it refetched. The effect below covers the remaining write paths, all
  // of which replace the drawing state.
  const selectedDomainId = selectedDoc?.domain_id ?? null;
  const invalidateAgentReviewQueue = useCallback(() => {
    if (!selectedDomainId) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.agentReviewQueues(selectedDomainId) });
  }, [queryClient, selectedDomainId]);
  const lastInvalidatedStateRef = useRef<PnidWizardState | null>(null);
  useEffect(() => {
    if (!pnidState || pnidStateLoadedDocId !== selectedDoc?.id) return;
    const previous = lastInvalidatedStateRef.current;
    lastInvalidatedStateRef.current = pnidState;
    if (previous && previous !== pnidState && previous.doc_id === pnidState.doc_id && selectedDomainId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentReviewQueues(selectedDomainId) });
    }
  }, [pnidState, pnidStateLoadedDocId, selectedDoc?.id, selectedDomainId, queryClient]);

  // DECISION(2026-10-26, Abhishek): The workspace shows no policy-issues pop-up after a save.
  // The pop-up covered the form's Cancel and Save.
  const refreshViewsAfterObjectSave = useCallback(() => {
    if (!selectedDocIdRef.current) return;
    // An SME save reviews an agent proposal, so the plant agent review queue that
    // listed it is stale.
    const domainId = selectedDocDomainIdRef.current;
    if (domainId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.agentReviewQueues(domainId) });
    }
  }, [queryClient]);

  // An instrument write patched into the drawing state keeps its old meta.updated_at, so
  // the publishable query's key stays put, while the backend may have changed the
  // instrument's type: any update of a tagged instrument takes the type of the drawing
  // that defines its tag. Each such path drops the drawing's publishable answer here.
  const invalidatePublishableInstruments = useCallback(() => {
    const docId = selectedDocIdRef.current;
    if (!docId) return;
    void queryClient.invalidateQueries({ queryKey: queryKeys.publishableAssetsOfDrawing(docId) });
  }, [queryClient]);

  const handleInstrumentEditorSave = useCallback(async (id: string, updates: PnidInstrumentUpdate) => {
    if (!selectedDoc?.id) return;
    await instrumentApi.updateInstrument(selectedDoc.id, id, updates);
    invalidateAgentReviewQueue();
    noteAgentReviewSave('instrument', id);
    await refreshPnidState(selectedDoc.id);

    setInstrumentEditorState(null);
    setSelectedInstrumentId(null);
    refreshViewsAfterObjectSave();
  }, [refreshViewsAfterObjectSave, invalidateAgentReviewQueue, refreshPnidState, selectedDoc?.id]);

  const handleInstrumentEditorDelete = useCallback(async (id: string) => {
    if (!selectedDoc?.id) return;
    await instrumentApi.deleteInstrument(selectedDoc.id, id);
    invalidateAgentReviewQueue();
    await refreshPnidState(selectedDoc.id);
    setInstrumentEditorState(null);
    setSelectedInstrumentId(null);
  }, [invalidateAgentReviewQueue, refreshPnidState, selectedDoc?.id]);

  const unifiedInstrumentRefs = useMemo(() => {
    return (pnidState?.instruments ?? []).map((inst) => ({
      id: inst.id,
      instrument_text: inst.instrument_text,
      generated_id: inst.generated_id,
      instrument_type: inst.instrument_type,
      pnid_hyperlink: inst.pnid_hyperlink,
    }));
  }, [pnidState?.instruments]);

  const equipmentInstruments = useMemo(
    () => getEquipmentContextInstruments(pnidState),
    [pnidState],
  );
  const nodeInstruments = useMemo(
    () => getNodeContextInstruments(pnidState),
    [pnidState],
  );
  const directNodeInstruments = useMemo(
    () => getDirectNodeContextInstruments(pnidState),
    [pnidState],
  );

  /** Regions being identified, outlined on the canvas until their answer arrives. */
  const [identifyingBboxes, setIdentifyingBboxes] = useState<BoundingBox[]>([]);
  const [isNodeMarkingMode, setIsNodeMarkingMode] = useState(false);
  const [nodeDrawingShape, setNodeDrawingShape] = useState<'pipeline' | 'duct'>('pipeline');
  /**
   * The stored line a path being drawn saves to, or null for a new line. The first click or
   * Mark sets it; the line form opens on the mark dialog at the finish.
   */
  const [lineDrawTarget, setLineDrawTarget] = useState<{ nodeId: string | null } | null>(null);
  /** A finished path or duct waits in the line form for Save or Cancel. */
  const drawnLineOpen = markDialog.isOpen && Boolean(markDialog.drawnLine);

  const [lineSegmentReviewPopover, setLineSegmentReviewPopover] = useState<{
    nodeId: string;
    hoveredDistinctId: string | null;
    selectedDistinctId: string | null;
  } | null>(null);

  // DECISION(2026-10-21, Abhishek): edge pan is off while a create or edit editor is open, except during a drawing gesture.
  // The cursor crosses the canvas border on its way to the editor, and a pan there
  // moves the target being edited. Outside an editor, SMEs edge-pan to move around the sheet.
  const entityEditorOpen =
    markDialog.isOpen
    || instrumentEditorState !== null
    || lineSegmentReviewPopover !== null
    // The Drawing details dock sits beside the view-only drawing.
    || setupDockOpen;

  // The dock shows one view. An instrument opened from the equipment or line form sits
  // on that form, which stays mounted underneath with its draft; that form is the only
  // view that is ever covered. A finished line waiting in its form is covered the same
  // way by any instrument or segment review, and comes back when they close. Otherwise
  // an open instrument outranks the form, and the form outranks a segment review.
  const markDialogView: DockView | null = markDialog.isOpen ? { kind: markDialog.kind } : null;
  const dockStack: DockView[] = !isBooting && setupDockOpen
    ? [{ kind: 'setup' }]
    : instrumentEditorState
      ? [...((instrumentEditorState.nested || drawnLineOpen) && markDialogView ? [markDialogView] : []), { kind: 'instrument' }]
      : lineSegmentReviewPopover && drawnLineOpen && markDialogView
        ? [markDialogView, { kind: 'lineSegmentReview' }]
        : markDialogView
          ? [markDialogView]
          : lineSegmentReviewPopover
            ? [{ kind: 'lineSegmentReview' }]
            : [];
  const dockView: DockView | null = dockStack[dockStack.length - 1] ?? null;
  const markDialogCovered = dockStack.length === 2;
  /** An instrument opened from the open form: Back returns to it, and Close closes both. */
  const instrumentOnForm = markDialogCovered && dockView?.kind === 'instrument' && Boolean(instrumentEditorState?.nested);

  // Escape typed outside the dock and outside a field: the kind pill, the collect mode and
  // a line in flight keep it, since the canvas reads it there; then the open dock closes
  // as Escape typed in it closes it; then an armed tool is let go.
  const editorDockOpen = dockView !== null;
  useEffect(() => {
    if ((armedTool === null && !editorDockOpen) || kindPillOpen || boxDraft !== null || canvasHasDraft) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || isEventInsideEditorDock(event)) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      if (tagName === "input" || tagName === "textarea" || tagName === "select" || target?.isContentEditable) return;
      // A modal's Escape closes the modal alone.
      if (target instanceof Element && target.closest('[role="dialog"]')) return;
      if (editorDockOpen) {
        escapeIntoOpenEditorDock();
        return;
      }
      if (armedTool === 'duct') setNodeDrawingShape('pipeline');
      setArmedTool(null);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [armedTool, editorDockOpen, kindPillOpen, boxDraft, canvasHasDraft]);
  /** Clears the views a newly opened equipment or line form replaces. */
  const replaceDockView = () => {
    closeInstrumentEditor();
    setLineSegmentReviewPopover(null);
  };

  useEffect(() => {
    if (!isNodeMarkingMode || !activeNodeId) return;
    const activeNode = pnidState?.nodes.find((node) => node.id === activeNodeId);
    if (activeNode) setNodeDrawingShape(activeNode.is_duct ? 'duct' : 'pipeline');
  }, [activeNodeId, isNodeMarkingMode, pnidState]);


  const [nodePointCandidates, setNodePointCandidates] = useState<
    NodePointCandidate[]
  >([]);
  const nodePointRunId = useRef(0);

  // Comment state
  const [selectedCommentId, setSelectedCommentId] = useState<string | null>(
    null
  );
  const [showResolvedComments, _setShowResolvedComments] = useState(false);

  const [selectedCommentScreenPos, setSelectedCommentScreenPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [canvasImageSize, setCanvasImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);

  // Draggable context bar state (default to right side)

  useEffect(() => {
    if (currentAssetKind !== 'line') {
      setLineSegmentReviewPopover(null);
    }
  }, [currentAssetKind]);


  const handleCanvasImageSize = useCallback((width: number, height: number) => {
    setCanvasImageSize((prev) => {
      if (prev && prev.width === width && prev.height === height) return prev;
      return { width, height };
    });
  }, []);

  // DECISION(2026-10-19, Abhishek): Setup frames a fixed bottom-right region of the
  // sheet, x 70–100% and y 75–100%. No drawing stores where its title block is, and
  // bottom-right is where P&IDs put it; on a sheet that puts it elsewhere the SME pans once.
  // The canvas reports `canvasImageSize` in the image space a fit box request uses.
  const setupTitleBlockBbox = useMemo<BoundingBox | null>(() => (
    canvasImageSize
      ? {
          x1: canvasImageSize.width * 0.7,
          y1: canvasImageSize.height * 0.75,
          x2: canvasImageSize.width,
          y2: canvasImageSize.height,
        }
      : null
  ), [canvasImageSize]);
  const [canvasFitRequestId, setCanvasFitRequestId] = useState(0);
  const [setupFitRequest, setSetupFitRequest] = useState<CanvasFocusRequest | null>(null);
  const setupFitRequestCountRef = useRef(0);

  // Opening Setup fits the title block beside the dock once, as soon as the canvas
  // reports the image size. A navigation centres and keeps the zoom, so a fit of
  // its own is what brings the whole region on screen.
  useEffect(() => {
    if (dockView?.kind !== 'setup' || !setupTitleBlockBbox) return;
    setupFitRequestCountRef.current += 1;
    setSetupFitRequest({ bbox: setupTitleBlockBbox, requestId: setupFitRequestCountRef.current });
  }, [dockView?.kind, setupTitleBlockBbox]);

  // Leaving Setup, by Save, ✕, or Escape, fits the whole drawing to the widened
  // canvas before it paints. A way out that navigated somewhere of its own keeps
  // its target.
  const setupWasOpenRef = useRef(false);
  const focusRequestCountAtSetupOpenRef = useRef(0);
  useLayoutEffect(() => {
    const setupOpen = dockView?.kind === 'setup';
    const setupJustOpened = setupOpen && !setupWasOpenRef.current;
    const setupJustClosed = setupWasOpenRef.current && !setupOpen;
    setupWasOpenRef.current = setupOpen;
    if (setupJustOpened) {
      focusRequestCountAtSetupOpenRef.current = canvasFocusRequestCountRef.current;
      // The dock shows one view, so Setup replaces any editor that was open.
      setInstrumentEditorState(null);
      setSelectedInstrumentId(null);
      setHighlightedEntityId(null);
      setMarkDialog((prev) => ({ ...prev, isOpen: false }));
    }
    if (!setupJustClosed) return;
    const navigatedOnExit =
      canvasFocusRequest !== null && canvasFocusRequestCountRef.current > focusRequestCountAtSetupOpenRef.current;
    if (!navigatedOnExit) setCanvasFitRequestId((id) => id + 1);
  }, [dockView?.kind, canvasFocusRequest]);

  const segmentCoordinateInfo = useMemo(() => {
    const targetW = canvasImageSize?.width ?? selectedDoc?.width ?? null;
    const targetH = canvasImageSize?.height ?? selectedDoc?.height ?? null;
    if (!targetW || !targetH) return null;

    const page = aiData?.page_image;
    const aiW = page?.width_px;
    const aiH = page?.height_px;
    const rs = page?.render_scale;

    let sourceW: number | null = null;
    let sourceH: number | null = null;
    let sourceLabel = "ai";

    if (aiW && aiH) {
      sourceW = aiW;
      sourceH = aiH;
    } else if (aiData?.line_segments?.length) {
      let maxX = 0;
      let maxY = 0;
      for (const s of aiData.line_segments) {
        maxX = Math.max(maxX, s.x1, s.x2);
        maxY = Math.max(maxY, s.y1, s.y2);
      }
      if (maxX > 0 && maxY > 0) {
        sourceW = maxX;
        sourceH = maxY;
        sourceLabel = "ai_segments";
      }
    } else if (pnidState?.nodes?.length) {
      let maxX = 0;
      let maxY = 0;
      for (const n of pnidState.nodes) {
        const segs = n.line_segments ?? [];
        for (const s of segs) {
          maxX = Math.max(maxX, s.x1, s.x2);
          maxY = Math.max(maxY, s.y1, s.y2);
        }
      }
      if (maxX > 0 && maxY > 0) {
        sourceW = maxX;
        sourceH = maxY;
        sourceLabel = "segments";
      }
    }

    if (!sourceW || !sourceH) return null;

    // Heuristic: sometimes the `page_image.width_px/height_px` describes the rendered image size
    // but the extracted segment coordinates are in the *base* (pre-render_scale) coordinate space.
    // Detect this by looking at observed segment coordinate ranges in the current PNID state.
    if (aiW && aiH && pnidState?.nodes?.length && rs && rs > 1.1) {
      let maxX = 0;
      let maxY = 0;
      for (const n of pnidState.nodes) {
        const segs = n.line_segments ?? [];
        for (const s of segs) {
          maxX = Math.max(maxX, s.x1, s.x2);
          maxY = Math.max(maxY, s.y1, s.y2);
        }
      }
      const ratioX = maxX / aiW;
      const ratioY = maxY / aiH;
      const ratio = Math.max(ratioX, ratioY);
      if (Number.isFinite(ratio) && ratio > 0 && ratio < 0.65) {
        sourceW = sourceW / rs;
        sourceH = sourceH / rs;
      }
    }

    let sx = 1;
    let sy = 1;
    if (targetW && targetH) {
      sx = targetW / sourceW;
      sy = targetH / sourceH;
    } else if (page?.render_scale && page.render_scale > 0) {
      sx = 1 / page.render_scale;
      sy = 1 / page.render_scale;
    }

    // Guardrails against clearly-wrong scaling
    const clamp = (v: number) =>
      Number.isFinite(v) ? Math.min(8, Math.max(0.05, v)) : 1;
    sx = clamp(sx);
    sy = clamp(sy);

    return {
      sx,
      sy,
      source: { width_px: sourceW, height_px: sourceH, render_scale: rs },
      source_label: sourceLabel,
      target:
        targetW && targetH
          ? { width_px: targetW, height_px: targetH }
          : undefined,
    };
  }, [
    aiData?.page_image,
    aiData?.line_segments,
    canvasImageSize?.width,
    canvasImageSize?.height,
    selectedDoc?.width,
    selectedDoc?.height,
    pnidState?.nodes,
  ]);

  useEffect(() => {
    if (currentAssetKind !== 'line') {
      setLineDrawTarget(null);
    }
  }, [currentAssetKind]);

  const handleLinePathStart = useCallback(
    (
      _screenPosition: { x: number; y: number },
      _canvasPosition: { x: number; y: number }
    ) => {
      if (!pnidState) return;
      // Starting a line takes the dock from an open instrument or segment review.
      setInstrumentEditorState(null);
      setSelectedInstrumentId(null);
      setLineSegmentReviewPopover(null);

      // Nothing is guessed here: the ends come from the vertices the clicks bind (L3a).
      setLineDrawTarget((prev) => {
        if (prev) return prev;
        // The active line is the one in hand only while the kind is line; a line started on
        // the Equipment tab is new.
        const selectedNode = activeNodeId && currentAssetKind === 'line'
          ? pnidState.nodes.find((n) => n.id === activeNodeId)
          : null;
        const bindToSelectedNode = Boolean(
          selectedNode &&
            (isNodeMarkingMode || selectedNode.status === "ai_identified")
        );
        return { nodeId: bindToSelectedNode ? selectedNode!.id : null };
      });
    },
    [pnidState, activeNodeId, isNodeMarkingMode, currentAssetKind]
  );

  /** The ref string a bound end stores, or null when the entity is gone from the sheet. */
  const refForBinding = (binding: VertexBinding | null): string | null => {
    if (!binding || !pnidState) return null;
    if (binding.kind === 'equipment') {
      const equipment = pnidState.equipments.find((candidate) => candidate.id === binding.id);
      return equipment ? refForEntity({ kind: 'equipment', equipment }) : null;
    }
    if (binding.kind === 'instrument') {
      const instrument = unifiedInstrumentRefs.find((candidate) => candidate.id === binding.id);
      return instrument ? refForEntity({ kind: 'instrument', instrument }) : null;
    }
    const node = pnidState.nodes.find((candidate) => candidate.id === binding.id);
    return node ? refForEntity({ kind: 'node', node }) : null;
  };

  const handlePendingMarkBboxUpdate = (bbox: BoundingBox) => {
    setMarkDialog((prev) => (prev.isOpen ? { ...prev, bbox } : prev));
  };

  // Alert dialog state
  const [alertDialog, setAlertDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "success" | "error" | "info" | "warning";
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
  });

  // Confirm dialog state (for delete confirmations)
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type?: "danger" | "warning" | "info";
    confirmText?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
    type: "danger",
    confirmText: "Confirm",
  });

  // Modal states
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [originalStateBeforePreview, setOriginalStateBeforePreview] =
    useState<PnidWizardState | null>(null);
  // During a preview pnidState is the previewed version, and the working copy waits here.
  const workingCopyUpdatedAt = (originalStateBeforePreview ?? pnidState)?.meta?.updated_at;
  const publishableInstruments = usePublishableInstrumentIds(selectedDoc.id, workingCopyUpdatedAt);
  // Until the first answer arrives the rail lists every instrument, so it never flashes empty.
  // A failed request leaves the same fallback, and the rail says so and offers a retry.
  // The endpoint reads the working copy, so a previewed version's instruments go unfiltered.
  const railPublishableInstrumentIds = publishableInstruments.isLoaded && previewingVersion === null
    ? publishableInstruments.ids
    : undefined;
  // nodeOverviewOpen removed - consolidated into PnidMarkDialog
  // equipmentOverviewOpen removed - consolidated into PnidMarkDialog
  const [discardDraftConfirm, setDiscardDraftConfirm] = useState<{
    isOpen: boolean;
    /** The tool a toolbar draw button arms once the draft is discarded. Absent: the
     *  Drawing details dock opens instead. */
    armOnConfirm?: ArmedTool;
    /** The drawing a search pick opens once the dock's edited form is discarded. */
    openDrawingOnConfirm?: PickedDrawing;
  }>({ isOpen: false });
  // Drops the canvas's draft in flight, which returns the canvas to rest.
  const [canvasClearSignal, setCanvasClearSignal] = useState(0);
  const [annotationImportOpen, setAnnotationImportOpen] = useState(false);
  const [annotationImportSourceId, setAnnotationImportSourceId] = useState("");
  const [annotationImportSearch, setAnnotationImportSearch] = useState("");
  const [annotationImporting, setAnnotationImporting] = useState(false);

  // Get annotation for a document
  const getAnnotation = (docId: string): AnnotationInfo | null =>
    annotations[docId] || null;

  const loadLinkedDocumentState = useCallback(async (docId: string) => {
    if (linkedDocumentStates[docId]) return;
    const { state } = await pnidApi.get(docId);
    setLinkedDocumentStates((prev) => ({ ...prev, [docId]: state }));
  }, [linkedDocumentStates]);

  // Group-based editing model:
  // - can_edit controls annotation edits.
  // - can_review lets the backend review a pending agent proposal on its save.
  // - raven_admin remains full-access.
  const userCanEdit = canEditUser(user);
  const readOnlyWizard = !isAdmin && !userCanEdit;

  const targetAnnotationCounts = useMemo(() => ({
    equipments: pnidState?.equipments.length ?? 0,
    nodes: pnidState?.nodes.length ?? 0,
    instruments: pnidState?.instruments.length ?? 0,
    controlLoops: pnidState?.control_loops.length ?? 0,
    relationships: pnidState?.instrument_relationships.length ?? 0,
  }), [pnidState]);

  const targetStateReady = Boolean(
    selectedDoc?.id &&
    pnidState &&
    pnidStateLoadedDocId === selectedDoc.id
  );
  const targetHasAnnotations = targetStateReady && Object.values(targetAnnotationCounts).some((count) => count > 0);
  const targetAnnotationSummary = useMemo(
    () => formatAnnotationCountSummary(targetAnnotationCounts),
    [targetAnnotationCounts],
  );
  const annotationImportDisabledReason = !targetStateReady
    ? "Loading target annotations..."
    : "";
  const annotationImportDisabled = Boolean(annotationImportDisabledReason) || annotationImporting;

  const annotationImportSources = useMemo(() => {
    const query = annotationImportSearch.trim();
    return documents
      .filter((doc) => doc.id !== selectedDoc?.id)
      .filter((doc) => getDocumentDiagramType(doc) === diagramMode)
      .filter((doc) => documentMatchesSearch(doc, getAnnotation(doc.id), query))
      .sort((a, b) => {
        const domainCompare = documentDomainPathLabel(domainIndex, a).localeCompare(
          documentDomainPathLabel(domainIndex, b),
        );
        if (domainCompare !== 0) return domainCompare;
        return getDocumentPrimaryLabel(a).localeCompare(getDocumentPrimaryLabel(b));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotationImportSearch, documents, selectedDoc?.id, diagramMode, annotations]);
  const selectedAnnotationImportSource = useMemo(
    () => documents.find((doc) => doc.id === annotationImportSourceId) ?? null,
    [annotationImportSourceId, documents],
  );

  const showAlert = (
    title: string,
    message: string,
    type: "success" | "error" | "info" | "warning" = "info"
  ) => {
    setAlertDialog({
      isOpen: true,
      title,
      message,
      type,
    });
  };

  const closeAlert = () => {
    setAlertDialog((prev) => ({ ...prev, isOpen: false }));
  };

  const applyPnidStateLocally = useCallback((
    state: PnidWizardState,
    options?: { clearSelection?: boolean },
  ) => {
    setPnidState(state);
    setPnidStateLoadedDocId(state.doc_id);
    if (options?.clearSelection !== false) {
      setSelectedWizardItemKeys(new Set());
    }
    // An editor whose entity this state no longer holds would write that entity back on
    // Save, so a delete, an undo or a refresh that removes the entity closes its editor.
    const nodeIds = new Set(state.nodes.map((node) => node.id));
    const equipmentIds = new Set(state.equipments.map((equipment) => equipment.id));
    const instrumentIds = new Set(state.instruments.map((instrument) => instrument.id));
    setMarkDialog((prev) => {
      const editedId = markDialogEditedId(prev);
      if (!prev.isOpen || !editedId) return prev;
      const heldIds = prev.kind === "node" ? nodeIds : equipmentIds;
      return heldIds.has(editedId) ? prev : { ...prev, isOpen: false };
    });
    setInstrumentEditorState((prev) => (prev && !instrumentIds.has(prev.instrument.id) ? null : prev));
    setSelectedInstrumentId((prev) => (prev && !instrumentIds.has(prev) ? null : prev));
    setLineDrawTarget((prev) => (prev?.nodeId && !nodeIds.has(prev.nodeId) ? null : prev));
    setLineSegmentReviewPopover((prev) => (prev && !nodeIds.has(prev.nodeId) ? null : prev));
  }, []);

  const pushPnidUndoSnapshot = useCallback((label: string) => {
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) return false;
    const entry: PnidUndoEntry = {
      docId: selectedDoc.id,
      label,
      state: clonePnidState(pnidState),
    };
    setUndoStack((prev) => [...prev.slice(-(WIZARD_HISTORY_LIMIT - 1)), entry]);
    setRedoStack([]);
    return true;
  }, [pnidState, pnidStateLoadedDocId, selectedDoc?.id]);

  const handleOpenPnidHyperlink = useCallback(async (
    link: PnidHyperlinkRef,
    options?: { newTab?: boolean },
  ) => {
    if (!link.pnid_doc_id) return;
    try {
      const doc = documents.find((candidate) => candidate.id === link.pnid_doc_id)
        ?? await documentApi.get(link.pnid_doc_id);
      if (options?.newTab !== false) {
        window.open(
          buildPnidPath(doc.id, { targetNodeId: link.target_node_id ?? null }),
          '_blank',
          'noopener,noreferrer',
        );
        return;
      }
      setInstrumentEditorState(null);
      setSelectedInstrumentId(null);
      setHighlightedEntityId(null);
      onOpenDocument(doc, { targetNodeId: link.target_node_id ?? null });
    } catch (error) {
      console.error("Failed to open linked P&ID:", error);
      showAlert("Linked P&ID unavailable", "The linked P&ID could not be opened.", "warning");
    }
  }, [documents, onOpenDocument]);

  const handleOpenLinkedDocument = useCallback(async (
    target: {
      docId: string;
      targetEquipmentId?: string | null;
      targetNodeId?: string | null;
      newTab?: boolean;
    },
  ) => {
    if (!target.docId) return;
    try {
      const doc = documents.find((candidate) => candidate.id === target.docId)
        ?? await documentApi.get(target.docId);
      const targetPath = buildPnidPath(doc.id, {
        targetEquipmentId: target.targetEquipmentId ?? null,
        targetNodeId: target.targetNodeId ?? null,
      });
      if (target.newTab !== false) {
        window.open(targetPath, '_blank', 'noopener,noreferrer');
        return;
      }
      setInstrumentEditorState(null);
      setSelectedInstrumentId(null);
      setHighlightedEntityId(null);
      onOpenDocument(doc, {
        targetEquipmentId: target.targetEquipmentId ?? null,
        targetNodeId: target.targetNodeId ?? null,
      });
    } catch (error) {
      console.error("Failed to open linked document:", error);
      showAlert("Linked document unavailable", "The linked document could not be opened.", "warning");
    }
  }, [documents, onOpenDocument]);

  const handleDiagramModeChange = useCallback(async (nextMode: 'pid' | 'pfd') => {
    if (readOnlyWizard) return;
    if (!selectedDoc || nextMode === diagramMode || markDialog.isOpen) return;

    const previousMode = diagramMode;
    setDiagramMode(nextMode);
    onPatchDocument(selectedDoc.id, { diagram_type: nextMode });

    try {
      await pnidApi.setDiagramType(selectedDoc.id, nextMode);
    } catch (error) {
      console.error("Failed to set diagram type:", error);
      setDiagramMode(previousMode);
      onPatchDocument(selectedDoc.id, { diagram_type: previousMode });
      showAlert("Mode update failed", "Could not persist the diagram mode.", "error");
    }
  }, [diagramMode, markDialog.isOpen, onPatchDocument, selectedDoc, readOnlyWizard]);

  const renderDiagramModeToggle = useCallback(() => (
    <div
      role="group"
      aria-label="Drawing type"
      style={{
        display: "inline-grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 2,
        padding: 2,
        borderRadius: 5,
        border: "1px solid rgba(0, 0, 0, 0.08)",
        backgroundColor: "#f8fafc",
        opacity: markDialog.isOpen || readOnlyWizard ? 0.55 : 1,
      }}>
      {(["pid", "pfd"] as const).map((mode) => {
        const active = diagramMode === mode;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => void handleDiagramModeChange(mode)}
            disabled={markDialog.isOpen || active || readOnlyWizard}
            style={{
              border: "none",
              borderRadius: 4,
              padding: "4px 8px",
              minHeight: 24,
              backgroundColor: active ? "var(--raven-primary-main)" : "transparent",
              color: active ? "#fff" : "var(--raven-text-secondary)",
              cursor: markDialog.isOpen || active || readOnlyWizard ? "default" : "pointer",
              fontSize: 12,
              fontWeight: 700,
            }}>
            {mode.toUpperCase()}
          </button>
        );
      })}
    </div>
  ), [diagramMode, handleDiagramModeChange, markDialog.isOpen, readOnlyWizard]);

  // Prevent browser zoom (Ctrl+scroll / pinch) on the entire page except canvas
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        const target = e.target as HTMLElement;
        const isCanvasArea =
          target.closest("canvas") || target.closest(".konvajs-content");
        if (!isCanvasArea) {
          e.preventDefault();
        }
      }
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    return () => document.removeEventListener("wheel", handleWheel);
  }, []);

  useEffect(() => {
    selectedDocIdRef.current = selectedDoc?.id ?? null;
  }, [selectedDoc?.id]);
  const selectedDocDomainIdRef = useRef<string | null>(null);
  useEffect(() => {
    selectedDocDomainIdRef.current = selectedDoc?.domain_id ?? null;
  }, [selectedDoc?.domain_id]);

  useEffect(() => {
    if (!routeTargetNodeId) {
      lastRouteTargetNodeKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const focusKey = `${selectedDoc.id}:node:${routeTargetNodeId}`;
    if (lastRouteTargetNodeKeyRef.current === focusKey) {
      return;
    }
    lastRouteTargetNodeKeyRef.current = focusKey;

    const node = pnidState.nodes.find((candidate) => candidate.id === routeTargetNodeId);
    if (!node) {
      console.warn("Linked P&ID target line not found:", routeTargetNodeId);
      return;
    }

    setAssetRailTab('lines');
    if (needsReviewOn) {
      // From the plant queue: the line opens for review the way its flag opens it.
      selectAgentReviewItemRef.current(agentReviewKey('node', node.id));
      return;
    }
    setActiveNodeId(node.id);
    navigateCanvasTo(nodeGeometryBbox(node));
  }, [navigateCanvasTo, needsReviewOn, pnidState, pnidStateLoadedDocId, routeTargetNodeId, selectedDoc?.id]);

  useEffect(() => {
    if (!routeTargetEquipmentId) {
      lastRouteTargetEquipmentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const focusKey = `${selectedDoc.id}:equipment:${routeTargetEquipmentId}`;
    if (lastRouteTargetEquipmentKeyRef.current === focusKey) {
      return;
    }
    lastRouteTargetEquipmentKeyRef.current = focusKey;

    const equipment = pnidState.equipments.find((candidate) => candidate.id === routeTargetEquipmentId);
    if (!equipment) {
      console.warn("Linked target equipment not found:", routeTargetEquipmentId);
      return;
    }

    setAssetRailTab('equipment');
    setActiveEquipmentId(equipment.id);
    navigateCanvasTo(navigationBoxOf('equipment', equipment));
  }, [navigateCanvasTo, pnidState, pnidStateLoadedDocId, routeTargetEquipmentId, selectedDoc?.id]);

  useEffect(() => {
    if (!routeTargetInstrumentId) {
      lastRouteTargetInstrumentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const focusKey = `${selectedDoc.id}:instrument:${routeTargetInstrumentId}`;
    if (lastRouteTargetInstrumentKeyRef.current === focusKey) {
      return;
    }
    lastRouteTargetInstrumentKeyRef.current = focusKey;

    // One link, one target. A higher-ranked target owns the rail tab and the
    // viewport, and acting here as well would land on neither. `edit_instrument_id`
    // ranks with this one and does everything it does, so it wins too.
    if (
      winningRouteTarget({
        routeEditEquipmentId,
        routeTargetEquipmentId,
        routeTargetNodeId,
        routeEditInstrumentId,
        routeTargetInstrumentId,
      }) !== 'instrument'
      || routeEditInstrumentId
    ) {
      console.warn(
        "target_instrument_id ignored: edit_instrument_id/edit_equipment_id/target_equipment_id/target_node_id takes precedence",
      );
      return;
    }

    if (!focusInstrument(routeTargetInstrumentId)) {
      console.warn("Linked target instrument not found:", routeTargetInstrumentId);
    }
  }, [
    focusInstrument,
    pnidState,
    pnidStateLoadedDocId,
    routeEditEquipmentId,
    routeEditInstrumentId,
    routeTargetEquipmentId,
    routeTargetInstrumentId,
    routeTargetNodeId,
    selectedDoc?.id,
  ]);

  useEffect(() => {
    if (!activeNodeId) {
      setNodePointCandidates([]);
      return;
    }
    setNodePointCandidates((prev) =>
      prev.filter((candidate) => candidate.nodeId === activeNodeId)
    );
  }, [activeNodeId]);

  // Server → local: apply only when the query hands us a new reference that we
  // have not already applied. A version preview shows a stored version, so a read
  // that lands during it waits, and applies when the preview ends.
  useEffect(() => {
    const response = pnidStateQuery.data;
    if (previewingVersion !== null) return;
    if (!response || response === lastAppliedStateRef.current) return;
    if (response.state.doc_id !== selectedDoc.id) return;
    const isFirstApplyForDoc = resolvedTabForDocRef.current !== selectedDoc.id;
    lastAppliedStateRef.current = response;
    const state = takeDrawingResponse(response);
    applyPnidStateLocally(state, { clearSelection: isFirstApplyForDoc });
    if (!isFirstApplyForDoc) return;

    setPnidNoDraft(state.pnid_no ?? "");
    setPnidTitleDraft(state.pnid_title ?? "");
    setDrawingClassDraft(state.drawing_class ?? "");
    setLegendSheetIdDraft(state.legend_sheet_id ?? "");
    if (!activeEquipmentId && state.equipments.length > 0) {
      setActiveEquipmentId(state.equipments[0].id);
    }
    if (!activeNodeId && state.nodes.length > 0) {
      setActiveNodeId(state.nodes[0].id);
    }
    resolvedTabForDocRef.current = selectedDoc.id;
    const routeTargets = {
      routeEditEquipmentId,
      routeTargetEquipmentId,
      routeTargetNodeId,
      routeEditInstrumentId,
      routeTargetInstrumentId,
    };
    setAssetRailTab(resolveInitialRailTab({ state, ...routeTargets }));
    // `?step=` is read here, once, and then dropped from the URL.
    const urlStep = parseStepParam(location.search);
    consumeRouteParam(STEP_PARAM);
    setSetupDockOpen(opensWithSetupDock({ state, urlStep }));
    const opensReview = opensWithNeedsReview(urlStep);
    setNeedsReviewOn(opensReview);
    reviewOpensFirstRowRef.current = opensReview && winningRouteTarget(routeTargets) === null;
  }, [
    pnidStateQuery.data,
    previewingVersion,
    selectedDoc.id,
    applyPnidStateLocally,
    takeDrawingResponse,
    activeEquipmentId,
    activeNodeId,
    routeEditEquipmentId,
    routeEditInstrumentId,
    routeTargetEquipmentId,
    routeTargetNodeId,
    routeTargetInstrumentId,
    location.search,
    consumeRouteParam,
  ]);

  useEffect(() => {
    if (!pnidStateQuery.isError) return;
    if (pnidStateLoadedDocId === selectedDoc.id) return;
    const status = axios.isAxiosError(pnidStateQuery.error)
      ? pnidStateQuery.error.response?.status
      : undefined;
    console.error("Failed to load PNID wizard state:", pnidStateQuery.error);
    setPnidStateLoadedDocId(selectedDoc.id);
    if (status === 404) {
      setPnidState(null);
      setDrawingValidation(NO_FINDINGS);
      if (resolvedTabForDocRef.current !== selectedDoc.id) {
        resolvedTabForDocRef.current = selectedDoc.id;
        // No saved annotation yet: the drawing has no number either.
        setAssetRailTab('equipment');
        setSetupDockOpen(true);
        consumeRouteParam(STEP_PARAM);
      }
      return;
    }
    showAlert(
      "Could not load drawing",
      "The drawing state failed to load. Refresh and try again.",
      "error",
    );
  }, [pnidStateQuery.isError, pnidStateQuery.error, pnidStateLoadedDocId, selectedDoc.id, consumeRouteParam]);

  const restorePnidSnapshot = useCallback(async (
    entry: PnidUndoEntry,
    direction: "undo" | "redo",
  ) => {
    if (!selectedDoc?.id || !pnidState || entry.docId !== selectedDoc.id) return;
    setHistoryApplying(true);
    const currentEntry: PnidUndoEntry = {
      docId: selectedDoc.id,
      label: entry.label,
      state: clonePnidState(pnidState),
    };
    try {
      const restored = await pnidApi.replaceState(selectedDoc.id, entry.state, {
        reason: `${direction}:${entry.label}`,
      });
      applyPnidStateLocally(restored);
      setAnnotationImportAdjustment(null);
      await onReloadDocuments();
      if (direction === "undo") {
        setUndoStack((prev) => prev.slice(0, -1));
        setRedoStack((prev) => [...prev.slice(-(WIZARD_HISTORY_LIMIT - 1)), currentEntry]);
        showAlert("Undone", entry.label, "success");
      } else {
        setRedoStack((prev) => prev.slice(0, -1));
        setUndoStack((prev) => [...prev.slice(-(WIZARD_HISTORY_LIMIT - 1)), currentEntry]);
        showAlert("Redone", entry.label, "success");
      }
    } catch (error) {
      console.error(`Failed to ${direction} P&ID action:`, error);
      showAlert("Undo failed", "Could not restore the saved annotation state.", "error");
    } finally {
      setHistoryApplying(false);
    }
  }, [applyPnidStateLocally, onReloadDocuments, pnidState, selectedDoc?.id]);

  const handleUndoPnidAction = useCallback(() => {
    if (historyApplying) return;
    const entry = undoStack[undoStack.length - 1];
    if (!entry) return;
    void restorePnidSnapshot(entry, "undo");
  }, [historyApplying, restorePnidSnapshot, undoStack]);

  const handleRedoPnidAction = useCallback(() => {
    if (historyApplying) return;
    const entry = redoStack[redoStack.length - 1];
    if (!entry) return;
    void restorePnidSnapshot(entry, "redo");
  }, [historyApplying, redoStack, restorePnidSnapshot]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEventInsideEditorDock(event)) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(target?.isContentEditable);
      if (isEditable) return;
      const isUndoKey = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "z";
      if (!isUndoKey) return;
      event.preventDefault();
      if (event.shiftKey) {
        handleRedoPnidAction();
      } else {
        handleUndoPnidAction();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleRedoPnidAction, handleUndoPnidAction]);

  const selectedBackendAnnotationTargets = useMemo(() => {
    const targets: PnidAnnotationBackendTarget[] = [];
    const seen = new Set<string>();
    for (const key of selectedWizardItemKeys) {
      const parsed = parseWizardItemKey(key);
      if (!parsed) continue;
      const backendTarget = toBackendAnnotationTarget(parsed);
      const backendKey = `${backendTarget.kind}:${backendTarget.id}`;
      if (seen.has(backendKey)) continue;
      seen.add(backendKey);
      targets.push(backendTarget);
    }
    return targets;
  }, [selectedWizardItemKeys]);

  const handleSelectAllWizardAnnotations = useCallback(() => {
    if (!pnidState) return;
    setSelectedWizardItemKeys(getAllSavedAnnotationKeys(pnidState));
  }, [pnidState]);

  const handleClearWizardAnnotationSelection = useCallback(() => {
    setSelectedWizardItemKeys(new Set());
  }, []);

  const handleShiftSelectedWizardAnnotations = useCallback(async (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => {
    if (!selectedDoc?.id || selectedBackendAnnotationTargets.length === 0) return;
    const { dx, dy } = delta;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
      showAlert("Move failed", "Could not save an invalid selected-annotation offset.", "error");
      return;
    }
    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) return;

    pushPnidUndoSnapshot(
      selectedBackendAnnotationTargets.length === 1
        ? "Move selected annotation"
        : `Move ${selectedBackendAnnotationTargets.length} selected annotations`,
    );
    try {
      const updated = await pnidApi.shiftSelectedAnnotations(
        selectedDoc.id,
        selectedBackendAnnotationTargets,
        dx,
        dy,
        options,
      );
      applyPnidStateLocally(updated, { clearSelection: false });
      showAlert(
        "Selection moved",
        `Shifted ${selectedBackendAnnotationTargets.length} selected annotation(s).`,
        "success",
      );
    } catch (error) {
      console.error("Failed to shift selected annotations:", error);
      try {
        const refreshed = await refreshPnidState(selectedDoc.id);
        setPnidState(refreshed);
      } catch (refreshError) {
        console.error("Failed to refresh after selected annotation shift error:", refreshError);
      }
      showAlert("Move failed", "Could not save the selected annotation offset.", "error");
      throw error;
    }
  }, [
    applyPnidStateLocally,
    pushPnidUndoSnapshot,
    refreshPnidState,
    selectedBackendAnnotationTargets,
    selectedDoc?.id,
  ]);

  const handleDeleteSelectedWizardAnnotations = useCallback(() => {
    if (!selectedDoc?.id || selectedBackendAnnotationTargets.length === 0) return;
    const count = selectedBackendAnnotationTargets.length;
    setConfirmDialog({
      isOpen: true,
      title: count === 1 ? "Delete selected annotation?" : `Delete ${count} selected annotations?`,
      message: "This can be undone with Cmd/Ctrl+Z.",
      type: "danger",
      confirmText: "Delete",
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        pushPnidUndoSnapshot(count === 1 ? "Delete selected annotation" : `Delete ${count} selected annotations`);
        try {
          const updated = await pnidApi.deleteSelectedAnnotations(selectedDoc.id, selectedBackendAnnotationTargets);
          applyPnidStateLocally(updated);
          showAlert("Deleted", count === 1 ? "Selected annotation deleted." : `${count} selected annotations deleted.`, "success");
        } catch (error) {
          console.error("Failed to delete selected annotations:", error);
          showAlert("Delete failed", "Could not delete the selected annotations.", "error");
        }
      },
    });
  }, [
    applyPnidStateLocally,
    pushPnidUndoSnapshot,
    selectedBackendAnnotationTargets,
    selectedDoc?.id,
  ]);

  const putRedundancySetTags = usePutRedundancySetTags(selectedDoc?.domain_id ?? "");
  const redundancySetEquipmentIds = useMemo(
    () => equipmentOnlySelectionIds(selectedBackendAnnotationTargets),
    [selectedBackendAnnotationTargets],
  );
  const handleMarkSelectedAsRedundancySet = useCallback(() => {
    if (!pnidState || !redundancySetEquipmentIds) return;
    const selection = resolveRedundancySetSelection(redundancySetEquipmentIds, pnidState);
    if (selection.untaggedItems.length > 0) {
      showAlert(
        "Not marked as a redundancy set",
        `Only tagged equipment can be members. These selected equipment carry no tag: ${selection.untaggedItems.join(", ")}.`,
        "warning",
      );
      return;
    }
    if (selection.distinctTagCount < 2) {
      showAlert("Not marked as a redundancy set", "A redundancy set needs at least two different tags.", "warning");
      return;
    }
    putRedundancySetTags.mutate(selection.tags, {
      onSuccess: (sets) => {
        const markedSet = findRedundancySet(sets, selection.tags[0]);
        showAlert("Redundancy set saved", `Members: ${(markedSet?.tags ?? selection.tags).join(", ")}.`, "success");
      },
      onError: (error) => {
        const conflict = redundancySetConflictOf(error);
        if (!conflict) {
          showAlert("Redundancy set not saved", "Could not save the redundancy set.", "error");
          return;
        }
        const conflictingSets = conflict.sets.map((set) => set.tags.join(", ")).join("; ");
        showAlert(
          "Redundancy set not saved",
          `${conflict.message} Existing sets: ${conflictingSets}. Remove a member from one set first.`,
          "warning",
        );
      },
    });
    // showAlert is a new function every render and only sets state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pnidState, putRedundancySetTags.mutate, redundancySetEquipmentIds]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (selectedBackendAnnotationTargets.length === 0 || setupDockOpen) return;
      if (isEventInsideEditorDock(event)) return;
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName?.toLowerCase();
      const isEditable =
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        Boolean(target?.isContentEditable);
      if (isEditable) return;
      event.preventDefault();
      handleDeleteSelectedWizardAnnotations();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleDeleteSelectedWizardAnnotations, selectedBackendAnnotationTargets.length, setupDockOpen]);

  const openAnnotationImportDialog = useCallback(() => {
    if (!selectedDoc) return;
    setAnnotationImportSearch("");
    setAnnotationImportSourceId("");
    setAnnotationImportOpen(true);
  }, [selectedDoc]);

  const runAnnotationImport = useCallback(async (overwriteExisting: boolean) => {
    if (!selectedDoc || !annotationImportSourceId) return;
    setAnnotationImporting(true);
    try {
      pushPnidUndoSnapshot(overwriteExisting ? "Replace imported annotations" : "Import annotations");
      const result = await pnidApi.importAnnotations(
        selectedDoc.id,
        annotationImportSourceId,
        { overwriteExisting },
      );
      const refreshed = await refreshPnidState(selectedDoc.id);
      setPnidState(refreshed);
      setAnnotationImportOpen(false);
      setAnnotationImportSourceId("");
      setAnnotationImportSearch("");
      await onReloadDocuments();
      const sourceLabel = selectedAnnotationImportSource
        ? getDocumentPrimaryLabel(selectedAnnotationImportSource)
        : "selected source";
      const copiedSummary = formatAnnotationCountSummary({
        equipments: result.copied.equipments,
        nodes: result.copied.nodes,
        instruments: result.copied.instruments,
        control_loops: result.copied.control_loops,
        relationships: result.copied.relationships,
      }) || "0 annotations";
      const replacedSummary = formatAnnotationCountSummary({
        equipments: result.replaced.equipments,
        nodes: result.replaced.nodes,
        instruments: result.replaced.instruments,
        control_loops: result.replaced.control_loops,
        relationships: result.replaced.relationships,
      });
      const geometryAdjustmentText = formatImportGeometryAdjustment(result.geometry_transform);
      setAnnotationImportAdjustment({
        sourceLabel,
        copiedSummary,
        replacedSummary: replacedSummary || "",
        overwroteExisting: result.overwrote_existing,
        skippedRelationships: result.skipped_relationships,
        geometryAdjustmentText,
      });
      setSelectedWizardItemKeys(getAllSavedAnnotationKeys(refreshed));
      showAlert(
        "Annotations imported",
        `${result.overwrote_existing && replacedSummary ? `Replaced ${replacedSummary}. ` : ""}Imported ${copiedSummary} from ${sourceLabel}.${geometryAdjustmentText ? ` ${geometryAdjustmentText}` : ""}${result.skipped_relationships ? ` Skipped ${result.skipped_relationships} relationship(s) with missing copied endpoints.` : ""} Target document metadata was retained.`,
        "success",
      );
    } catch (error: any) {
      console.error("Failed to import annotations:", error);
      const detail = error?.response?.data?.detail;
      showAlert(
        "Import failed",
        typeof detail === "string" ? detail : "Could not import annotations from the selected document.",
        "error",
      );
    } finally {
      setAnnotationImporting(false);
    }
  }, [
    annotationImportSourceId,
    onReloadDocuments,
    pushPnidUndoSnapshot,
    refreshPnidState,
    selectedAnnotationImportSource,
    selectedDoc,
  ]);

  const handleImportAnnotations = useCallback(() => {
    if (!selectedDoc || !annotationImportSourceId) return;
    if (targetHasAnnotations) {
      const sourceLabel = selectedAnnotationImportSource
        ? getDocumentPrimaryLabel(selectedAnnotationImportSource)
        : "selected source";
      const sourceDomain = selectedAnnotationImportSource
        ? documentDomainPathLabel(domainIndex, selectedAnnotationImportSource)
        : "";
      const targetLabel = getDocumentPrimaryLabel(selectedDoc);
      const targetDomain = documentDomainPathLabel(domainIndex, selectedDoc);
      setConfirmDialog({
        isOpen: true,
        title: "Replace target annotations?",
        message: `Import ${diagramTypeLabel(diagramMode)} annotations from ${sourceLabel}${sourceDomain ? ` (${sourceDomain})` : ""} into ${targetLabel}${targetDomain ? ` (${targetDomain})` : ""}. This will replace ${targetAnnotationSummary || "the existing target annotations"}. Target P&ID number, title, document ID, plant, notes, metadata, and diagram type will be retained.`,
        type: "danger",
        confirmText: "Replace & Import",
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          void runAnnotationImport(true);
        },
      });
      return;
    }
    void runAnnotationImport(false);
  }, [
    annotationImportSourceId,
    diagramMode,
    getAnnotation,
    runAnnotationImport,
    selectedAnnotationImportSource,
    selectedDoc,
    targetAnnotationSummary,
    targetHasAnnotations,
  ]);

  useEffect(() => {
    if (!annotationImportOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !annotationImporting) {
        setAnnotationImportOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [annotationImportOpen, annotationImporting]);

  // Comment handlers
  const handleSelectComment = (
    commentId: string,
    screenX: number,
    screenY: number
  ) => {
    setSelectedCommentId(commentId);
    setSelectedCommentScreenPos({ x: screenX, y: screenY });
  };

  const handleResolveComment = async () => {
    if (!selectedDoc || !selectedCommentId) return;
    try {
      const updated = await commentsApi.resolve(
        selectedDoc.id,
        selectedCommentId,
        effectiveUserName || "Anonymous"
      );
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => (prev ?? []).map((c) => (c.id === selectedCommentId ? updated : c)),
      );
      setSelectedCommentId(null);
      setSelectedCommentScreenPos(null);
    } catch (err) {
      console.error("Failed to resolve comment:", err);
    }
  };

  const handleUnresolveComment = async () => {
    if (!selectedDoc || !selectedCommentId) return;
    try {
      const updated = await commentsApi.unresolve(
        selectedDoc.id,
        selectedCommentId
      );
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => (prev ?? []).map((c) => (c.id === selectedCommentId ? updated : c)),
      );
    } catch (err) {
      console.error("Failed to unresolve comment:", err);
    }
  };

  const handleDeleteComment = async () => {
    if (!selectedDoc || !selectedCommentId) return;
    try {
      await commentsApi.delete(selectedDoc.id, selectedCommentId);
      queryClient.setQueryData<CanvasComment[]>(
        queryKeys.comments(selectedDoc.id),
        (prev) => (prev ?? []).filter((c) => c.id !== selectedCommentId),
      );
      setSelectedCommentId(null);
      setSelectedCommentScreenPos(null);
    } catch (err) {
      console.error("Failed to delete comment:", err);
    }
  };

  const handlePrefillFromAI = async () => {
    if (!selectedDoc) return;
    setLoadingAI(true);
    trackAction('ai_prefill_triggered');
    const prefillStart = Date.now();
    try {
      const updatedState = await pnidApi.prefillFromAI(selectedDoc.id);
      setPnidState(updatedState);
      setPnidNoDraft(updatedState.pnid_no ?? "");
      setPnidTitleDraft(updatedState.pnid_title ?? "");
      setDrawingClassDraft(updatedState.drawing_class ?? "");
      setLegendSheetIdDraft(updatedState.legend_sheet_id ?? "");
      void queryClient.invalidateQueries({ queryKey: queryKeys.aiData(selectedDoc.id) });

      const firstUnmarked = updatedState.equipments.find(
        (e) => e.status === "ai_identified"
      );
      if (firstUnmarked) {
        setActiveEquipmentId(firstUnmarked.id);
      } else if (updatedState.equipments.length > 0) {
        setActiveEquipmentId(updatedState.equipments[0].id);
      }

      trackAction('ai_prefill_completed', {
        equipment_count: updatedState.equipments.length,
        line_count: updatedState.nodes.length,
        latency_ms: Date.now() - prefillStart,
      });
      showAlert(
        "AI Data Loaded",
        `Loaded ${updatedState.equipments.length} equipment items and ${updatedState.nodes.length} lines from AI data.`,
        "success"
      );
      if (updatedState.equipments.length > 0) {
        closeSetupDock();
      }
    } catch (error) {
      console.error("Failed to prefill from AI:", error);
      showAlert(
        "Prefill Failed",
        "No AI data found for this document or failed to load.",
        "error"
      );
    } finally {
      setLoadingAI(false);
    }
  };

  const savePnidSetup = async () => {
    if (!selectedDoc) return;
    if (readOnlyWizard) return; // Reviewers and viewing docs assigned to others cannot save
    const nextPnidNo = pnidNoDraft.trim();
    if (!nextPnidNo) {
      showAlert("PnID No required", "Please enter a P&ID number.", "warning");
      return;
    }
    try {
      const updated = await setupMutation.mutateAsync({
        pnid_no: nextPnidNo,
        pnid_title: pnidTitleDraft.trim() || undefined,
        drawing_class: drawingClassDraft || null,
        legend_sheet_id: legendSheetIdDraft || null,
      });
      setPnidState(updated);
      setPnidNoDraft(updated.pnid_no ?? "");
      setPnidTitleDraft(updated.pnid_title ?? "");
      setDrawingClassDraft(updated.drawing_class ?? "");
      setLegendSheetIdDraft(updated.legend_sheet_id ?? "");
      onPatchDocument(selectedDoc.id, {
        pnid_no: updated.pnid_no ?? null,
        pnid_title: updated.pnid_title ?? null,
      });
      showAlert(
        "Saved",
        "Drawing setup saved.",
        "success"
      );
      closeSetupDock();
    } catch (error) {
      console.error(error);
      showAlert("Save failed", "Could not save drawing setup.", "error");
    }
  };

  /* The record of the legend the drawing's setup names, in the drawing's plant.
     The saved reference counts, not the setup draft, so the button follows what
     the server holds. */
  const applicableLegendRoute = pnidState?.legend_sheet_id && selectedDoc.domain_id
    ? legendSheetRecordRoute(selectedDoc.domain_id, pnidState.legend_sheet_id)
    : null;

  const getActiveEquipment = () =>
    pnidState?.equipments.find((e) => e.id === activeEquipmentId) ?? null;
  const getActiveNode = () =>
    pnidState?.nodes.find((n) => n.id === activeNodeId) ?? null;

  // Node marking actions
  const handleMarkNode = (
    nodeId: string,
    // A double-click on the canvas marks a line the SME is already looking at.
    { navigate = true }: { navigate?: boolean } = {}
  ) => {
    const node = pnidState?.nodes.find((candidate) => candidate.id === nodeId);
    if (diagramMode === 'pfd' && node?.is_duct) {
      showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
      return;
    }
    // Set the node as active and enter marking mode
    closeInstrumentEditor();
    setLineSegmentReviewPopover(null);
    setActiveNodeId(nodeId);
    setIsNodeMarkingMode(true);
    setNodeDrawingShape(node?.is_duct ? 'duct' : 'pipeline');
    if (navigate) navigateCanvasTo(nodeGeometryBbox(node));
    // The form, which prefills each end's sheet, opens when the path is drawn.
    if (node && pnidState) setLineDrawTarget({ nodeId });
    // The drawing will be handled by handleLineDrawn or handleBoxDrawn when user draws on canvas
  };

  const handleUnmarkNode = async (nodeId: string) => {
    if (!selectedDoc) return;
    const existingNode = pnidState?.nodes.find((node) => node.id === nodeId);
    if (diagramMode === 'pfd' && existingNode?.is_duct) {
      showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
      return;
    }
    trackAction('item_unmarked', { item_type: 'node', item_id: nodeId });
    try {
      // Clear whichever mutually exclusive geometry marks this connection.
      await pnidApi.updateNode(selectedDoc.id, nodeId, {
        polyline: null,
        duct_bbox: null,
        status: "ai_identified",
      });
      // Refresh state
      const newState = takeDrawingResponse(await pnidApi.get(selectedDoc.id));
      setPnidState(newState);
      setCanvasFocusRequest(null);
      // Automatically enter marking mode for redrawing
      setIsNodeMarkingMode(true);
      if (newState.nodes.some((n) => n.id === nodeId)) setLineDrawTarget({ nodeId });
    } catch (err) {
      console.error("Failed to unmark node:", err);
    }
  };

  // Instrument marking actions
  const openMarkDialog = (
    kind: PnidDialogKind,
    bbox: BoundingBox,
    anchor: { x: number; y: number },
    initialValues?: any
  ) => {
    replaceDockView();
    setMarkDialog({
      isOpen: true,
      kind,
      bbox,
      anchor,
      editTarget: null,
      initialValues: initialValues ?? {},
    });
  };


  const openEditDialog = (
    kind: EditableWizardTargetKind,
    id: string,
    options?: {
      shouldFocus?: boolean;
      /** An instrument opened from the equipment or line form, on top of that form. */
      nested?: boolean;
    }
  ) => {
    if (!pnidState) return;
    // Both instrument kinds look through every instrument on the drawing: the kind says
    // which parent to activate, not which list the instrument is in, and an unattached
    // one is in neither parent-keyed list.
    const found =
      kind === "equipment"
        ? pnidState.equipments.find((e) => e.id === id)
        : kind === "equipmentInstrument"
        ? pnidState.instruments.find((i) => i.id === id)
        : kind === "node"
        ? pnidState.nodes.find((n) => n.id === id)
        : kind === "nodeInstrument"
        ? pnidState.instruments.find((i) => i.id === id)
        : pnidState.control_loops.find((l) => l.id === id);
    if (!found) return;
    if (kind === "equipmentInstrument") {
      const inst = found as PnidInstrument;
      const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
      setActiveEquipmentId(effectiveContext.context_type === 'equipment' ? effectiveContext.context_id : null);
    }
    if (kind === "nodeInstrument") {
      const inst = found as PnidInstrument;
      const effectiveContext = getInstrumentEffectiveContext(inst, pnidState);
      setActiveNodeId(effectiveContext.context_type === 'node' ? effectiveContext.context_id : null);
    }
    if (kind === "node") {
      setActiveNodeId(id);
    }
    if (kind === "controlLoop") {
      // For now, just show an alert - control loop editing can be added later
      showAlert(
        "Edit Control Loop",
        `Loop ${(found as any).loop_tag}: ${
          (found as any).members?.join(", ") || "No members"
        }`,
        "info"
      );
      return;
    }
    const shouldFocus = options?.shouldFocus !== false;
    if (shouldFocus && (found as any).bbox) {
      navigateCanvasTo((found as any).bbox);
    }

    // For instrument kinds, open the unified InstrumentEditor
    if (kind === "equipmentInstrument" || kind === "nodeInstrument") {
      handleRightClickInstrument(id, options?.nested);
      return;
    }

    replaceDockView();
    setMarkDialog({
      isOpen: true,
      kind,
      bbox: kind === "node"
        ? ((found as PnidNode).is_duct
            ? (found as PnidNode).duct_bbox ?? null
            : (found as PnidNode).polyline
              ? polylineToBbox((found as PnidNode).polyline!, true)
              : null)
        : (found as any).bbox,
      anchor: null,
      editTarget: { kind, id },
      initialValues: {
        ...(found as any),
        // For node/equipment editing, set _markExistingId so PnidMarkDialog shows instrument list UI
        ...(kind === "node" || kind === "equipment" ? { _markExistingId: id } : {}),
      },
    });
  };

  // A Primary P&ID link lands here. The panel can only open once this drawing's
  // annotation is loaded, and it opens once per link: the parameter is dropped
  // from the URL straight after, so reopening or reloading the drawing later
  // shows it as the reviewer left it.
  useEffect(() => {
    if (!routeEditEquipmentId) {
      lastRouteEditEquipmentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const editKey = `${selectedDoc.id}:edit:${routeEditEquipmentId}`;
    if (lastRouteEditEquipmentKeyRef.current === editKey) {
      return;
    }
    lastRouteEditEquipmentKeyRef.current = editKey;

    const equipment = pnidState.equipments.find((candidate) => candidate.id === routeEditEquipmentId);
    if (equipment) {
      setAssetRailTab('equipment');
      setActiveEquipmentId(equipment.id);
      navigateCanvasTo(navigationBoxOf('equipment', equipment));
      openEditDialog("equipment", equipment.id);
    } else {
      console.warn("Linked equipment to edit not found:", routeEditEquipmentId);
    }

    consumeRouteParam(EDIT_EQUIPMENT_PARAM);
  }, [navigateCanvasTo, pnidState, pnidStateLoadedDocId, routeEditEquipmentId, selectedDoc?.id]);

  // The instrument twin of the link above, and it waits on the same things: this
  // drawing's annotation loaded, the instrument saved on it, and one shot per
  // link before the parameter is dropped from the URL. `focusInstrument` puts the
  // wizard on the instrument and `handleRightClickInstrument` opens the editor
  // over it, so the link lands exactly where a right-click on the sheet does.
  //
  // When it cannot open the editor — a higher-ranked target in the same URL, or
  // an instrument this drawing does not hold — it says so and leaves the canvas
  // alone, as a missing equipment target does.
  useEffect(() => {
    if (!routeEditInstrumentId) {
      lastRouteEditInstrumentKeyRef.current = null;
      return;
    }
    if (!selectedDoc?.id || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) {
      return;
    }

    const editKey = `${selectedDoc.id}:edit-instrument:${routeEditInstrumentId}`;
    if (lastRouteEditInstrumentKeyRef.current === editKey) {
      return;
    }
    lastRouteEditInstrumentKeyRef.current = editKey;

    if (
      winningRouteTarget({
        routeEditEquipmentId,
        routeTargetEquipmentId,
        routeTargetNodeId,
        routeEditInstrumentId,
        routeTargetInstrumentId,
      }) !== 'instrument'
    ) {
      console.warn(
        "edit_instrument_id ignored: edit_equipment_id/target_equipment_id/target_node_id takes precedence",
      );
    } else if (focusInstrument(routeEditInstrumentId)) {
      void handleRightClickInstrument(routeEditInstrumentId);
    } else {
      console.warn("Linked instrument to edit not found:", routeEditInstrumentId);
    }

    consumeRouteParam(EDIT_INSTRUMENT_PARAM);
  }, [pnidState, pnidStateLoadedDocId, routeEditInstrumentId, selectedDoc?.id]);

  const updateNodeSegmentsForDistinct = useCallback(
    async (
      nodeId: string,
      distinct: MergedLineSegment,
      nextStatus: "confirmed" | "deleted"
    ) => {
      if (!selectedDoc) return;
      if (!pnidState) return;
      const node = pnidState.nodes.find((n) => n.id === nodeId);
      if (!node) return;
      const current = node.line_segments ?? [];
      const ids = new Set(distinct.source_segment_ids);
      const updated = current.map((s) =>
        ids.has(s.id) ? { ...s, status: nextStatus } : s
      );
      await pnidApi.updateNode(selectedDoc.id, nodeId, {
        line_segments: updated,
      });
      const newState = takeDrawingResponse(await pnidApi.get(selectedDoc.id));
      setPnidState(newState);
    },
    [selectedDoc, pnidState, takeDrawingResponse]
  );

  const confirmAllNodeSegments = useCallback(
    async (nodeId: string) => {
      if (!selectedDoc) return;
      if (!pnidState) return;
      const node = pnidState.nodes.find((n) => n.id === nodeId);
      if (!node) return;
      const current = node.line_segments ?? [];
      const updated = current.map((s) =>
        s.status === "deleted" ? s : { ...s, status: "confirmed" as const }
      );
      await pnidApi.updateNode(selectedDoc.id, nodeId, {
        line_segments: updated,
      });
      const newState = takeDrawingResponse(await pnidApi.get(selectedDoc.id));
      setPnidState(newState);
    },
    [selectedDoc, pnidState, takeDrawingResponse]
  );

  const handleWizardCanvasSelect = (target: {
    kind: WizardSelectionKind;
    id: string;
    screenPosition?: { x: number; y: number };
    isMultiSelect?: boolean;
  }) => {
    // A finished line waits in its form for Save or Cancel. A click on a saved annotation
    // may cover the form with a line's segment review, but opens no other editor.
    if (drawnLineOpen) {
      const node = target.kind === "node" ? pnidState?.nodes.find((n) => n.id === target.id) : null;
      if (!(currentAssetKind === 'line' && node?.line_segments?.length)) return;
    }
    const key = wizardItemKey(target);
    if (target.isMultiSelect) {
      setSelectedWizardItemKeys((prev) => {
        const next = new Set(prev);
        if (next.has(key)) {
          next.delete(key);
        } else {
          next.add(key);
        }
        return next;
      });
      return;
    }
    setSelectedWizardItemKeys(new Set([key]));

    if (target.kind === 'equipment') {
      // Set active equipment and open edit dialog
      setActiveEquipmentId(target.id);
      setCanvasFocusRequest(null);
      openEditDialog(target.kind, target.id, { shouldFocus: false });
      return;
    }

    if (target.kind === "node") {
      setActiveNodeId(target.id);
      setCanvasFocusRequest(null);

      if (currentAssetKind === 'line') {
        // Prefer the segment review popover when line segments exist for this node
        const node = pnidState?.nodes.find((n) => n.id === target.id);
        const hasSegments = Boolean(
          node?.line_segments && node.line_segments.length > 0
        );
        if (hasSegments) {
          if (!drawnLineOpen) closeMarkDialog();
          closeInstrumentEditor();
          setLineSegmentReviewPopover({
            nodeId: target.id,
            hoveredDistinctId: null,
            selectedDistinctId: null,
          });
          return;
        }

        // Otherwise open the detailed line dialog directly
        openEditDialog("node", target.id, { shouldFocus: false });
        return;
      } else {
        // With equipment in hand, open the edit dialog directly
        openEditDialog("node", target.id, { shouldFocus: false });
      }
      return;
    }

    if (target.kind === "controlLoop") {
      openEditDialog("controlLoop", target.id, { shouldFocus: false });
      return;
    }
    if (target.kind === "relationship") return;

    openEditDialog(target.kind, target.id, { shouldFocus: false });
  };

  /** Closes the mark form. Its save or cancel also lets go of an armed Equipment, Line or Duct tool. */
  const closeMarkDialog = () => {
    setMarkDialog((p) => ({ ...p, isOpen: false }));
    if (armedTool === 'duct') setNodeDrawingShape('pipeline');
    setArmedTool((tool) => (tool === 'equipment' || tool === 'line' || tool === 'duct' ? null : tool));
  };
  /** Cancel or Escape on the open form: a finished line's form leaves line drawing with it. */
  const closeMarkForm = () => (drawnLineOpen ? exitLineDrawing() : closeMarkDialog());

  /** Leaves line drawing: the form, the marking mode and the drawn path go, and the canvas returns to rest. */
  const exitLineDrawing = () => {
    setLineDrawTarget(null);
    setIsNodeMarkingMode(false);
    setNodeDrawingShape('pipeline');
    closeMarkDialog();
    setCanvasClearSignal((prev) => prev + 1);
  };


  const handleRedrawPath = async () => {
    const nodeId = markDialog.initialValues?._markExistingId;
    if (!nodeId || markDialog.kind !== "node") return;
    const confirmed = window.confirm(
      "Redraw this line? Current line marking will be removed so you can draw it again."
    );
    if (!confirmed) return;
    closeMarkDialog();
    await handleUnmarkNode(nodeId);
  };

  const discardDraft = () => {
    closeMarkDialog();
    setBoxDraft(null);
    setIsNodeMarkingMode(false);
    setNodeDrawingShape('pipeline');
    setLineDrawTarget(null);
    setCanvasClearSignal((prev) => prev + 1);
    setCanvasHasDraft(false);
  };

  /**
   * Whether the form already shows this equipment or line. Reopening it would reset the
   * SME's unsaved draft, so the caller keeps it; an instrument opened from that form covers
   * it, so the instrument closes instead.
   */
  const keepsOpenAssetForm = (kind: 'equipment' | 'node', id: string): boolean => {
    if (!(markDialog.isOpen && markDialog.editTarget?.kind === kind && markDialog.editTarget.id === id)) return false;
    if (instrumentEditorState?.nested) closeInstrumentEditor();
    return true;
  };

  /**
   * Opens one pending agent element, as its row's flag does: the rail moves to the
   * element's tab, the canvas frames it and its editor opens through the path a
   * right-click or a form's Edit uses.
   */
  const selectAgentReviewItem = (key: string) => {
    const target = parseAgentReviewKey(key);
    if (!target || !pnidState) return;
    setAssetRailTab(target.kind === 'node' ? 'lines' : target.kind === 'instrument' ? 'instruments' : 'equipment');
    if (target.kind === 'instrument') {
      const instrument = pnidState.instruments.find((candidate) => candidate.id === target.id);
      if (!instrument) return;
      navigateCanvasTo(navigationBoxOf('instrument', instrument));
      // An instrument of another asset is hidden while isolated, so going to it ends Isolate.
      setIsolateActiveAsset(false);
      // The editor toggles closed when asked to open the instrument it already shows. The
      // canvas selection can name another instrument (Eye), so it does not decide this.
      if (instrumentEditorState?.instrument.id !== target.id) {
        void handleRightClickInstrument(target.id);
      }
      return;
    }
    if (target.kind === 'equipment') {
      const equipment = pnidState.equipments.find((candidate) => candidate.id === target.id);
      if (!equipment) return;
      setActiveEquipmentId(equipment.id);
      if (!keepsOpenAssetForm('equipment', equipment.id)) openEditDialog("equipment", equipment.id);
      return;
    }
    const node = pnidState.nodes.find((candidate) => candidate.id === target.id);
    if (!node) return;
    setActiveNodeId(node.id);
    navigateCanvasTo(nodeGeometryBbox(node));
    if (!keepsOpenAssetForm('node', node.id)) openEditDialog("node", node.id, { shouldFocus: false });
  };

  // Save advances. With Needs review on, a save names its element here; once the
  // refreshed drawing no longer lists it as pending, the next flagged row after it in
  // the rail's order at the save opens, on its own tab. Past the last row nothing
  // opens: the order does not wrap. With the filter off, a save does not advance.
  useEffect(() => {
    if (!needsReviewOn) {
      agentReviewAdvanceFromRef.current = null;
      return;
    }
    const savedKey = agentReviewAdvanceFromRef.current;
    if (!savedKey || pendingAgentItems.some((item) => item.key === savedKey)) return;
    // The save handler closes its editor after the refresh, in a later render; an
    // editor opened before that close would be closed with it.
    if (instrumentEditorState || markDialog.isOpen) return;
    agentReviewAdvanceFromRef.current = null;
    const atSave = agentReviewOrderAtSaveRef.current;
    const pendingKeys = new Set(pendingAgentItems.map((item) => item.key));
    const next = atSave.slice(atSave.indexOf(savedKey) + 1).find((key) => pendingKeys.has(key));
    if (next) selectAgentReviewItem(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectAgentReviewItem is this render's; the deps are the save's signals
  }, [instrumentEditorState, markDialog.isOpen, needsReviewOn, pendingAgentItems]);
  // The first flagged row of the rail, from its first tab, with no filter or search yet.
  useEffect(() => {
    if (!reviewOpensFirstRowRef.current || !pnidState || pnidStateLoadedDocId !== selectedDoc.id) return;
    reviewOpensFirstRowRef.current = false;
    const [first] = flaggedRowOrder({
      fromTab: 'equipment', assets: railAssets, equipmentTypeLabel, query: '', pendingIds: pendingAgentIds,
      publishableInstrumentIds: railPublishableInstrumentIds,
    });
    if (first) selectAgentReviewItem(first);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per Agent review link, when its drawing loads
  }, [pnidState, pnidStateLoadedDocId, selectedDoc.id]);
  selectAgentReviewItemRef.current = selectAgentReviewItem;

  /**
   * The unsaved work in flight, named for the discard-draft confirm; empty when there
   * is none. This is the one definition of "unsaved". Collected regions are unsaved
   * work, and so is an armed redraw the way Rebox has always been. So is a finished
   * line waiting in its form: the canvas holds no draft of it any more.
   */
  const markFormDraftLabel = () => (markDialog.drawnLine
    ? 'the line you drew'
    : `the open ${markDialog.kind === 'node' ? 'line' : 'equipment'} form`);
  const unsavedDraftItems = (): string[] => {
    const items: string[] = [];
    if (markDialog.isOpen) items.push(markFormDraftLabel());
    if (isNodeMarkingMode) items.push('the line redraw');
    if (boxDraft?.target !== undefined) items.push('the armed redraw');
    else if (boxDraft && boxDraft.regions.length > 0) {
      items.push(`${boxDraft.regions.length} collected ${boxDraft.regions.length === 1 ? 'region' : 'regions'}`);
    }
    if (canvasHasDraft) items.push('the shape on the canvas');
    return items;
  };
  const hasUnsavedDraft = () => unsavedDraftItems().length > 0;

  /** Every way out of the Drawing details dock. */
  const closeSetupDock = () => {
    setSetupDockOpen(false);
  };

  // The ✕ and the toolbar button leave without saving, and drop the unsaved edits so
  // the form shows the stored values when it next opens.
  const closeSetupWithoutSaving = () => {
    setPnidNoDraft(pnidState?.pnid_no ?? "");
    setPnidTitleDraft(pnidState?.pnid_title ?? "");
    setDrawingClassDraft(pnidState?.drawing_class ?? "");
    setLegendSheetIdDraft(pnidState?.legend_sheet_id ?? "");
    closeSetupDock();
  };

  /**
   * The toolbar's Drawing details button. Opening the dock closes the open form, so a
   * draft in flight asks the discard-draft confirm first; pressed while open, it closes
   * the dock as the ✕ does.
   */
  const toggleSetupDock = () => {
    if (setupDockOpen) {
      closeSetupWithoutSaving();
      return;
    }
    if (hasUnsavedDraft()) {
      setDiscardDraftConfirm({ isOpen: true });
      return;
    }
    setSetupDockOpen(true);
  };

  /** Discards the draft in flight, then does what the confirm stood in front of. */
  const confirmDiscardDraft = () => {
    const { openDrawingOnConfirm } = discardDraftConfirm;
    if (openDrawingOnConfirm) {
      setDiscardDraftConfirm({ isOpen: false });
      closeInstrumentEditor();
      discardDraft();
      openPickedDrawing(openDrawingOnConfirm);
      return;
    }
    discardDraft();
    if (discardDraftConfirm.armOnConfirm) toggleDrawTool(discardDraftConfirm.armOnConfirm);
    else setSetupDockOpen(true);
    setDiscardDraftConfirm({ isOpen: false });
  };

  // Click-to-attach for unattached (context-free) AI/template instruments.
  // AI-published instruments (provenance.source='template_match') land without any
  // context_type/context_id; this adopts them onto the active equipment/node immediately
  // so they become first-class equipment/node instruments everywhere.
  const handleDirectAttachAnnotation = useCallback(async (annotationId: string) => {
    if (!selectedDoc || readOnlyWizard) return;
    const contextType: 'equipment' | 'node' = currentAssetKind === 'equipment' ? 'equipment' : 'node';
    const contextId = currentAssetKind === 'equipment' ? activeEquipmentId : activeNodeId;
    if (!contextId) return;
    const instrument = pnidState?.instruments.find((inst) => inst.id === annotationId);
    // Only adopt instruments that are not attached anywhere; never steal from another context.
    if (!instrument || getInstrumentEffectiveContext(instrument, pnidState).context_type !== null) return;

    try {
      await instrumentApi.updateInstrument(selectedDoc.id, annotationId, {
        context_type: contextType,
        context_id: contextId,
      });
    } catch (error) {
      console.error('Failed to attach AI instrument:', error);
      showAlert('Attach failed', 'Could not attach this instrument. Please try again.', 'error');
      return;
    }

    await refreshPnidState(selectedDoc.id);
    trackAction('template_annotation_direct_attached', {
      annotation_id: annotationId,
      context_type: contextType,
      context_id: contextId,
    });
  }, [selectedDoc, readOnlyWizard, currentAssetKind, activeEquipmentId, activeNodeId, pnidState, refreshPnidState, trackAction]);


  const buildCandidateInstrumentText = (
    candidate: NodePointCandidate,
    fallbackLabel: string
  ) => {
    const tag = candidate.tagText?.trim() || "";
    const symbol =
      candidate.symbolMatch?.trim() || candidate.identifiedText?.trim() || "";
    if (tag) {
      return symbol ? `${tag} (${symbol})` : tag;
    }
    return symbol || fallbackLabel;
  };

  const runNodePointClassification = async (
    nodeId: string,
    polyline: number[]
  ) => {
    if (!ENABLE_NODE_POINT_CLASSIFICATION) return;
    if (!selectedDoc) return;
    if (!polyline || polyline.length < 4) return;

    const points = polylineToPoints(polyline);
    if (points.length === 0) return;

    const runId = nodePointRunId.current + 1;
    nodePointRunId.current = runId;

    const initialCandidates: NodePointCandidate[] = points.map((point) => ({
      id: crypto.randomUUID(),
      nodeId,
      point,
      status: "running",
      options: [],
      source: "classification",
    }));
    setNodePointCandidates(initialCandidates);

    const updateCandidate = (
      id: string,
      patch: Partial<NodePointCandidate>
    ) => {
      if (nodePointRunId.current !== runId) return;
      setNodePointCandidates((prev) =>
        prev.map((candidate) =>
          candidate.id === id ? { ...candidate, ...patch } : candidate
        )
      );
    };

    const processCandidate = async (candidate: NodePointCandidate) => {
      let bbox: BoundingBox | null = null;
      let samError: string | undefined;
      const samStart = Date.now();
      try {
        const samResult = await documentApi.samSegment(
          selectedDoc.id,
          Math.round(candidate.point.x),
          Math.round(candidate.point.y)
        );
        bbox = {
          x1: samResult.bbox.x1,
          y1: samResult.bbox.y1,
          x2: samResult.bbox.x2,
          y2: samResult.bbox.y2,
        };
        trackAction('sam_detection_completed', {
          latency_ms: Date.now() - samStart,
          success: true,
        });
      } catch (error) {
        console.error("SAM detection failed for node point:", error);
        samError = "SAM failed";
        bbox = pointToBbox(candidate.point, 24);
        trackAction('sam_detection_completed', {
          latency_ms: Date.now() - samStart,
          success: false,
        });
      }

      updateCandidate(candidate.id, { bbox, error: samError });

      try {
        const classify = await pnidApi.classifyInstrumentCandidates(
          selectedDoc.id,
          bbox!,
          "node",
          nodeId,
          3
        );
        const result = classify.result || {};
        const options = (result.candidates || []).map((item) => ({
          label: item.label as NodePointCandidateLabel,
          score: item.score,
        }));

        updateCandidate(candidate.id, {
          status: "ready",
          options,
          identifiedText: result.identified_text ?? null,
          symbolMatch: result.symbol_match ?? null,
          tagText: result.tag_text ?? null,
          confidence: result.confidence ?? null,
        });
      } catch (error) {
        console.error("Classification failed for node point:", error);
        updateCandidate(candidate.id, {
          status: "error",
          error: "Classification failed",
        });
      }
    };

    const runWithConcurrency = async <T,>(
      items: T[],
      limit: number,
      task: (item: T) => Promise<void>
    ) => {
      const executing = new Set<Promise<void>>();
      for (const item of items) {
        const promise = task(item);
        executing.add(promise);
        const cleanup = () => executing.delete(promise);
        promise.then(cleanup).catch(cleanup);
        if (executing.size >= limit) {
          await Promise.race(executing);
        }
      }
      await Promise.all(executing);
    };

    await runWithConcurrency(initialCandidates, 2, processCandidate);
  };

  const resolveNodePointCandidate = async (
    candidateId: string,
    label: NodePointCandidateLabel | "ignore"
  ) => {
    if (!selectedDoc || !pnidState) return;
    const candidate = nodePointCandidates.find(
      (item) => item.id === candidateId
    );
    if (!candidate) return;
    const isBranchCandidate = candidate.source === "branch";
    if (!ENABLE_NODE_POINT_CLASSIFICATION && !isBranchCandidate) return;

    if (label === "ignore") {
      setNodePointCandidates((prev) =>
        prev.filter((item) => item.id !== candidateId)
      );
      return;
    }

    const isBranch = label === "Branch";
    const bbox = isBranch
      ? pointToBbox(candidate.point, 14)
      : candidate.bbox || pointToBbox(candidate.point, 24);
    // DECISION(2026-10-16, Abhishek Kumar): a branch saved from a candidate carries no
    // instrument text, the same as a branch marked by hand.
    // Instrument text is unique across the drawing, so "Branch to <line_no>" was refused
    // whenever two branches met lines sharing a line_no (two "BYPASS LINE"s on PR/13B);
    // the row already shows the target through `branch_ref`.
    const instrumentText = isBranch
      ? null
      : buildCandidateInstrumentText(candidate, label);
    const instrumentType = isBranch ? null : (label as InstrumentCategory);

    try {
      await instrumentApi.createInstrument(selectedDoc.id, {
        context_type: 'node',
        context_id: candidate.nodeId,
        instrument_type: instrumentType,
        instrument_text: instrumentText,
        bbox,
        branch_ref: isBranch ? candidate.branchRef ?? null : undefined,
        branch_direction: isBranch ? candidate.branchDirection ?? "IN" : null,
      });
      await refreshPnidState(selectedDoc.id);
      setNodePointCandidates((prev) =>
        prev.filter((item) => item.id !== candidateId)
      );
    } catch (error) {
      console.error("Failed to create node instrument from candidate:", error);
      showAlert(
        "Save failed",
        "Could not save this instrument. Please try again.",
        "error"
      );
    }
  };

  const handleCanvasInteraction = useCallback((counts: Record<string, number>) => {
    trackAction('canvas_interaction_batch', { ...counts, flush_interval_s: 10 });
  }, [trackAction]);

  // Trigger instrument drawing mode from the mark dialog's "Mark Instrument" button
  const handleMarkInstrumentFromDialog = useCallback((contextType: 'equipment' | 'node', contextId: string) => {
    instrumentContextOverrideRef.current = { contextType, contextId };
    setBoxDraft(armDraft('instrument'));
  }, []);

  /**
   * The equipment or line a new instrument hangs off. The symbol region decides it, so
   * only `regions[0]` is tested here — a balloon drawn out in the margin must not move the
   * instrument to whatever it happens to sit over. Null when nothing resolves, and the
   * instrument is then saved unattached.
   */
  const resolveInstrumentContext = (
    symbolRegion: BoundingBox,
  ): { contextType: 'equipment' | 'node'; contextId: string } | null => {
    if (!pnidState) return null;

    // Use explicit context override if set (from "Mark Instrument" button in dialog)
    const override = instrumentContextOverrideRef.current;
    if (override) return override;

    // The active item of the kind in hand
    if (currentAssetKind === 'equipment' && activeEquipmentId) {
      return { contextType: 'equipment', contextId: activeEquipmentId };
    }
    if (currentAssetKind === 'line' && activeNodeId) {
      return { contextType: 'node', contextId: activeNodeId };
    }

    // Fallback: auto-detect from the symbol's position
    const allEquipments = pnidState.equipments ?? [];
    const overlappingEquipment = allEquipments.find(
      (equipment) =>
        equipment.status !== 'ai_identified' && equipment.bbox && bboxesOverlap(symbolRegion, equipment.bbox),
    );
    if (overlappingEquipment) return { contextType: 'equipment', contextId: overlappingEquipment.id };

    const allNodes = pnidState.nodes ?? [];
    const crossedNode = allNodes.find(
      (node) =>
        node.status !== 'ai_identified' &&
        node.polyline &&
        node.polyline.length >= 4 &&
        polylineIntersectsBbox(node.polyline, symbolRegion),
    );
    if (crossedNode) return { contextType: 'node', contextId: crossedNode.id };

    const overlappingDuct = allNodes.find(
      (node) =>
        node.status !== 'ai_identified' && node.is_duct && node.duct_bbox && bboxesOverlap(node.duct_bbox, symbolRegion),
    );
    if (overlappingDuct) return { contextType: 'node', contextId: overlappingDuct.id };

    return null;
  };

  /**
   * Adds a region to an instrument draft. The first region of a new instrument also
   * fixes its parent, so Done saves the parent in hand when the symbol was drawn.
   */
  const appendInstrumentRegion = (draft: BoxDraft, bbox: BoundingBox): BoxDraft => {
    const next = appendRegion(draft, bbox);
    if (draft.target || draft.regions.length > 0) return next;
    return { ...next, context: resolveInstrumentContext(bbox) };
  };

  /**
   * Commits a draft collected for equipment, a line, or a control loop. These stay
   * single-region, so the redraw is the same one-box update it has always been.
   */
  const commitSingleRegionRedraw = async (target: GeometryTarget, bbox: BoundingBox) => {
    if (!selectedDoc || !pnidState) return;
    try {
      pushPnidUndoSnapshot("Rebox annotation");
      if (target.kind === "equipment") {
        const updated = await pnidApi.updateEquipment(selectedDoc.id, target.id, {
          bbox,
          status: "in_progress",
        });
        setPnidState({
          ...pnidState,
          equipments: pnidState.equipments.map((e) => (e.id === updated.id ? updated : e)),
        });
      } else if (target.kind === "node") {
        const existingNode = pnidState.nodes.find((node) => node.id === target.id);
        if (diagramMode === 'pfd' && existingNode?.is_duct) {
          showAlert('P&ID-only geometry', 'Existing ducts remain visible in PFD mode, but duct authoring is available only in P&ID mode.', 'info');
          setBoxDraft(null);
          return;
        }
        const isDuct = Boolean(existingNode?.is_duct);
        const polyline = bbox.polygon || [];
        const updated = await pnidApi.updateNode(selectedDoc.id, target.id, {
          polyline: isDuct ? null : polyline,
          duct_bbox: isDuct ? bbox : null,
          status: "in_progress",
        });
        setPnidState({
          ...pnidState,
          nodes: pnidState.nodes.map((n) => (n.id === updated.id ? updated : n)),
        });
      } else if (target.kind === "controlLoop") {
        const updated = await pnidApi.updateControlLoop(selectedDoc.id, target.id, {
          bbox,
          status: "in_progress",
        });
        setPnidState({
          ...pnidState,
          control_loops: pnidState.control_loops.map((loop) => (loop.id === updated.id ? updated : loop)),
        });
      }
      setBoxDraft(null);
      showAlert("Marked", "Marked successfully.", "success");
    } catch (error) {
      console.error(error);
      showAlert("Mark failed", "Could not mark item.", "error");
    }
  };

  /**
   * Commits a draft collected for an instrument: one write and one identify, fired
   * together and only here. A draft the SME abandons therefore leaves no row behind,
   * and a failure keeps the regions so the next Done can retry them.
   */
  const commitInstrumentRegions = async (draft: BoxDraft) => {
    if (!selectedDoc?.id || !pnidState) return;
    const target = draft.target;
    // "Mark Instrument" in the equipment or line form sets the override, and the new
    // instrument opens on that form; a redraw keeps the place its editor had.
    const nested = target
      ? Boolean(instrumentEditorState?.nested)
      : instrumentContextOverrideRef.current !== null;

    const existing = target ? pnidState.instruments.find((inst) => inst.id === target.id) : undefined;
    if (target && !existing) {
      showAlert("Mark failed", "Could not find the instrument to mark.", "error");
      return;
    }

    // A redraw arms with the marking's own regions already in the draft, so what is
    // committed is what the SME has been looking at and popping from. Nothing is
    // replaced behind their back, and the one destructive move — popping the final
    // region — asks on the spot.
    const existingRegions = existing ? instrumentRegions(existing) : [];
    const regions = committedRegions(draft, existingRegions);
    // The marking saves boxes only; which click target a region came from goes to identify alone.
    const bboxes = regions.map((region) => region.box);

    // Only a brand-new instrument takes the parent its draft resolved; a redraw already hangs off one.
    let writeInstrument: () => Promise<PnidInstrument>;
    if (existing) {
      writeInstrument = () =>
        instrumentApi.updateInstrument(selectedDoc.id, existing.id, { bboxes, status: 'in_progress' });
    } else {
      // DECISION(2026-09-07, Abhishek): An instrument drawn with no resolvable parent is saved with a null context instead of being rejected.
      // Some P&IDs contain no equipment and no lines, so no parent can exist. There is no way to distinguish a deliberate orphan from one that lost its parent, and both are context_type: null.
      const context = draft.context ?? null;
      writeInstrument = () =>
        instrumentApi.createInstrument(selectedDoc.id, {
          context_type: context?.contextType ?? null,
          context_id: context?.contextId ?? null,
          bboxes,
          status: 'in_progress',
        });
    }

    // Identify runs alongside the write so the editor waits on whichever is slower
    // rather than on the two in sequence. A failure here costs the SME a pre-fill,
    // never the instrument, so it resolves to null instead of rejecting.
    setIdentifyingBboxes(draft.regions.map((region) => region.box));
    const identified = assetApi
      .identify(selectedDoc.id, { regions: regions.map(identifyRegion) })
      .catch((err) => {
        console.warn('Identify failed; the instrument editor opens empty:', err);
        return null;
      })
      .finally(() => setIdentifyingBboxes([]));

    try {
      if (existing) pushPnidUndoSnapshot(target?.op === 'append' ? "Add instrument region" : "Rebox annotation");
      const saved = await writeInstrument();
      instrumentContextOverrideRef.current = null;
      // Drawing a new instrument re-arms, so an SME marking a sheet draws one after
      // another without returning to the toolbar between them. A redraw or an added
      // region is finished work for one instrument, so it disarms.
      // DECISION(2026-10-26, abhishek): A draft the kind pill started does not re-arm
      // after its save; a draft armed from a draw button does.
      // The pill is the default way in, so re-arming after it would leave the next drag
      // marking an instrument and the pill would never show again.
      setBoxDraft(target || draft.fromKindPill ? null : armDraft('instrument'));
      await refreshPnidState(selectedDoc.id);

      // A redraw re-focuses the wizard on the instrument's owner, the way Rebox has.
      if (target?.kind === 'equipmentInstrument') setActiveEquipmentId(saved.context_id);
      else if (target?.kind === 'nodeInstrument') setActiveNodeId(saved.context_id);

      const { instrument, validation } = await instrumentApi.getInstrument(selectedDoc.id, saved.id);
      // The kind decides which dialog the answer belongs in. This draw path opens the
      // instrument editor, so any other kind is a suggestion it cannot show.
      const suggestion = await identified;
      if (suggestion && suggestion.kind !== 'instrument') {
        console.warn(`Identify answered with kind "${suggestion.kind}"; the instrument editor opens empty.`);
      }
      setSelectedInstrumentId(saved.id);
      if (!nested) setMarkDialog((prev) => ({ ...prev, isOpen: false }));
      setInstrumentEditorState({
        instrument,
        findings: validation.findings,
        nested,
        identifyPrefill: suggestion?.kind === 'instrument' ? suggestion.fields : {},
      });
    } catch (err) {
      console.error('Failed to save instrument regions:', err);
      showAlert(
        existing ? 'Mark failed' : 'Create failed',
        existing ? 'Could not mark item.' : 'Could not create instrument.',
        'error',
      );
    }
  };

  /** Done: the collected regions become one element. Fires once, at the end of the draw. */
  const handleFinishRegions = async () => {
    const draft = boxDraft;
    if (!draft || !canFinish(draft)) return;
    const target = draft.target;
    if (target && !marksAnInstrument(draft)) {
      await commitSingleRegionRedraw(target, draft.regions[0].box);
      return;
    }
    await commitInstrumentRegions(draft);
  };

  /**
   * Escape with regions collected: back out the last one and stay armed.
   *
   * A redraw seeds the draft with the marking's saved regions, so Escape pops those
   * as well — one list, popped from the end. Popping the last region of an existing
   * marking would leave it with no geometry, which is a delete, so it asks first.
   */
  const handleRemoveLastRegion = () => {
    const draft = boxDraft;
    if (!draft) return;
    const target = draft.target;
    if (target && draft.regions.length === 1) {
      setConfirmDialog({
        isOpen: true,
        title: "Delete this marking?",
        message: "That is its only region. Removing it deletes the marking from the sheet.",
        type: "danger",
        confirmText: "Delete marking",
        onConfirm: () => {
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
          setBoxDraft(null);
          instrumentContextOverrideRef.current = null;
          void handleInstrumentEditorDelete(target.id);
        },
      });
      return;
    }
    setBoxDraft(removeLastRegion(draft));
  };

  /** Escape on an empty draft: leave the collect mode entirely. */
  const handleExitCollect = () => {
    setBoxDraft(null);
    instrumentContextOverrideRef.current = null;
  };

  /** A click on a text target while a dock field has focus writes the target's text into it. */
  const handleCopyClickTarget = (clickTarget: ClickTarget) => {
    if (!copyField || !clickTarget.text) return;
    if (copyIntoField(copyField, clickTarget.text)) trackAction('click_target_copied', { kind: clickTarget.kind });
  };

  /** A click on a click target while collecting adds that target's box as the next region. */
  const handleClickTarget = (clickTarget: ClickTarget) => {
    if (!boxDraft) return;
    trackAction('click_target_marked', { kind: clickTarget.kind });
    setBoxDraft(appendClickTargetRegion(boxDraft, clickTarget));
  };

  /**
   * Whether a draw button reads pressed. Its tool is in use while it is armed, and the
   * instrument tool also while any instrument draft is on the canvas, the kind pill's
   * included, which arms nothing so that its save does not re-arm.
   */
  const isDrawToolPressed = (tool: ArmedTool) =>
    armedTool === tool || (tool === 'instrument' && boxDraft?.kind === 'instrument');

  /** Arms equipment or instrument drawing, or lets go of the tool when its button reads pressed. */
  const toggleBoxTool = (pressedTool: 'equipment' | 'instrument') => {
    setSelectedInstrumentId(null);
    setCanvasFocusRequest(null);
    if (isDrawToolPressed(pressedTool)) {
      setArmedTool(null);
      handleExitCollect();
      return;
    }
    if (pressedTool === 'instrument') {
      armInstrumentDraw();
      return;
    }
    handleExitCollect();
    setArmedTool('equipment');
    setActiveEquipmentId(null);
  };

  /** Starts a new line, with no line targeted. */
  const startNewLine = () => {
    setActiveNodeId(null);
    setCanvasFocusRequest(null);
    // A new line starts from no point: a point placed for the line targeted before, and
    // the line it bound to, go. A finished line waiting in its form stays.
    if (!drawnLineOpen) setLineDrawTarget(null);
    setCanvasClearSignal((prev) => prev + 1);
  };

  /** Arms the pressed tool, or lets go of it when it is the armed one. */
  const toggleDrawTool = (pressedTool: ArmedTool) => {
    // DECISION(2026-10-30, Abhishek): arming a draw tool closes the open editor dock through Escape's path.
    // A drawing tool and a form waiting on a click cannot both own the next press, and the
    // dock's Escape asks before it discards an edited form. Letting go of a tool keeps the dock.
    if (!isDrawToolPressed(pressedTool) && editorDockOpen) escapeIntoOpenEditorDock();
    // Any press that leaves Duct returns the shape to the pipeline every other tool draws.
    if (armedTool === 'duct') setNodeDrawingShape('pipeline');
    if (pressedTool === 'equipment' || pressedTool === 'instrument') {
      toggleBoxTool(pressedTool);
      return;
    }
    if (armedTool === pressedTool) {
      // Letting go of Line or Duct leaves line drawing, as Escape with no vertex does:
      // the vertices in flight go and the canvas returns to rest.
      setArmedTool(null);
      setCanvasClearSignal((prev) => prev + 1);
      return;
    }
    startNewLine();
    setNodeDrawingShape(pressedTool === 'duct' ? 'duct' : 'pipeline');
    setArmedTool(pressedTool);
  };

  /**
   * A toolbar draw button. A press on a button that does not read pressed, with a draft
   * in flight, asks the discard-draft confirm first and arms the tool once the SME
   * confirms. The tool stays armed until a save, a cancel, Escape with nothing in flight,
   * or a second press.
   */
  const pressDrawButton = (pressedTool: ArmedTool) => {
    if (!isDrawToolPressed(pressedTool) && hasUnsavedDraft()) {
      setDiscardDraftConfirm({ isOpen: true, armOnConfirm: pressedTool });
      return;
    }
    toggleDrawTool(pressedTool);
  };

  /**
   * The equipment form on a drawn box. An active AI-identified equipment, while
   * equipment is the active kind, takes the box as its marking; otherwise the
   * form starts a new equipment.
   */
  const openEquipmentDrawDialog = (bbox: BoundingBox, anchor: { x: number; y: number }) => {
    const selectedEq = currentAssetKind === 'equipment'
      ? pnidState?.equipments.find((e) => e.id === activeEquipmentId)
      : undefined;
    if (selectedEq && selectedEq.status === "ai_identified") {
      openMarkDialog("equipment", bbox, anchor, {
        equipment_no: selectedEq.equipment_no,
        equipment_type: selectedEq.equipment_type,
        description: selectedEq.description,
        pnid_no: selectedEq.pnid_no ?? pnidState?.pnid_no ?? "",
        details: selectedEq.details,
        equipment_data: selectedEq.equipment_data,
        ai_instruments: selectedEq.ai_instruments,
        connected_equipments: selectedEq.connected_equipments ?? [],
        // The stored owner rides along because the save always states the
        // owner. Without it a foreign owner would read as this drawing's own,
        // and the save would hand the asset back to this drawing.
        owner_domain_id: selectedEq.owner_domain_id,
        _markExistingId: selectedEq.id,
      });
    } else {
      openMarkDialog("equipment", bbox, anchor, {
        pnid_no: pnidState?.pnid_no ?? "",
      });
    }
  };

  /** The kind pill's answer for a box drawn with no tool armed. */
  const handlePendingBoxKind = (kind: PendingBoxKind, bbox: BoundingBox, anchor: { x: number; y: number }) => {
    trackAction('item_drawn_on_canvas');
    if (kind === 'equipment') {
      openEquipmentDrawDialog(bbox, anchor);
      return;
    }
    instrumentContextOverrideRef.current = null;
    setBoxDraft({ ...appendInstrumentRegion(armDraft('instrument'), bbox), fromKindPill: true });
  };

  /**
   * The line form on the finished path or duct. A stored line it marks saves through the
   * mark dialog's mark-existing route, and a new line through its create route.
   */
  const openDrawnLineForm = (
    bbox: BoundingBox,
    anchor: { x: number; y: number },
    boundNode: PnidNode | null,
    values: Record<string, any>,
    boundCount: number
  ) => {
    const nodes = pnidState?.nodes ?? [];
    const nextNodeNo = nodes.reduce((max, n) => Math.max(max, n.node_no), 0) + 1;
    setMarkDialog({
      isOpen: true,
      kind: "node",
      bbox,
      anchor,
      editTarget: null,
      initialValues: boundNode
        ? { ...boundNode, ...values, _markExistingId: boundNode.id, _targetNodeId: boundNode.id }
        : { ...values, node_no: nextNodeNo },
      drawnLine: { boundCount },
    });
  };

  /**
   * A finished box or polygon: a region of the instrument draft while one collects, the duct
   * form when a line tool draws with the duct toggle on (the canvas then keeps the box mode),
   * else the equipment form. With nothing armed the box asks its kind on the pill instead.
   */
  const handleBoxDrawn = (bbox: BoundingBox, anchor: { x: number; y: number }) => {
    if (!selectedDoc) return;
    trackAction('item_drawn_on_canvas');

    // Collecting: a drag adds a region to the draft and does nothing else. The write
    // waits for Done, so an abandoned drawing leaves nothing behind.
    if (boxDraft) {
      setBoxDraft(appendInstrumentRegion(boxDraft, bbox));
      return;
    }

    const drawsDuct = diagramMode === 'pid' && nodeDrawingShape === 'duct' && (armedTool === 'duct' || isNodeMarkingMode);
    if (drawsDuct) {
      const nodes = pnidState?.nodes ?? [];
      const selectedNode = activeNodeId
        ? nodes.find((node) => node.id === activeNodeId) ?? null
        : null;
      const bindToSelectedNode = Boolean(
        selectedNode && (isNodeMarkingMode || selectedNode.status === 'ai_identified')
      );
      closeInstrumentEditor();
      setLineSegmentReviewPopover(null);
      openDrawnLineForm(bbox, anchor, bindToSelectedNode ? selectedNode : null, { ...getLineFormValues(selectedNode), is_duct: true }, 0);
      return;
    }

    openEquipmentDrawDialog(bbox, anchor);
  };

  /** A finished path opens the line form, whatever the rail tab. */
  const handleLineDrawn = (bbox: BoundingBox, anchor: { x: number; y: number }, lineEnds?: LineEnds) => {
    if (!selectedDoc) return;
    trackAction('item_drawn_on_canvas');

    const nodes = pnidState?.nodes ?? [];
    // The ends the clicks bound spell the refs. A free end keeps what the line stored, if anything (D2).
    // A binding to the line under edit is refused: a line never names itself as its own end.
    const lineUnderEditId = lineDrawTarget?.nodeId ?? (isNodeMarkingMode ? activeNodeId : null);
    const endRef = (binding: VertexBinding | null) =>
      binding?.kind === 'node' && binding.id === lineUnderEditId ? null : refForBinding(binding);
    const boundSourceRef = endRef(lineEnds?.source ?? null);
    const boundDestRef = endRef(lineEnds?.dest ?? null);
    const boundNode = lineUnderEditId ? nodes.find((n) => n.id === lineUnderEditId) ?? null : null;
    // A path drawn again while its form is open starts from the form's seed, so an end the
    // new path leaves free keeps what the last one bound.
    const seededValues: Record<string, any> = drawnLineOpen ? markDialog.initialValues ?? {} : getLineFormValues(boundNode);

    setLineSegmentReviewPopover(null);
    openDrawnLineForm(bbox, anchor, boundNode, {
      ...seededValues,
      is_duct: false,
      source_ref: boundSourceRef ?? seededValues.source_ref ?? "",
      source_id: boundSourceRef ? lineEnds?.source?.id ?? null : seededValues.source_id ?? null,
      dest_ref: boundDestRef ?? seededValues.dest_ref ?? "",
      dest_id: boundDestRef ? lineEnds?.dest?.id ?? null : seededValues.dest_id ?? null,
      // A bound end replaces the stored ref, and the sheet and drain type that went
      // with it: the form prefills the sheet from the new end, and no bound target is a drain.
      source_pnid: boundSourceRef ? "" : seededValues.source_pnid,
      dest_pnid: boundDestRef ? "" : seededValues.dest_pnid,
      drain_type: boundDestRef ? null : seededValues.drain_type ?? boundNode?.drain_type ?? null,
    }, lineEnds?.boundCount ?? 0);
  };

  /** The saved P&ID equipment the mark dialog is editing, if any.
   *  A compound tag can only be split on an equipment that already exists. */
  const compoundSplitTargetId =
    diagramMode === "pfd" || markDialog.kind !== "equipment"
      ? null
      : markDialog.editTarget?.kind === "equipment"
        ? markDialog.editTarget.id
        : (markDialog.initialValues?._markExistingId as string | undefined) ?? null;

  /** The type the split's new assets would inherit, read from the same stored
   *  record the split sends it from. */
  const compoundSplitInheritedType =
    (compoundSplitTargetId
      ? pnidState?.equipments.find((e) => e.id === compoundSplitTargetId)?.equipment_type
      : null) ?? null;

  /**
   * Splits a compound-tagged equipment on the open drawing into one asset per
   * part, and brings the drawing back in step with what the run wrote.
   */
  const splitCompoundEquipmentOnDrawing = useCallback(
    async (equipmentId: string, parts: CompoundSplitPart[]) => {
      if (!selectedDoc) {
        throw new Error("This equipment is no longer open. Reopen it and split again.");
      }
      const docId = selectedDoc.id;
      const savedTags = await splitCompoundEquipment({
        docId,
        equipmentId,
        parts,
        refresh: async () => {
          await refreshPnidState(docId);
        },
      });
      // The rename resets the marks the old tag carried and copies the new tag's
      // family onto the row, so the register no longer says what it said before
      // the split — the same reason a row rename invalidates it.
      invalidateRegisterContext(docId);
      return savedTags;
    },
    [refreshPnidState, selectedDoc]
  );

  /** The edit panel's split: the same run, followed by the panel's own close and
   *  the alert naming what is now on the drawing. */
  const handleSplitCompoundEquipment = async (parts: CompoundSplitPart[]) => {
    if (!compoundSplitTargetId) {
      throw new Error("This equipment is no longer open. Reopen it and split again.");
    }
    const savedTags = await splitCompoundEquipmentOnDrawing(compoundSplitTargetId, parts);
    closeMarkDialog();
    showAlert(
      "Compound tag split",
      `${savedTags.join(", ")} are now separate assets on this drawing.`,
      "success"
    );
  };

  /** What a mark save writes to: the open dialog's target, or one a caller passes. */
  type MarkTarget = Pick<typeof markDialog, "kind" | "bbox" | "editTarget">;

  /**
   * A path drawn after Mark (or a redraw) on a stored line saves that line as in progress, and
   * says so; the SME edits its details from the line after.
   */
  const lineMarkedStatus = (): { status?: MarkingStatus } => (isNodeMarkingMode ? { status: "in_progress" } : {});
  /** The body the P&ID form's Save sends for a stored line it marks with the path just drawn. */
  const markExistingNodeBody = (values: Parameters<typeof buildNodeUpdatePayload>[0], target: MarkTarget) => {
    const polyline = target.bbox?.polygon || [];
    const isDuct = Boolean(values.is_duct);
    return {
      ...buildNodeUpdatePayload(values),
      polyline: isDuct ? null : polyline,
      duct_bbox: isDuct ? target.bbox : null,
      ...lineMarkedStatus(),
    };
  };
  const announceLineMarked = (node: PnidNode, isDuct: boolean) => showAlert(
    "Line Marked",
    `Node #${node.node_no} ${isDuct ? 'duct box' : 'path'} saved. Click on the node or use "Edit" to update details.`,
    "success"
  );

  const handleMarkDialogSubmit = async (values: Record<string, any>, target: MarkTarget = markDialog) => {
    if (!selectedDoc) return;
    if (!pnidState) return;
    // Only require bbox for new items (not edits or marking existing AI items)
    const isEdit = Boolean(target.editTarget?.id);
    const isMarkingExisting = Boolean(values._markExistingId);
    if (!target.bbox && !isEdit && !isMarkingExisting) return;

    let savedItemId: string | null = null;
    const savedParentId: string | null = null;

    try {
      if (target.kind === "equipment") {
        const markExistingId = values._markExistingId;

        const connectionsChanged = Array.isArray(values.connected_equipments);
        const pfdLinksChanged = Array.isArray(values.linked_pfds);
        if (target.editTarget?.id) {
          const updated = await pnidApi.updateEquipment(
            selectedDoc.id,
            target.editTarget.id,
            buildEquipmentUpdatePayload(values, connectionsChanged, pfdLinksChanged)
          );
          invalidateAgentReviewQueue();
          let next: PnidWizardState;
          if (connectionsChanged || pfdLinksChanged) {
            // Peers/cross-document targets were mirrored server-side — refetch current doc.
            next = takeDrawingResponse(await pnidApi.get(selectedDoc.id));
          } else {
            next = {
              ...pnidState,
              equipments: pnidState.equipments.map((e) =>
                e.id === updated.id ? updated : e
              ),
            };
          }
          setPnidState(next);
          setActiveEquipmentId(updated.id);
          savedItemId = updated.id;
        } else if (markExistingId) {
          const updated = await pnidApi.updateEquipment(
            selectedDoc.id,
            markExistingId,
            {
              ...buildEquipmentUpdatePayload(values, connectionsChanged, pfdLinksChanged),
              bbox: target.bbox,
            }
          );
          invalidateAgentReviewQueue();
          let next: PnidWizardState;
          if (connectionsChanged || pfdLinksChanged) {
            next = takeDrawingResponse(await pnidApi.get(selectedDoc.id));
          } else {
            next = {
              ...pnidState,
              equipments: pnidState.equipments.map((e) =>
                e.id === updated.id ? updated : e
              ),
            };
          }
          setPnidState(next);
          setActiveEquipmentId(updated.id);
          savedItemId = updated.id;
          const nextUnmarked = next.equipments.find(
            (e) => e.status === "ai_identified"
          );
          if (nextUnmarked) {
            setActiveEquipmentId(nextUnmarked.id);
          }
        } else {
          const created = await pnidApi.createEquipment(selectedDoc.id, {
            equipment_no: values.equipment_no,
            equipment_type: values.equipment_type,
            subtype: values.subtype ?? null,
            description: values.description,
            pnid_no: values.pnid_no ?? null,
            bbox: target.bbox,
            painting_spec: values.painting_spec?.trim() || null,
            notes: values.notes ?? null,
            linked_pfds: pfdLinksChanged ? values.linked_pfds : null,
            ...(values.is_primary_source !== undefined
              ? { is_primary_source: values.is_primary_source }
              : {}),
            owner_domain_id: values.owner_domain_id ?? '',
          });
          const next = {
            ...pnidState,
            equipments: [...pnidState.equipments, created],
          };
          setPnidState(next);
          setActiveEquipmentId(created.id);
          savedItemId = created.id;
        }
        if (values.is_primary_source !== undefined) {
          // The saved mark is part of the register's answer for this drawing, so
          // the edit panel must re-read it: its locks were computed before the
          // reviewer marked it.
          invalidateRegisterContext(selectedDoc.id);
        }
      }

      if (target.kind === "node") {
        const markExistingId = values._markExistingId;
        let savedLine: PnidNode;

        if (target.editTarget?.id) {
          const updated = await pnidApi.updateNode(
            selectedDoc.id,
            target.editTarget.id,
            buildNodeUpdatePayload(values)
          );
          invalidateAgentReviewQueue();
          const next = {
            ...pnidState,
            nodes: pnidState.nodes.map((n) =>
              n.id === updated.id ? updated : n
            ),
          };
          setPnidState(next);
          setActiveNodeId(updated.id);
          savedItemId = updated.id;
          savedLine = updated;
        } else if (markExistingId) {
          const polyline = target.bbox?.polygon || [];
          const isDuct = Boolean(values.is_duct);
          const updated = await pnidApi.updateNode(
            selectedDoc.id,
            markExistingId,
            markExistingNodeBody(values, target)
          );
          invalidateAgentReviewQueue();
          const next = {
            ...pnidState,
            nodes: pnidState.nodes.map((n) =>
              n.id === updated.id ? updated : n
            ),
          };
          setPnidState(next);
          setActiveNodeId(updated.id);
          savedItemId = updated.id;
          savedLine = updated;
          if (!isDuct && polyline.length >= 4) {
            void runNodePointClassification(updated.id, polyline);
          }
          const nextUnmarked = next.nodes.find(
            (n) => n.status === "ai_identified"
          );
          if (isNodeMarkingMode) {
            announceLineMarked(updated, isDuct);
          } else if (nextUnmarked) {
            setActiveNodeId(nextUnmarked.id);
          }
        } else {
          const polyline = target.bbox?.polygon || [];
          const isDuct = Boolean(values.is_duct);
          const created = await pnidApi.createNode(selectedDoc.id, {
            node_no: values.node_no ?? null,
            source_ref: values.source_ref,
            source_pnid: values.source_pnid,
            source_id: values.source_id ?? null,
            dest_ref: values.dest_ref,
            dest_pnid: values.dest_pnid,
            dest_id: values.dest_id ?? null,
            ...getNodeLineTypePayload(values),
            note: values.note ?? null,
            painting_spec: values.painting_spec?.trim() || null,
            is_duct: isDuct,
            notes: values.notes ?? null,
            ...getNodeLinePropertyPayload(values),
            drain_type: values.drain_type ?? null,
            polyline: isDuct ? null : polyline,
            duct_bbox: isDuct ? target.bbox : null,
          });
          const next = { ...pnidState, nodes: [...pnidState.nodes, created] };
          setPnidState(next);
          setActiveNodeId(created.id);
          savedItemId = created.id;
          savedLine = created;
          if (!isDuct && polyline.length >= 4) {
            void runNodePointClassification(created.id, polyline);
          }
        }
        await attachEndInstrumentsToLine(savedLine, selectedDoc, pnidState, showAlert, refreshPnidState);
        // Clear the canvas path after successfully saving a node
        setCanvasClearSignal((prev) => prev + 1);
        setIsNodeMarkingMode(false);
        setNodeDrawingShape('pipeline');
        setLineDrawTarget(null);
      }


      trackAction(isEdit ? 'item_edited' : 'item_marked', {
        item_type: target.kind,
        item_id: savedItemId ?? target.editTarget?.id ?? null,
        parent_id: savedParentId,
        field_change_count: values._field_change_count ?? 0,
        fields_changed_count: (values._fields_changed as string[] | undefined)?.length ?? 0,
      });

      const savedTargetId = savedItemId ?? target.editTarget?.id ?? null;
      if (savedTargetId) {
        refreshViewsAfterObjectSave();
        if (target.kind === 'equipment' || target.kind === 'node') {
          noteAgentReviewSave(target.kind, savedTargetId);
        }
      }

      closeMarkDialog();
    } catch (error) {
      console.error(error);
      showAlert("Save failed", "Could not save this mark.", "error");
    }
  };

  /**
   * The open P&ID line or equipment form's draft call on a stored element: the body
   * handleMarkDialogSubmit would send and the validate-draft POST. Undefined for a new element,
   * which has no id to validate, during a version preview, which shows no findings, and in a
   * PFD form.
   */
  const markDialogTargetId: string | undefined = markDialog.editTarget?.id ?? markDialog.initialValues?._markExistingId;
  const markDialogDraftRequestFor = (() => {
    if (previewingVersion !== null || diagramMode !== "pid" || !markDialogTargetId) return undefined;
    const docId = selectedDoc.id;
    const target: MarkTarget = markDialog;
    const marksExisting = !target.editTarget?.id;
    if (target.kind === "equipment") {
      return (values: Parameters<typeof handleMarkDialogSubmit>[0]): DraftRequest => {
        const body = {
          ...buildEquipmentUpdatePayload(values, Array.isArray(values.connected_equipments), Array.isArray(values.linked_pfds)),
          ...(marksExisting ? { bbox: target.bbox } : {}),
        };
        return { body, send: () => assetApi.validateDraftEquipment(docId, markDialogTargetId, body) };
      };
    }
    return (values: Parameters<typeof handleMarkDialogSubmit>[0]): DraftRequest => {
      const body = marksExisting ? markExistingNodeBody(values, target) : buildNodeUpdatePayload(values);
      return { body, send: () => assetApi.validateDraftNode(docId, markDialogTargetId, body) };
    };
  })();

  const handleMarkDialogSubmitPfd = async (values: Record<string, any>, target: MarkTarget = markDialog) => {
    if (!selectedDoc || !pnidState) return;

    const isEdit = Boolean(target.editTarget?.id);
    const isMarkingExisting = Boolean(values._markExistingId);
    if (!target.bbox && !isEdit && !isMarkingExisting) return;

    let savedItemId: string | null = null;

    try {
      if (target.kind === "equipment") {
        const targetId = target.editTarget?.id ?? values._markExistingId ?? null;
        const existing = targetId ? pnidState.equipments.find((e) => e.id === targetId) : null;
        const payload = buildPfdPayload(values, pfdEquipmentSchema, PFD_EQUIPMENT_FIELD_KEYS, {
          details: existing?.details ?? {},
        });
        // Connected equipment is mirrored to peers server-side; refetch when it changes.
        const connectionsChanged = Array.isArray(values.connected_equipments);
        const pnidLinksChanged = Array.isArray(values.linked_pnids);

        if (targetId) {
          const updated = await pnidApi.updateEquipment(selectedDoc.id, targetId, {
            ...payload,
            painting_spec: values.painting_spec?.trim() || null,
            ...(values._markExistingId ? { bbox: target.bbox } : {}),
          });
          const next = connectionsChanged || pnidLinksChanged
            ? takeDrawingResponse(await pnidApi.get(selectedDoc.id))
            : {
                ...pnidState,
                equipments: pnidState.equipments.map((e) => e.id === updated.id ? updated : e),
              };
          setPnidState(next);
          setActiveEquipmentId(updated.id);
          savedItemId = updated.id;
        } else {
          const created = await pnidApi.createEquipment(selectedDoc.id, {
            equipment_no: payload.equipment_no,
            equipment_type: payload.equipment_type,
            description: payload.description,
            pnid_no: pnidState.pnid_no ?? null,
            bbox: target.bbox,
            painting_spec: values.painting_spec?.trim() || null,
            details: payload.details ?? {},
            connected_equipments: payload.connected_equipments ?? null,
            linked_pnids: payload.linked_pnids ?? null,
            notes: payload.notes ?? null,
          });
          const next = connectionsChanged || pnidLinksChanged
            ? takeDrawingResponse(await pnidApi.get(selectedDoc.id))
            : { ...pnidState, equipments: [...pnidState.equipments, created] };
          setPnidState(next);
          setActiveEquipmentId(created.id);
          savedItemId = created.id;
        }
      }

      if (target.kind === "node") {
        const targetId = target.editTarget?.id ?? values._markExistingId ?? null;
        const existing = targetId ? pnidState.nodes.find((n) => n.id === targetId) : null;
        const payload = buildPfdPayload(values, pfdNodeSchema, PFD_NODE_FIELD_KEYS, {
          line_spec: existing?.line_spec ?? {},
        });
        // The type the caller states: the line form's Line type, or the stream dialog's
        // Signal (submitPfdStreamDialog). A blank Stream No on an instrument line is
        // generated as <source>_<dest>_<signal>, as in P&ID.
        const lineTypePayload = getNodeLineTypePayload(values);

        if (targetId) {
          const polyline = values._markExistingId ? target.bbox?.polygon || [] : undefined;
          const isDuct = Boolean(existing?.is_duct ?? values.is_duct);
          const updated = await pnidApi.updateNode(selectedDoc.id, targetId, {
            ...payload,
            ...lineTypePayload,
            // drain_type and the two end ids are set as a side-effect of the end picks and
            // are not schema fields, so they are not in payload — pass them explicitly.
            drain_type: values.drain_type ?? null,
            source_id: values.source_id ?? null,
            dest_id: values.dest_id ?? null,
            painting_spec: values.painting_spec?.trim() || null,
            is_duct: isDuct,
            ...(polyline ? {
              polyline: isDuct ? null : polyline,
              duct_bbox: isDuct ? target.bbox : null,
              ...lineMarkedStatus(),
            } : {}),
          });
          const next = {
            ...pnidState,
            nodes: pnidState.nodes.map((n) => n.id === updated.id ? updated : n),
          };
          setPnidState(next);
          setActiveNodeId(updated.id);
          savedItemId = updated.id;
          if (!isDuct && polyline && polyline.length >= 4) {
            void runNodePointClassification(updated.id, polyline);
          }
          if (polyline && isNodeMarkingMode) announceLineMarked(updated, isDuct);
        } else {
          const polyline = target.bbox?.polygon || [];
          // RAV-844 duct authoring is intentionally P&ID-only.
          const isDuct = false;
          const created = await pnidApi.createNode(selectedDoc.id, {
            line_no: lineTypePayload.line_no,
            source_ref: payload.source_ref,
            source_pnid: payload.source_pnid,
            source_id: values.source_id ?? null,
            dest_ref: payload.dest_ref,
            dest_pnid: payload.dest_pnid,
            dest_id: values.dest_id ?? null,
            line_type: lineTypePayload.line_type,
            instrument_line_subtype: lineTypePayload.instrument_line_subtype,
            drain_type: values.drain_type ?? null,
            painting_spec: values.painting_spec?.trim() || null,
            is_duct: isDuct,
            line_spec: payload.line_spec ?? {},
            linked_pfd_streams: payload.linked_pfd_streams ?? null,
            polyline: isDuct ? null : polyline,
            duct_bbox: isDuct ? target.bbox : null,
          });
          setPnidState({ ...pnidState, nodes: [...pnidState.nodes, created] });
          setActiveNodeId(created.id);
          savedItemId = created.id;
          if (!isDuct && polyline.length >= 4) {
            void runNodePointClassification(created.id, polyline);
          }
        }
        setCanvasClearSignal((prev) => prev + 1);
        setIsNodeMarkingMode(false);
        setLineDrawTarget(null);
      }


      trackAction(isEdit ? 'item_edited' : 'item_marked', {
        item_type: target.kind,
        item_id: savedItemId ?? target.editTarget?.id ?? null,
        field_change_count: values._field_change_count ?? 0,
        fields_changed_count: (values._fields_changed as string[] | undefined)?.length ?? 0,
      });

      // The same completion as a P&ID save: the views a save makes stale, and the
      // Needs review advance when this save reviewed a proposal.
      const savedTargetId = savedItemId ?? target.editTarget?.id ?? null;
      if (savedTargetId && (target.kind === 'equipment' || target.kind === 'node')) {
        refreshViewsAfterObjectSave();
        noteAgentReviewSave(target.kind, savedTargetId);
      }

      closeMarkDialog();
    } catch (error) {
      console.error(error);
      showAlert("Save failed", "Could not save this PFD mark.", "error");
    }
  };


  // The PFD stream dialog has no Line type field: Signal === P&ID, so a selected
  // instrument_line_subtype marks the stream an instrument line, and none a process line.
  const submitPfdStreamDialog = (values: Parameters<typeof handleMarkDialogSubmitPfd>[0]) => handleMarkDialogSubmitPfd(
    markDialog.kind === "node"
      ? { ...values, line_type: values.instrument_line_subtype ? "instrument" : "process" }
      : values,
  );

  /**
   * Writes a dragged rectangle into local state before the save answers. `regionIndex`
   * says which of an instrument's regions moved; every other kind is single-region and
   * ignores it.
   */
  const patchPersistedBbox = useCallback((
    target: PersistedBboxTarget,
    bbox: BoundingBox,
    regionIndex = 0,
  ) => {
    setPnidState((prev) => {
      if (!prev) return prev;
      if (target.kind === "equipment") {
        return {
          ...prev,
          equipments: prev.equipments.map((equipment) =>
            equipment.id === target.id ? { ...equipment, bbox } : equipment
          ),
        };
      }
      if (target.kind === "controlLoop") {
        return {
          ...prev,
          control_loops: prev.control_loops.map((loop) =>
            loop.id === target.id ? { ...loop, bbox } : loop
          ),
        };
      }
      if (target.kind === "node") {
        return {
          ...prev,
          nodes: prev.nodes.map((node) =>
            node.id === target.id ? { ...node, duct_bbox: bbox } : node
          ),
        };
      }
      return {
        ...prev,
        instruments: prev.instruments.map((instrument) =>
          instrument.id === target.id ? withRegionAt(instrument, regionIndex, bbox) : instrument
        ),
      };
    });
    setInstrumentEditorState((prev) =>
      prev?.instrument.id === target.id
        ? { ...prev, instrument: withRegionAt(prev.instrument, regionIndex, bbox) }
        : prev
    );
  }, []);

  const handleBeginPersistedBboxUpdate = useCallback((target: PersistedBboxTarget) => {
    const label =
      target.kind === "equipment"
        ? "Move or resize equipment annotation"
        : target.kind === "controlLoop"
          ? "Move or resize control-loop annotation"
          : target.kind === "node"
            ? "Move or resize duct annotation"
            : "Move or resize instrument annotation";
    pushPnidUndoSnapshot(label);
  }, [pushPnidUndoSnapshot]);

  const handlePersistedBboxUpdate = useCallback(async (
    target: PersistedBboxTarget,
    bbox: BoundingBox,
    options?: { commit?: boolean; regionIndex?: number },
  ) => {
    if (!selectedDoc?.id) return;
    const regionIndex = options?.regionIndex ?? 0;
    patchPersistedBbox(target, bbox, regionIndex);
    if (!options?.commit) return;

    try {
      if (target.kind === "equipment") {
        const updated = await pnidApi.updateEquipment(selectedDoc.id, target.id, { bbox });
        patchPersistedBbox(target, updated.bbox ?? bbox);
      } else if (target.kind === "controlLoop") {
        const updated = await pnidApi.updateControlLoop(selectedDoc.id, target.id, { bbox });
        patchPersistedBbox(target, updated.bbox ?? bbox);
      } else if (target.kind === "node") {
        const updated = await pnidApi.updateNode(selectedDoc.id, target.id, { duct_bbox: bbox });
        patchPersistedBbox(target, updated.duct_bbox ?? bbox);
      } else {
        // Only the dragged region moves; the instrument's other regions travel
        // unchanged, and the first of them stays the anchor the backend derives.
        const dragged = pnidState?.instruments.find((inst) => inst.id === target.id);
        const regions = dragged ? instrumentRegions(dragged) : [];
        const updated = await instrumentApi.updateInstrument(selectedDoc.id, target.id, {
          bboxes: regions.length > 0
            ? regions.map((region, index) => (index === regionIndex ? bbox : region))
            : [bbox],
        });
        patchPersistedBbox(target, instrumentRegions(updated)[regionIndex] ?? bbox, regionIndex);
        invalidatePublishableInstruments();
      }
    } catch (error) {
      console.error("Failed to update mark box:", error);
      try {
        await refreshPnidState(selectedDoc.id);
      } catch (refreshError) {
        console.error("Failed to refresh after mark box update error:", refreshError);
      }
      showAlert("Box update failed", "Could not save the adjusted markup box.", "error");
    }
  }, [invalidatePublishableInstruments, patchPersistedBbox, pnidState?.instruments, refreshPnidState, selectedDoc?.id]);


  const handleShiftWizardAnnotations = useCallback(async (
    delta: { dx: number; dy: number },
    options?: { lineSegmentScale?: { sx: number; sy: number } },
  ) => {
    if (!selectedDoc?.id) return;
    const { dx, dy } = delta;
    if (!Number.isFinite(dx) || !Number.isFinite(dy)) {
      showAlert("Move failed", "Could not save an invalid annotation offset.", "error");
      return;
    }
    if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) return;

    try {
      pushPnidUndoSnapshot("Move all annotations");
      const updated = await pnidApi.shiftAnnotations(selectedDoc.id, dx, dy, options);
      setPnidState(updated);
      showAlert(
        "Annotations moved",
        `Shifted all saved annotations by ${Math.round(dx)} px horizontally and ${Math.round(dy)} px vertically.`,
        "success",
      );
    } catch (error) {
      console.error("Failed to shift annotations:", error);
      try {
        const refreshed = await refreshPnidState(selectedDoc.id);
        setPnidState(refreshed);
      } catch (refreshError) {
        console.error("Failed to refresh after annotation shift error:", refreshError);
      }
      showAlert("Move failed", "Could not save the annotation offset.", "error");
      throw error;
    }
  }, [pushPnidUndoSnapshot, refreshPnidState, selectedDoc?.id]);

  // Check if a node is referenced by other entities (for deletion protection)
  const getNodeReferences = (nodeId: string) => {
    if (!pnidState) return { refs: [], canDelete: true };

    const node = pnidState.nodes.find((n) => n.id === nodeId);
    if (!node) return { refs: [], canDelete: true };

    const refs: string[] = [];
    const nodeNoRef = `Node #${node.node_no}`;

    // Other lines that name this one as an end: by its id where one is stored, else by
    // the "Node #N" string, which a changed node_no breaks (D8).
    const namesThisNode = (id: string | null | undefined, ref: string) =>
      id ? id === nodeId : ref === nodeNoRef;
    for (const n of pnidState.nodes) {
      if (n.id === nodeId) continue;
      if (namesThisNode(n.source_id, n.source_ref))
        refs.push(`Node #${n.node_no} (as source)`);
      if (namesThisNode(n.dest_id, n.dest_ref))
        refs.push(`Node #${n.node_no} (as destination)`);
      if (n.parent_node_id === nodeId)
        refs.push(`Node #${n.node_no} (as parent bifurcation)`);
    }

    // Check node instruments that branch to this node
    for (const ni of directNodeInstruments) {
      if (ni.branch_ref === String(node.node_no)) {
        const parentNode = pnidState.nodes.find((n) => n.id === ni.context_id);
        refs.push(
          `Instrument on Node #${parentNode?.node_no || "?"} (branch point)`
        );
      }
    }

    return { refs, canDelete: refs.length === 0 };
  };

  const executeDelete = async (target: { kind: WizardTargetKind; id: string }) => {
    if (!selectedDoc || !pnidState) return;
    const kind = target.kind;
    try {
      if (kind === "equipment") {
        pushPnidUndoSnapshot("Delete equipment annotation");
        await pnidApi.deleteEquipment(selectedDoc.id, target.id);
        invalidateAgentReviewQueue();
        const next = {
          ...pnidState,
          equipments: pnidState.equipments.filter((e) => e.id !== target.id),
          instruments: pnidState.instruments.filter(
            (i) => i.context_id !== target.id
          ),
        };
        applyPnidStateLocally(next, { clearSelection: false });
      } else if (kind === "equipmentInstrument") {
        pushPnidUndoSnapshot("Delete instrument annotation");
        await instrumentApi.deleteInstrument(selectedDoc.id, target.id);
        applyPnidStateLocally({
          ...pnidState,
          instruments: pnidState.instruments.filter(
            (i) => i.id !== target.id
          ),
        }, { clearSelection: false });
      } else if (kind === "node") {
        // Check for references before deleting
        const { refs, canDelete } = getNodeReferences(target.id);
        if (!canDelete) {
          showAlert(
            "Cannot Delete",
            `This node is referenced by:\n• ${refs.join(
              "\n• "
            )}\n\nRemove these references first.`,
            "error"
          );
          return;
        }
        pushPnidUndoSnapshot("Delete line annotation");
        await pnidApi.deleteNode(selectedDoc.id, target.id);
        invalidateAgentReviewQueue();
        const next = {
          ...pnidState,
          nodes: pnidState.nodes.filter((n) => n.id !== target.id),
          instruments: pnidState.instruments.filter(
            (i) => i.context_id !== target.id
          ),
        };
        applyPnidStateLocally(next, { clearSelection: false });
      } else if (kind === "nodeInstrument") {
        pushPnidUndoSnapshot("Delete instrument annotation");
        await instrumentApi.deleteInstrument(selectedDoc.id, target.id);
        applyPnidStateLocally({
          ...pnidState,
          instruments: pnidState.instruments.filter(
            (i) => i.id !== target.id
          ),
        }, { clearSelection: false });
      }
      showAlert("Deleted", "Annotation deleted. Press Cmd/Ctrl+Z to undo.", "success");
    } catch (error) {
      console.error(error);
      showAlert("Delete failed", "Could not delete item.", "error");
    }
  };

  const handleWizardDelete = (target: { kind: WizardTargetKind; id: string }) => {
    if (!pnidState) return;
    const kind = target.kind;

    // Build a descriptive name for the item being deleted
    let itemName = "this item";
    if (kind === "equipment") {
      const eq = pnidState.equipments.find((e) => e.id === target.id);
      itemName = eq ? `equipment "${eq.equipment_no}"` : "this equipment";
    } else if (kind === "equipmentInstrument") {
      const inst = equipmentInstruments.find(
        (i) => i.id === target.id
      );
      itemName = inst
        ? `instrument "${formatInstrumentIdentity(inst)}"`
        : "this instrument";
    } else if (kind === "node") {
      const node = pnidState.nodes.find((n) => n.id === target.id);
      itemName = node
        ? `line "${node.line_no || `Node #${node.node_no}`}"`
        : "this line";
    } else if (kind === "nodeInstrument") {
      const inst = nodeInstruments.find((i) => i.id === target.id);
      itemName = inst
        ? `instrument "${formatInstrumentIdentity(inst)}"`
        : "this instrument";
    }
    // executeDelete removes an equipment's or a line's instruments with it, so the confirm names them.
    if (kind === "equipment" || kind === "node") {
      const attachedCount = pnidState.instruments.filter((i) => i.context_id === target.id).length;
      if (attachedCount > 0) {
        itemName += ` and its ${attachedCount} attached instrument${attachedCount === 1 ? "" : "s"}`;
      }
    }

    setConfirmDialog({
      isOpen: true,
      title: "Confirm Delete",
      message: `Are you sure you want to delete ${itemName}? This can be undone with Cmd/Ctrl+Z.`,
      type: "danger",
      confirmText: "Delete",
      onConfirm: () => {
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
        executeDelete(target);
      },
    });
  };

  // One-click delete from the canvas "×" badge — no confirm dialog, undoable via Cmd/Ctrl+Z.
  const handleQuickDeleteWizardItem = (target: {
    kind: "equipmentInstrument" | "nodeInstrument";
    id: string;
  }) => {
    if (instrumentEditorState?.instrument.id === target.id) {
      setInstrumentEditorState(null);
    }
    setSelectedInstrumentId((prev) => (prev === target.id ? null : prev));
    setSelectedWizardItemKeys((prev) => {
      const key = wizardItemKey(target);
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
    void executeDelete(target);
  };

  const handleReorderNodeInstruments = async (
    nodeId: string,
    reorderedInstruments: PnidInstrument[]
  ) => {
    if (!pnidState || !selectedDoc) return;

    const previousState = pnidState;
    const orderedIds = reorderedInstruments.map((i) => i.id);
    const reorderedWithSeq = reorderedInstruments.map((inst, idx) => ({
      ...inst,
      sequence_order: idx + 1,
    }));
    const otherInstruments = pnidState.instruments.filter(
      (i) => i.context_type !== 'node' || i.context_id !== nodeId
    );
    setPnidState({
      ...pnidState,
      instruments: [...otherInstruments, ...reorderedWithSeq],
    });

    try {
      await pnidApi.reorderNodeInstruments(selectedDoc.id, nodeId, orderedIds);
    } catch (err) {
      setPnidState(previousState);
      console.error('Failed to reorder instruments', err);
      alert('Failed to reorder instruments');
    }
  };

  // Auto-sequence node instruments based on position along polyline
  const activeEquipment = getActiveEquipment();
  const activeNode = getActiveNode();
  // The item an open editor or the selection names, the docked editor's first, and a
  // drawn or redrawn line's new geometry before its saved one. A resize of the canvas
  // keeps it on screen.
  const dockedLineNodeId =
    dockView?.kind === 'lineSegmentReview' ? lineSegmentReviewPopover?.nodeId : null;
  const canvasKeepVisibleBbox =
    instrumentEditorState?.instrument.bbox
    ?? (markDialog.isOpen ? markDialog.bbox : null)
    ?? nodeGeometryBbox(pnidState?.nodes.find((node) => node.id === dockedLineNodeId))
    ?? pnidState?.instruments.find((instrument) => instrument.id === selectedInstrumentId)?.bbox
    ?? (currentAssetKind === 'line' ? nodeGeometryBbox(activeNode) : activeEquipment?.bbox)
    ?? null;

  const handleDrawingContextNavigate = useCallback((target: Pick<NoteTarget, 'kind' | 'id'>) => {
    if (!pnidState) return;
    setDrawingContextOpen(false);

    if (target.kind === 'equipment') {
      const equipment = pnidState.equipments.find((item) => item.id === target.id);
      setAssetRailTab('equipment');
      setActiveEquipmentId(target.id);
      setActiveNodeId(null);
      setSelectedInstrumentId(null);
      navigateCanvasTo(navigationBoxOf('equipment', equipment));
      return;
    }

    if (target.kind === 'line') {
      const node = pnidState.nodes.find((item) => item.id === target.id);
      setAssetRailTab('lines');
      setActiveNodeId(target.id);
      setActiveEquipmentId(null);
      setSelectedInstrumentId(null);
      navigateCanvasTo(nodeGeometryBbox(node));
      return;
    }

    focusInstrument(target.id);
  }, [focusInstrument, navigateCanvasTo, pnidState]);

  // The rail's row actions; a search edit takes the same paths, so an open form is kept the same way.
  const centerEquipment = (id: string) => {
    const equipment = pnidState?.equipments.find((candidate) => candidate.id === id);
    if (!equipment) return;
    setSelectedInstrumentId(null);
    setActiveEquipmentId(id);
    navigateCanvasTo(navigationBoxOf('equipment', equipment));
  };
  const openEquipmentEditor = (id: string) => {
    setSelectedInstrumentId(null);
    setActiveEquipmentId(id);
    if (keepsOpenAssetForm('equipment', id)) return;
    openEditDialog('equipment', id, { shouldFocus: false });
  };
  const centerInstrument = (id: string) => {
    const instrument = pnidState?.instruments.find((candidate) => candidate.id === id);
    if (!instrument) return;
    setSelectedInstrumentId(id);
    navigateCanvasTo(navigationBoxOf('instrument', instrument));
    // An instrument of another asset is hidden while isolated, so going to it ends Isolate.
    setIsolateActiveAsset(false);
  };
  const openInstrumentEditor = (id: string) => {
    const instrument = pnidState?.instruments.find((candidate) => candidate.id === id);
    if (!pnidState || !instrument) return;
    setIsolateActiveAsset(false);
    // The editor toggles closed when asked to open the instrument it already shows.
    if (instrumentEditorState?.instrument.id === id) return;
    const owner = getInstrumentEffectiveContext(instrument, pnidState);
    openEditDialog(owner.context_type === 'node' ? 'nodeInstrument' : 'equipmentInstrument', id, { shouldFocus: false });
  };
  const centerLine = (id: string) => {
    setActiveNodeId(id);
    const node = pnidState?.nodes.find((n) => n.id === id);
    navigateCanvasTo(nodeGeometryBbox(node));
  };
  const openLineEditor = (id: string) => {
    if (keepsOpenAssetForm('node', id)) return;
    openEditDialog('node', id, { shouldFocus: false });
  };

  /** Which open dock forms hold the SME's edits, as each form reports them. */
  const [dockFormsEdited, setDockFormsEdited] = useState({ markForm: false, instrumentEditor: false });
  // Stable, because each form re-reports whenever its callback changes.
  const reportMarkFormEdited = useCallback((edited: boolean) => {
    setDockFormsEdited((current) => (current.markForm === edited ? current : { ...current, markForm: edited }));
  }, []);
  const reportInstrumentEditorEdited = useCallback((edited: boolean) => {
    setDockFormsEdited((current) => (current.instrumentEditor === edited ? current : { ...current, instrumentEditor: edited }));
  }, []);
  // A form reports nothing as it unmounts, so a closed form, saved or cancelled, is cleared here.
  const instrumentEditorOpenNow = instrumentEditorState !== null;
  useEffect(() => {
    if (!markDialog.isOpen) reportMarkFormEdited(false);
  }, [markDialog.isOpen, reportMarkFormEdited]);
  useEffect(() => {
    if (!instrumentEditorOpenNow) reportInstrumentEditorEdited(false);
  }, [instrumentEditorOpenNow, reportInstrumentEditorEdited]);
  const editedDockFormItems = (): string[] => [
    ...(dockFormsEdited.markForm ? [markFormDraftLabel()] : []),
    ...(dockFormsEdited.instrumentEditor ? ['the open instrument form'] : []),
  ];
  // An edited form, and every draft the draw tools guard (a drawn line, collected regions,
  // a shape on the canvas): a form reports only its own field edits, not the geometry it opened on.
  const drawingLeaveDraftItems = (): string[] => [...new Set([...editedDockFormItems(), ...unsavedDraftItems()])];

  /** The Ctrl+F / Cmd+F search palette. */
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState(readRecentSearches);
  /** Where focus was when the palette opened: the field a Keep editing returns to. */
  const focusBeforeSearchRef = useRef<HTMLElement | null>(null);
  const openSearch = useCallback(() => {
    if (document.activeElement instanceof HTMLElement) focusBeforeSearchRef.current = document.activeElement;
    setSearchOpen(true);
  }, []);
  // The query goes with the palette, so the next open starts on the recent searches.
  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
  }, []);
  // Read on the first search, not on sheet open: most visits never search.
  const searchClickTargets = useClickTargets(selectedDoc.id, searchOpen);
  // The drawing list and the click targets are reads of their own; until they answer, what has arrived lists alone.
  const searchIndex = useMemo(
    () => (searchOpen ? buildSearchIndex(pnidState, { drawings: documents, openDrawingId: selectedDoc.id }, searchClickTargets) : []),
    [searchOpen, pnidState, documents, selectedDoc.id, searchClickTargets],
  );
  const shownSearchHits = useMemo(() => searchHits(searchIndex, searchQuery), [searchIndex, searchQuery]);
  // Show selects and pans as a drawing-context note does; edit does what the rail row's pencil
  // does, on the item's own tab. Sheet text is no item, so it pans and leaves the selection alone.
  // A drawing joins the recent searches only once it opens, not when a Keep editing stays.
  const openPickedDrawing = ({ drawing, primary }: PickedDrawing) => {
    setRecentSearches(rememberRecentSearch(primary, recentSearches));
    onOpenDocument(drawing);
  };
  const keepEditingOverPickedDrawing = () => {
    setDiscardDraftConfirm({ isOpen: false });
    const field = focusBeforeSearchRef.current;
    if (field?.isConnected) field.focus();
  };
  const handleSearchPick = (hit: SearchHit, action: SearchPickAction) => {
    closeSearch();
    if (hit.kind === 'drawing') {
      const drawing = documents.find((candidate) => candidate.id === hit.id);
      if (!drawing) return;
      const picked = { drawing, primary: hit.primary };
      // Leaving the drawing unmounts the dock, so an edited form asks first.
      if (drawingLeaveDraftItems().length > 0) setDiscardDraftConfirm({ isOpen: true, openDrawingOnConfirm: picked });
      else openPickedDrawing(picked);
      return;
    }
    setRecentSearches(rememberRecentSearch(hit.primary, recentSearches));
    if (hit.kind === 'click_target') {
      setDrawingContextOpen(false);
      navigateCanvasTo(hit.bbox);
      return;
    }
    if (action !== 'edit') {
      handleDrawingContextNavigate({ kind: hit.kind, id: hit.id });
      return;
    }
    if (hit.kind === 'equipment') {
      setAssetRailTab('equipment');
      centerEquipment(hit.id);
      openEquipmentEditor(hit.id);
    } else if (hit.kind === 'instrument') {
      setAssetRailTab('instruments');
      centerInstrument(hit.id);
      openInstrumentEditor(hit.id);
    } else {
      setAssetRailTab('lines');
      centerLine(hit.id);
      openLineEditor(hit.id);
    }
  };
  // A modal waiting on an answer keeps the keyboard; the palette would open under a confirm
  // (its scrim sits below ConfirmDialog's) and take focus from it.
  const workspaceModalOpen = annotationImportOpen || versionHistoryOpen || alertDialog.isOpen
    || confirmDialog.isOpen || discardDraftConfirm.isOpen;
  // Capture phase on window, so the browser's own find never opens over the drawing, even from
  // a dock field.
  const searchShortcutActive = !isBooting && !workspaceModalOpen;
  useEffect(() => {
    if (!searchShortcutActive) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'f') return;
      event.preventDefault();
      openSearch();
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [openSearch, searchShortcutActive]);

  const handleDrawingContextSaveNote = useCallback(async (target: NoteTarget, note: string) => {
    if (!selectedDoc?.id || !pnidState) {
      throw new Error('No P&ID document is selected.');
    }
    const persistedNote = note || null;
    pushPnidUndoSnapshot(`${target.note ? 'Edit' : 'Add'} ${target.kind} note`);

    if (target.kind === 'equipment') {
      const updated = await pnidApi.updateEquipment(selectedDoc.id, target.id, { notes: persistedNote });
      setPnidState({
        ...pnidState,
        equipments: pnidState.equipments.map((item) => item.id === updated.id ? updated : item),
      });
      trackAction('object_note_saved', { item_type: 'equipment', item_id: target.id, cleared: !persistedNote });
      refreshViewsAfterObjectSave();
      return;
    }

    if (target.kind === 'line') {
      const updated = await pnidApi.updateNode(selectedDoc.id, target.id, { notes: persistedNote });
      setPnidState({
        ...pnidState,
        nodes: pnidState.nodes.map((item) => item.id === updated.id ? updated : item),
      });
      trackAction('object_note_saved', { item_type: 'line', item_id: target.id, cleared: !persistedNote });
      refreshViewsAfterObjectSave();
      return;
    }

    const updated = await instrumentApi.updateInstrument(selectedDoc.id, target.id, { notes: persistedNote });
    setPnidState({
      ...pnidState,
      instruments: pnidState.instruments.map((item) => item.id === updated.id ? updated : item),
    });
    invalidatePublishableInstruments();
    trackAction('object_note_saved', { item_type: 'instrument', item_id: target.id, cleared: !persistedNote });
    refreshViewsAfterObjectSave();
  }, [invalidatePublishableInstruments, refreshViewsAfterObjectSave, pnidState, pushPnidUndoSnapshot, selectedDoc?.id, trackAction]);

  return (
    <EditorDockSlotContext.Provider value={editorDockSlot}>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        /* Fills the workspace shell's content card instead of the viewport. */
        flex: 1,
        minHeight: 0,
        height: "100%",
      }}>
      {/* The shell-standard header band every other page carries. */}
      <PageHeader
        title={pnidState?.pnid_no || selectedDoc.original_filename || selectedDoc.filename}
        titleBadge={publishedVersion !== null ? (
          <span
            title={`Published as version ${publishedVersion}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "3px 7px",
              borderRadius: 6,
              backgroundColor: "var(--raven-purple-200)",
              color: "var(--raven-purple-900)",
              fontFamily: "monospace",
              fontSize: 12,
              fontWeight: 700,
            }}>
            v{publishedVersion}
          </span>
        ) : undefined}
        onBack={() => navigate('/drawings')}
        actions={!isBooting ? (
          <div className="pnid-ws-headeractions">
            <div className="pnid-ws-headerctas">
              <WorkspaceRailItem
                icon={Send}
                label="Publish"
                variant="contained"
                disabled={!selectedDoc || readOnlyWizard}
                onClick={() => setPublishPanelOpen(true)}
                title={
                  readOnlyWizard
                    ? "You need edit access to publish"
                    : "Check this drawing and publish it as a new version"
                }
              />
            </div>
          </div>
        ) : undefined}
      />
      {/* One band carrying every drawing action. */}
      {!isBooting && (
        <div className="pnid-ws-band">
          <div className="pnid-ws-actionbar" role="group" aria-label="Drawing actions">
          {!readOnlyWizard && (
            <div className="pnid-ws-draw-tools" role="group" aria-label="Draw">
              {offeredDrawToolButtons.map(({ tool, title }) => (
                <button
                  key={tool}
                  type="button"
                  className="pnid-btn pnid-btn--outlined pnid-btn--sm"
                  aria-label={DRAW_TOOL_LABEL[tool]}
                  aria-pressed={isDrawToolPressed(tool)}
                  aria-keyshortcuts={DRAW_TOOL_KEY[tool]}
                  // The canvas is view-only under the Drawing details dock.
                  disabled={setupDockOpen}
                  title={setupDockOpen ? "Close Drawing details to draw" : title}
                  onClick={() => pressDrawButton(tool)}
                >
                  <DrawToolLabel tool={tool} />
                </button>
              ))}
            </div>
          )}
          {!readOnlyWizard && (
            <div className="pnid-ws-rail-pair" aria-label="Undo redo annotation actions">
              <WorkspaceRailItem
                icon={Undo2}
                label="Undo"
            iconOnly
                onClick={handleUndoPnidAction}
                disabled={historyApplying || undoStack.length === 0}
                title="Undo last annotation action (Cmd/Ctrl+Z)"
              />
              <WorkspaceRailItem
                icon={Redo2}
                label="Redo"
            iconOnly
                onClick={handleRedoPnidAction}
                disabled={historyApplying || redoStack.length === 0}
                title="Redo annotation action (Cmd/Ctrl+Shift+Z)"
              />
            </div>
          )}
          {!readOnlyWizard && (
            <WorkspaceRailItem
              icon={Download}
              label="Import Annotations"
              iconOnly
              flagged={targetHasAnnotations}
              sub={targetHasAnnotations ? `Confirm replace: ${targetAnnotationSummary}` : undefined}
              onClick={openAnnotationImportDialog}
              disabled={annotationImportDisabled}
              aria-label={
                annotationImportDisabledReason
                  ? `Import annotations disabled. ${annotationImportDisabledReason}`
                  : targetHasAnnotations
                  ? `Import annotations. Target has ${targetAnnotationSummary || "existing annotations"}; confirmation required before replacement.`
                  : `Import ${diagramTypeLabel(diagramMode)} annotations from another document`
              }
              title={
                annotationImportDisabledReason ||
                (targetHasAnnotations
                  ? `Target has ${targetAnnotationSummary || "existing annotations"}. You will be asked to confirm before replacing them.`
                  : "") ||
                `Import ${diagramTypeLabel(diagramMode)} annotations from another document`
              }
            />
          )}
          {isAdmin && (
            <WorkspaceRailItem icon={FolderOpen} label="File Manager"
            iconOnly onClick={onOpenFileManager} />
          )}
          <hr className="pnid-ws-rail-divider" />
          <WorkspaceRailItem
            icon={Info}
            label="Drawing details"
            iconOnly
            aria-pressed={setupDockOpen}
            onClick={toggleSetupDock}
            title={setupDockOpen ? "Close drawing details" : "Drawing details: number, title, class and legend"}
          />
          <WorkspaceRailItem
            icon={BookOpen}
            label="Open Legend"
            iconOnly
            disabled={!applicableLegendRoute}
            onClick={() => {
              if (applicableLegendRoute) window.open(applicableLegendRoute, '_blank', 'noopener,noreferrer');
            }}
            title={applicableLegendRoute
              ? "Open the applicable legend's record in a new tab"
              : "No applicable legend. Set one in P&ID setup."}
          />
          <WorkspaceRailItem
            icon={History}
            label="Version History"
            iconOnly
            onClick={() => setVersionHistoryOpen(true)}
            title="View and restore previous versions"
          />
          <hr className="pnid-ws-rail-divider" />
          <WorkspaceRailItem
            icon={Focus}
            label="Isolate"
            iconOnly
            aria-pressed={isolateActiveAsset}
            onClick={() => setIsolateActiveAsset((on) => !on)}
            title="Isolate: show only the open asset and its instruments (hold X)"
          />
          <button type="button" className="pnid-ws-rail-item pnid-ws-search-button"
            aria-keyshortcuts={SEARCH_SHORTCUT.aria} onClick={openSearch} disabled={workspaceModalOpen}
            title="Search this drawing">
            <AppIcon icon={Search} size={16} />
            <span>Search</span>
            <kbd className="pnid-ws-shortcut">{SEARCH_SHORTCUT.label}</kbd>
          </button>
          <CanvasZoomControls scaleSource={canvasScaleSource} onZoom={requestCanvasZoom} />
          </div>
        </div>
      )}

    <div
      style={{
        display: "flex",
        flex: 1,
        /* The bands above are fixed height; this row absorbs the remainder and
           clips, so the canvas scrolls inside it instead of growing the page. */
        minHeight: 0,
        overflow: "hidden",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        backgroundColor: "var(--raven-bg-page)",
      }}>
      {/* Targets live beside the canvas, not in a chip strip above it. */}
      {!isBooting && (
        <WorkspaceAssetRail
          tab={assetRailTab} onTabChange={setAssetRailTab}
          assets={railAssets}
          publishableInstrumentIds={railPublishableInstrumentIds}
          publishableInstrumentsError={publishableInstruments.isError && previewingVersion === null
            ? { onRetry: publishableInstruments.refetch }
            : undefined}
          activeEquipmentId={activeEquipmentId} selectedInstrumentId={selectedInstrumentId}
          activeNodeId={activeNodeId}
          query={railQuery} onQueryChange={setRailQuery}
          needsReview={{
            checked: needsReviewOn,
            onChange: setNeedsReviewOn,
            pendingIds: pendingAgentIds,
            onReview: (kind, id) => selectAgentReviewItem(agentReviewKey(kind, id)),
            onShownRowsChange: reportRailShownIds,
            emptyMessage: (
              <span>
                No pending agent proposals on this drawing.{' '}
                <Link to="/agent-review-queue">Plant agent review queue</Link>
              </span>
            ),
          }}
          onCenterEquipment={centerEquipment}
          onEditEquipment={openEquipmentEditor}
          onCenterInstrument={centerInstrument}
          onEditInstrument={openInstrumentEditor}
          onCenterNode={centerLine}
          onEditNode={openLineEditor}
        />
      )}
      {/* Left action rail — the canvas header's former right-hand cluster.
          Same JSX and handlers, relocated; it renders exactly when the header
          does (after boot). */}
      {/* Main Content Area - Canvas fills entire space */}
      <div
        style={{
          flex: 1,
          display: "flex",
          position: "relative",
          overflow: "hidden",
        }}>
        {/* Canvas Area */}
        <div
          style={{
            flex: 1,
            display: "flex",
            position: "relative",
            overflow: "hidden",
          }}>
          {dockView?.kind === 'setup' && (
            <EditorDock
              title="Drawing Details"
              // A drawing with no number yet shows no ✕: the dock asks for the number first.
              // The toolbar button still closes it.
              onClose={pnidState?.pnid_no ? closeSetupWithoutSaving : undefined}
              footer={
                <DockFooter>
                  <button
                    disabled={setupMutation.isPending}
                    onClick={() =>
                      readOnlyWizard
                        ? closeSetupDock()
                        : savePnidSetup()
                    }
                    className="pnid-btn pnid-btn--contained"
                    style={{ width: "100%" }}>
                    {readOnlyWizard ? "Continue to View →" : "Save"}
                  </button>
                </DockFooter>
              }>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
                      Drawing type
                    </span>
                    {renderDiagramModeToggle()}
                  </div>

                  {/* AI Prefill Button - editors only */}
                  {!readOnlyWizard && hasAIData && !pnidState?.ai_data_loaded && (
                    <button
                      onClick={handlePrefillFromAI}
                      disabled={loadingAI}
                      style={{
                        width: "100%",
                        padding: "12px",
                        backgroundColor: loadingAI ? "var(--raven-text-secondary)" : "#8b5cf6",
                        color: "white",
                        border: "none",
                        borderRadius: 8,
                        cursor: loadingAI ? "not-allowed" : "pointer",
                        marginBottom: 16,
                        fontSize: 14,
                        fontWeight: 500,
                      }}>
                      {loadingAI
                        ? "⏳ Loading AI data..."
                        : "✨ Prefill from AI Data"}
                    </button>
                  )}

                  {pnidState?.ai_data_loaded && (
                    <div
                      style={{
                        padding: "10px 14px",
                        backgroundColor: "#dcfce7",
                        border: "1px solid #bbf7d0",
                        borderRadius: 8,
                        marginBottom: 16,
                        fontSize: 13,
                        color: "#166534",
                      }}>
                      ✓ AI data loaded: {pnidState.equipments.length} equipment,{" "}
                      {pnidState.nodes.length} lines
                    </div>
                  )}

                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      marginBottom: 6,
                      fontWeight: 500,
                    }}>
                    Drawing Number
                  </label>
                  <input
                    type="text"
                    value={pnidNoDraft}
                    onChange={(e) => !readOnlyWizard && setPnidNoDraft(e.target.value)}
                    placeholder="e.g., 1T4031 PR/5AE-9"
                    disabled={readOnlyWizard}
                    readOnly={readOnlyWizard}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 16,
                      fontSize: 14,
                      ...(readOnlyWizard && { backgroundColor: "#f3f4f6", cursor: "not-allowed" }),
                    }}
                  />

                  <label
                    style={{
                      display: "block",
                      fontSize: 13,
                      marginBottom: 6,
                      fontWeight: 500,
                    }}>
                    Drawing Title (optional)
                  </label>
                  <input
                    type="text"
                    value={pnidTitleDraft}
                    onChange={(e) => !readOnlyWizard && setPnidTitleDraft(e.target.value)}
                    placeholder="e.g., AMINE REGENERATION UNIT"
                    disabled={readOnlyWizard}
                    readOnly={readOnlyWizard}
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      marginBottom: 16,
                      fontSize: 14,
                      ...(readOnlyWizard && { backgroundColor: "#f3f4f6", cursor: "not-allowed" }),
                    }}
                  />

                  <label htmlFor="drawing-class" style={{ display: "block", fontSize: 13, marginBottom: 6, fontWeight: 500 }}>
                    Drawing class (optional)
                  </label>
                  {/* The select drops its native chrome and takes the text inputs' unstyled browser font (Arial), so it matches them in height and look. */}
                  <div style={{ position: "relative", marginBottom: 16 }}>
                    <select
                      id="drawing-class"
                      value={drawingClassDraft}
                      onChange={(event) => setDrawingClassDraft(event.target.value as DrawingClass | "")}
                      disabled={readOnlyWizard}
                      style={{
                        width: "100%",
                        padding: "10px 36px 10px 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: "Arial",
                        color: "inherit",
                        backgroundColor: "#fff",
                        appearance: "none",
                        ...(readOnlyWizard && { backgroundColor: "#f3f4f6", cursor: "not-allowed" }),
                      }}>
                      <option value="">Not assigned</option>
                      <option value="process">Process</option>
                      <option value="utility">Utility</option>
                      <option value="vendor">Vendor</option>
                      <option value="package">Package</option>
                      <option value="interconnection">Interconnection</option>
                    </select>
                    <AppIcon
                      icon={ChevronDown}
                      size={16}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                    />
                  </div>

                  <label htmlFor="applicable-legend" style={{ display: "block", fontSize: 13, marginBottom: 6, fontWeight: 500 }}>
                    Applicable legend (optional)
                  </label>
                  <div style={{ position: "relative", marginBottom: 6 }}>
                    <select
                      id="applicable-legend"
                      aria-describedby="applicable-legend-help"
                      value={legendSheetIdDraft}
                      onChange={(event) => setLegendSheetIdDraft(event.target.value)}
                      disabled={readOnlyWizard || legendSheetsStatus !== "ready"}
                      style={{
                        width: "100%",
                        padding: "10px 36px 10px 12px",
                        border: "1px solid var(--raven-divider)",
                        borderRadius: 8,
                        fontSize: 14,
                        fontFamily: "Arial",
                        color: "inherit",
                        backgroundColor: "var(--raven-surface-tier1)",
                        appearance: "none",
                        ...((readOnlyWizard || legendSheetsStatus !== "ready") && { backgroundColor: "var(--raven-surface-tier3)", cursor: "not-allowed" }),
                      }}>
                      <option value="">No applicable legend</option>
                      {legendSheetOptions.map(({ id, name }) => (
                        <option key={id} value={id}>{name}</option>
                      ))}
                    </select>
                    <AppIcon
                      icon={ChevronDown}
                      size={16}
                      style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
                    />
                  </div>
                  <p id="applicable-legend-help" style={{ margin: "0 0 24px", fontSize: 12, color: "var(--raven-text-secondary)" }}>
                    {legendSheetsStatus === "empty" && "Add a legend sheet for this plant first"}
                    {legendSheetsStatus === "loading" && "Loading legend sheets…"}
                    {legendSheetsStatus === "error" && (
                      <>
                        Legend sheets could not be loaded.
                        <button
                          type="button"
                          onClick={() => void legendSheets.refetch()}
                          className="pnid-btn pnid-btn--outlined"
                          style={{
                            marginLeft: 8,
                            padding: "4px 8px",
                            fontSize: 12,
                            backgroundColor: "var(--raven-surface-tier2)",
                            color: "var(--raven-text-secondary)",
                            border: "none",
                            borderRadius: 4,
                            cursor: "pointer",
                          }}>
                          Retry
                        </button>
                      </>
                    )}
                  </p>

            </EditorDock>
          )}
          {/* Canvas */}
          <div
            style={{
              flex: 1,
              padding: 8,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}>
            {isBooting ? (
              <WorkspaceSkeleton />
            ) : (
              /* The canvas. Beside the Drawing details dock it is view-only. */
              <>
                {/* Version Preview Banner */}
                {dockView?.kind !== 'setup' && previewingVersion && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "8px 14px",
                      marginBottom: 8,
                      backgroundColor: "#fef3c7",
                      border: "1px solid #fcd34d",
                      borderRadius: 8,
                    }}>
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 16 }}>👁️</span>
                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#92400e",
                        }}>
                        Viewing version {previewingVersion}
                      </span>
                      <span style={{ fontSize: 12, color: "#a16207" }}>
                        — Changes will not be saved until you restore
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button
                        onClick={() => {
                          if (originalStateBeforePreview) {
                            setPnidState(originalStateBeforePreview);
                          }
                          setPreviewingVersion(null);
                          setOriginalStateBeforePreview(null);
                        }}
                        style={{
                          padding: "5px 12px",
                          fontSize: 12,
                          backgroundColor: "white",
                          color: "#374151",
                          border: "1px solid #d1d5db",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontWeight: 500,
                        }}>
                        Exit Preview
                      </button>
                      <button
                        onClick={() => setVersionHistoryOpen(true)}
                        style={{
                          padding: "5px 12px",
                          fontSize: 12,
                          backgroundColor: "var(--raven-purple-900)",
                          color: "white",
                          border: "none",
                          borderRadius: 5,
                          cursor: "pointer",
                          fontWeight: 600,
                        }}>
                        Restore This Version
                      </button>
                    </div>
                  </div>
                )}

                {/* Reviewer mode banner */}
                {dockView?.kind !== 'setup' && user?.is_reviewer && (
                  <div
                    style={{
                      marginBottom: 8,
                      padding: "8px 12px",
                      backgroundColor: "#fef3c7",
                      border: "1px solid #f59e0b",
                      borderRadius: 8,
                      fontSize: 13,
                      color: "#92400e",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}>
                    <span style={{ fontWeight: 600 }}>👁️ Reviewer mode</span>
                    <span>View and add comments only. Editing is disabled.</span>
                  </div>
                )}

                {/* The import-adjustment notice, when an import just landed. A flat
                    row, not a card: the band above already carries the surface. */}
                {dockView?.kind !== 'setup' && <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    marginBottom: 4,
                    padding: "2px 0",
                    position: "relative",
                    zIndex: 30,
                    overflow: "visible",
                  }}>
                  {annotationImportAdjustment && !readOnlyWizard && (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 10px",
                        borderRadius: 8,
                        backgroundColor: "var(--raven-purple-100)",
                        border: "1px solid #bfdbfe",
                        color: "var(--raven-purple-900)",
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      <span
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: 99,
                          backgroundColor: "var(--raven-purple-900)",
                          flex: "0 0 auto",
                        }}
                      />
                      <span style={{ flex: "1 1 auto", minWidth: 0 }}>
                        Imported {annotationImportAdjustment.copiedSummary} from{" "}
                        <strong>{annotationImportAdjustment.sourceLabel}</strong>
                        {annotationImportAdjustment.overwroteExisting && annotationImportAdjustment.replacedSummary
                          ? ` after replacing ${annotationImportAdjustment.replacedSummary}`
                          : ""}
                        . {annotationImportAdjustment.geometryAdjustmentText || "No automatic scaling was needed."} Drag selected boxes to fine-tune placement, use handles to resize, or delete selected items.
                      </span>
                      <button
                        type="button"
                        onClick={handleSelectAllWizardAnnotations}
                        style={{
                          padding: "5px 8px",
                          fontSize: 12,
                          border: "1px solid #93c5fd",
                          borderRadius: 6,
                          backgroundColor: "white",
                          color: "var(--raven-purple-800)",
                          cursor: "pointer",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={handleUndoPnidAction}
                        disabled={historyApplying || undoStack.length === 0}
                        style={{
                          padding: "5px 8px",
                          fontSize: 12,
                          border: "1px solid #93c5fd",
                          borderRadius: 6,
                          backgroundColor: "white",
                          color: undoStack.length ? "var(--raven-purple-800)" : "var(--raven-text-secondary)",
                          cursor: historyApplying || undoStack.length === 0 ? "not-allowed" : "pointer",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Undo import
                      </button>
                      <button
                        type="button"
                        onClick={() => setAnnotationImportAdjustment(null)}
                        style={{
                          padding: "5px 8px",
                          fontSize: 12,
                          border: "1px solid #bfdbfe",
                          borderRadius: 6,
                          backgroundColor: "var(--raven-purple-200)",
                          color: "#1e3a8a",
                          cursor: "pointer",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Finish
                      </button>
                    </div>
                  )}
                  {geometryTarget &&
                    (() => {
                      // Get label for the instrument being marked
                      let targetLabel: string = geometryTarget.kind;
                      if (
                        geometryTarget.kind === "equipmentInstrument" &&
                        pnidState
                      ) {
                        const inst = equipmentInstruments.find(
                          (i) => i.id === geometryTarget.id
                        );
                        targetLabel =
                          (inst ? formatInstrumentIdentity(inst) : "") || "Equipment Instrument";
                      } else if (
                        geometryTarget.kind === "nodeInstrument" &&
                        pnidState
                      ) {
                        const inst = nodeInstruments.find(
                          (i) => i.id === geometryTarget.id
                        );
                        targetLabel =
                          (inst ? formatInstrumentIdentity(inst) : "") || "Line Instrument";
                      }
                      return (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "6px 10px",
                            borderRadius: 8,
                            backgroundColor: "var(--raven-purple-100)",
                            border: "1px solid #93c5fd",
                            fontSize: 12,
                            color: "var(--raven-purple-800)",
                            fontWeight: 600,
                          }}>
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: 99,
                              backgroundColor: "var(--raven-purple-900)",
                              animation: "pulse 1.5s infinite",
                            }}
                          />
                          <span>
                            {geometryTarget.op === 'append'
                              ? 'Draw another region for: '
                              : 'Draw box to mark: '}
                            <strong>{targetLabel}</strong>
                          </span>
                          <button
                            onClick={() => setBoxDraft(null)}
                            style={{
                              marginLeft: "auto",
                              padding: "4px 8px",
                              fontSize: 12,
                              border: "1px solid #93c5fd",
                              borderRadius: 6,
                              cursor: "pointer",
                              backgroundColor: "white",
                              color: "var(--raven-purple-900)",
                            }}>
                            Cancel
                          </button>
                        </div>
                      );
                    })()}
                </div>}

                {/* Canvas component */}
                <div
                  ref={canvasContainerRef}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    borderRadius: 8,
                    overflow: "hidden",
                    backgroundColor: "white",
                    position: "relative",
                  }}>
                  <PidCanvas
                // The Drawing details dock shows the drawing so the SME can read its title block:
                // nothing on it is editable while the dock is open, and pan and zoom still work.
                readOnly={readOnlyWizard || setupDockOpen}
                edgePanEnabled={!entityEditorOpen}
                imageUrl={selectedDoc.image_url || documentApi.getImageUrl(selectedDoc.id)}
                sections={[]}
                onBoxDrawn={handleBoxDrawn}
                onLineDrawn={handleLineDrawn}
                pendingPieces={[]}
                onCancelPending={undefined}
                onSelectSection={() => {}}
                selectedSectionId={undefined}
                onSelectPending={undefined}
                selectedPendingId={undefined}
                docId={selectedDoc.id}
                annotating={true}
                pnidState={pnidState}
                armedTool={armedTool}
                offeredDrawTools={offeredDrawTools}
                // A key presses a draw button only where the buttons take presses and no dialog is open.
                onPressDrawTool={readOnlyWizard || setupDockOpen || discardDraftConfirm.isOpen
                  ? undefined
                  : pressDrawButton}
                activeAssetKind={currentAssetKind}
                activeEquipmentId={activeEquipmentId}
                activeNodeId={activeNodeId}
                selectedInstrumentId={selectedInstrumentId}
                highlightedEntityId={highlightedEntityId}
                onRightClickInstrument={setupDockOpen ? undefined : (id) => void handleRightClickInstrument(id)}
                collectingRegions={boxDraft !== null}
                draftRegions={boxDraft?.regions.map((region) => region.box) ?? []}
                onFinishRegions={handleFinishRegions}
                onRemoveLastRegion={handleRemoveLastRegion}
                onExitCollect={handleExitCollect}
                // A focused dock field outranks a marking, which reaches one only through the
                // instrument editor's own Edit regions: the SME moved focus there after arming.
                clickTargets={copyField ? copyClickTargets : isMarkingInstrument ? visibleClickTargets : undefined}
                onClickTarget={handleClickTarget}
                onCopyClickTarget={copyField ? handleCopyClickTarget : undefined}
                askKindOnDraw={armedTool === null && !boxDraft && !isNodeMarkingMode}
                onPendingBoxKind={handlePendingBoxKind}
                onKindPillOpenChange={setKindPillOpen}
                isolateActiveAsset={isolateActiveAsset}
                focusRequest={canvasFocusRequest}
                keepVisibleBbox={canvasKeepVisibleBbox}
                fitRequestId={canvasFitRequestId}
                fitBoxRequest={setupFitRequest}
                zoomRequest={canvasZoomRequest}
                onScaleChange={canvasScaleSource.write}
                pendingMarkBbox={markDialog.isOpen ? markDialog.bbox : undefined}
                onUpdatePendingMarkBbox={markDialog.isOpen && !markDialog.drawnLine ? handlePendingMarkBboxUpdate : undefined}
                onSelectWizardItem={setupDockOpen ? undefined : handleWizardCanvasSelect}
                selectedWizardItemKeys={selectedWizardItemKeys}
                onSelectAllWizardItems={handleSelectAllWizardAnnotations}
                onClearWizardItemSelection={handleClearWizardAnnotationSelection}
                onDeleteSelectedWizardItems={handleDeleteSelectedWizardAnnotations}
                onMarkSelectedAsRedundancySet={
                  selectedDoc?.domain_id && !readOnlyWizard && redundancySetEquipmentIds
                    ? handleMarkSelectedAsRedundancySet
                    : undefined
                }
                onShiftSelectedWizardAnnotations={handleShiftSelectedWizardAnnotations}
                onBeginWizardItemBboxUpdate={handleBeginPersistedBboxUpdate}
                onUpdateWizardItemBbox={handlePersistedBboxUpdate}
                onQuickDeleteWizardItem={handleQuickDeleteWizardItem}
                onShiftWizardAnnotations={handleShiftWizardAnnotations}
                onDoubleClickNode={(nodeId) => handleMarkNode(nodeId, { navigate: false })}
                onPathStart={handleLinePathStart}
                onExitLineDrawing={exitLineDrawing}
                identifyingBboxes={identifyingBboxes}
                isNodeMarkingMode={isNodeMarkingMode}
                nodeDrawingShape={diagramMode === 'pid' ? nodeDrawingShape : 'pipeline'}
                onDraftStateChange={setCanvasHasDraft}
                clearDraftSignal={canvasClearSignal}
                nodePointCandidates={nodePointCandidates}
                onResolveNodePointCandidate={resolveNodePointCandidate}
                onImageSize={handleCanvasImageSize}
                segmentReview={{
                  nodeId: lineSegmentReviewPopover?.nodeId ?? null,
                  hoveredDistinctId: lineSegmentReviewPopover?.hoveredDistinctId ?? null,
                  selectedDistinctId: lineSegmentReviewPopover?.selectedDistinctId ?? null,
                }}
                segmentCoordinateScale={
                  segmentCoordinateInfo ? { sx: segmentCoordinateInfo.sx, sy: segmentCoordinateInfo.sy } : undefined
                }
                // Comment props
                comments={comments}
                selectedCommentId={selectedCommentId}
                onSelectComment={handleSelectComment}
                showResolvedComments={showResolvedComments}
                onDirectAttachAnnotation={handleDirectAttachAnnotation}
                onCanvasInteraction={handleCanvasInteraction}
              />
            </div>
              </>
            )}
          </div>

          <div ref={setEditorDockSlot} style={{ display: "contents" }} />
          {(dockView?.kind === "equipment" || dockView?.kind === "node" || markDialogCovered) && (
          <EditorDock
            hidden={markDialogCovered}
            onClose={closeMarkForm}
            confirmDiscardOnEscape
            title={
              markDialog.kind === "node"
                ? "Create / Edit Line"
                : markDialog.editTarget
                  ? `Edit ${markDialog.kind}`
                  : `Mark ${markDialog.kind}`
            }>
            <PnidMarkDialog
              // A drawn line's form opens fresh, on its first empty end, whatever the dock showed before.
              key={markDialog.drawnLine ? "drawn-line" : markDialog.editTarget?.id ?? "mark"}
              kind={markDialog.kind}
              pnidNoDefault={pnidState?.pnid_no}
              activeEquipment={getActiveEquipment()}
              // A drawn line's form names its stored line through _targetNodeId, and a new one names none.
              activeNode={markDialog.drawnLine ? null : getActiveNode()}
              equipmentInstruments={equipmentInstruments}
              nodeInstruments={nodeInstruments}
              directNodeInstruments={directNodeInstruments}
              nodes={pnidState?.nodes ?? []}
              equipments={pnidState?.equipments ?? []}
              instruments={unifiedInstrumentRefs}
              validation={shownValidation}
              draftRequestFor={markDialogDraftRequestFor}
              aiData={aiData}
              currentDocument={selectedDoc}
              allDocuments={documents}
              linkedDocumentStates={linkedDocumentStates}
              onLoadLinkedDocumentState={loadLinkedDocumentState}
              onOpenLinkedDocument={handleOpenLinkedDocument}
              initialValues={markDialog.initialValues}
              drawnPath={markDialog.drawnLine && {
                pointCount: (markDialog.bbox?.polygon?.length ?? 0) / 2,
                boundCount: markDialog.drawnLine.boundCount,
              }}
              diagramMode={diagramMode}
              onSubmit={diagramMode === 'pfd' ? submitPfdStreamDialog : handleMarkDialogSubmit}
              onCancel={closeMarkForm}
              onDirtyChange={reportMarkFormEdited}
              isMarkingExisting={Boolean(
                markDialog.initialValues?._markExistingId
              )}

              onRedrawPath={
                markDialog.kind === "node" &&
                Boolean(markDialog.initialValues?._markExistingId) &&
                !markDialog.drawnLine
                  ? handleRedrawPath
                  : undefined
              }
              // A drawn line's form saves a new path, so it offers no Delete of the line it binds.
              onDelete={markDialog.drawnLine ? undefined : () => {
                const editedId = markDialogEditedId(markDialog);
                if (editedId) handleWizardDelete({ kind: markDialog.kind, id: editedId });
              }}
              onEditInstrument={(instId) => {
                openEditDialog("nodeInstrument", instId, { shouldFocus: false, nested: true });
              }}
              onUnattachInstrument={(instId) => {
                handleWizardDelete({ kind: "nodeInstrument", id: instId });
              }}
              onEditEquipmentInstrument={(instId) => {
                openEditDialog("equipmentInstrument", instId, { shouldFocus: false, nested: true });
              }}
              onUnattachEquipmentInstrument={(instId) => {
                handleWizardDelete({ kind: "equipmentInstrument", id: instId });
              }}
              onReorderInstruments={(reordered) => {
                if (activeNodeId) handleReorderNodeInstruments(activeNodeId, reordered);
              }}
              onAutoSequence={async (nodeId) => {
                if (!selectedDoc) return;
                const result = await pnidApi.autoSequenceNodeInstruments(selectedDoc.id, nodeId);
                const updated = takeDrawingResponse(await pnidApi.get(selectedDoc.id));
                setPnidState(updated);
                console.log(`Auto-sequenced: ${result.message}`);
              }}
              onAutoAddBranches={async (nodeId) => {
                if (!selectedDoc) return { created: 0, skipped: 0 };
                try {
                  const result = await pnidApi.autoAddBranchInstruments(selectedDoc.id, nodeId, {
                    dryRun: true,
                    endpointThresholdPx: 60,
                  });
                  const candidates = result.candidates ?? [];
                  const skippedCount = result.skipped_endpoints.length;
                  setNodePointCandidates((prev) => {
                    const withoutBranch = prev.filter(
                      (c) => !(c.nodeId === nodeId && c.source === "branch")
                    );
                    const mapped = candidates.map((candidate) => {
                      const branchRef = String(candidate.other_node_no);
                      const branchLine = candidate.other_line_no?.trim();
                      const label = branchLine || `Node #${branchRef}`;
                      return {
                        id: crypto.randomUUID(),
                        nodeId,
                        point: { x: candidate.x, y: candidate.y },
                        bbox: null,
                        status: "ready" as const,
                        options: [{ label: "Branch" as const, score: 1 }],
                        source: "branch" as const,
                        branchRef,
                        branchLineNo: branchLine || null,
                        branchDirection: candidate.branch_direction,
                        branchLabel: label,
                        branchAngleDeg: candidate.angle_deg ?? null,
                        branchDistancePx: candidate.distance_px ?? null,
                      };
                    });
                    return [...withoutBranch, ...mapped];
                  });
                  if (candidates.length > 0) {
                    showAlert("Branch Candidates", `Found ${candidates.length} candidate point${candidates.length === 1 ? "" : "s"}. Review the list to add branches.`, "info");
                  } else {
                    showAlert("No Candidates", skippedCount ? `Checked ${skippedCount} endpoints. No branch candidates were found.` : "No branch candidates were detected for this line.", "info");
                  }
                  return { created: 0, skipped: skippedCount };
                } catch (error) {
                  console.error("Branch candidate detection failed:", error);
                  showAlert("Branch detection failed", "Could not detect branch candidates. Please try again.", "error");
                  return { created: 0, skipped: 0 };
                }
              }}
              branchCandidates={activeNodeId ? nodePointCandidates.filter(c => c.nodeId === activeNodeId && c.source === "branch") : []}
              onConfirmBranchCandidate={(candidateId) => {
                void resolveNodePointCandidate(candidateId, "Branch");
              }}
              onIgnoreBranchCandidate={(candidateId) => {
                void resolveNodePointCandidate(candidateId, "ignore");
              }}
              onHighlightEntity={setHighlightedEntityId}
              onMarkInstrument={handleMarkInstrumentFromDialog}
              onSplitCompoundEquipment={
                compoundSplitTargetId ? handleSplitCompoundEquipment : undefined
              }
              compoundSplitInheritedType={compoundSplitInheritedType}
              readOnly={readOnlyWizard}
            />
          </EditorDock>
          )}

          <SlidePanel
            isOpen={drawingContextOpen}
            onClose={() => setDrawingContextOpen(false)}
            title={`Notes & Revisions${pnidState?.pnid_no ? ` — ${pnidState.pnid_no}` : ""}`}
            width={520}>
            <DrawingContextPanel
              pnidState={pnidState}
              readOnly={readOnlyWizard}
              onNavigate={handleDrawingContextNavigate}
              onSaveNote={handleDrawingContextSaveNote}
            />
          </SlidePanel>

          <SlidePanel
            isOpen={publishPanelOpen}
            onClose={() => setPublishPanelOpen(false)}
            title="Publish check"
            width={460}>
            <PublishCheckPanel
              docId={selectedDoc?.id ?? null}
              isOpen={publishPanelOpen}
              currentDrawingNo={pnidState?.pnid_no ?? null}
              onClose={() => setPublishPanelOpen(false)}
              onPublished={setPublishedVersion}
              onOpenNotes={() => {
                setPublishPanelOpen(false);
                setDrawingContextOpen(true);
              }}
              showAlert={showAlert}
            />
          </SlidePanel>
        </div>
      </div>

      {annotationImportOpen && selectedDoc && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(15, 23, 42, 0.36)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}>
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="annotation-import-title"
            style={{
              width: "min(720px, 100%)",
              maxHeight: "80vh",
              backgroundColor: "white",
              borderRadius: 8,
              boxShadow: "0 24px 60px rgba(15, 23, 42, 0.22)",
              border: "1px solid #e5e7eb",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}>
            <div
              style={{
                padding: "16px 18px",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}>
              <div style={{ minWidth: 0 }}>
                <div id="annotation-import-title" style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
                  Import annotations
                </div>
                <div
                  style={{
                    marginTop: 3,
                    fontSize: 12,
                    color: "#64748b",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                  Target: {getDocumentPrimaryLabel(selectedDoc)} - {diagramTypeLabel(diagramMode)}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close import annotations dialog"
                onClick={() => setAnnotationImportOpen(false)}
                disabled={annotationImporting}
                style={{
                  border: "1px solid #e5e7eb",
                  backgroundColor: "white",
                  borderRadius: 6,
                  padding: "5px 9px",
                  cursor: annotationImporting ? "not-allowed" : "pointer",
                  color: "#475569",
                  fontWeight: 700,
                }}>
                x
              </button>
            </div>

            <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 12, minHeight: 0 }}>
              {!targetStateReady && (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 6,
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    color: "#475569",
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                  {annotationImportDisabledReason || "Loading target annotations..."}
                </div>
              )}
              {targetHasAnnotations && (
                <div
                  style={{
                    padding: "10px 12px",
                    borderRadius: 6,
                    backgroundColor: "#fef3c7",
                    border: "1px solid #fcd34d",
                    color: "#92400e",
                    fontSize: 12,
                    fontWeight: 600,
                  }}>
                  Target has existing annotations: {targetAnnotationSummary || "annotations"}. Import will ask for confirmation before replacing them.
                </div>
              )}

              <input
                type="text"
                value={annotationImportSearch}
                onChange={(event) => setAnnotationImportSearch(event.target.value)}
                placeholder={`Search source ${diagramTypeLabel(diagramMode)} documents`}
                disabled={annotationImporting}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: "1px solid #d1d5db",
                  borderRadius: 7,
                  fontSize: 13,
                  outline: "none",
                }}
              />

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  overflowY: "auto",
                  maxHeight: "42vh",
                }}>
                {annotationImportSources.length === 0 ? (
                  <div style={{ padding: 18, color: "var(--raven-text-secondary)", fontSize: 13, textAlign: "center" }}>
                    No matching source documents
                  </div>
                ) : (
                  annotationImportSources.map((doc) => {
                    const annotation = getAnnotation(doc.id);
                    const selected = annotationImportSourceId === doc.id;
                    return (
                      <button
                        key={doc.id}
                        type="button"
                        onClick={() => setAnnotationImportSourceId(doc.id)}
                        disabled={annotationImporting}
                        style={{
                          width: "100%",
                          border: "none",
                          borderBottom: "1px solid #f1f5f9",
                          backgroundColor: selected ? "var(--raven-purple-100)" : "white",
                          cursor: annotationImporting ? "not-allowed" : "pointer",
                          padding: "11px 14px",
                          textAlign: "left",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 14,
                        }}>
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: selected ? "var(--raven-purple-800)" : "#111827",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {getDocumentPrimaryLabel(doc)}
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              color: "#64748b",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {doc.pnid_title || getDocumentSecondaryLabel(doc)}
                          </div>
                          <div
                            style={{
                              marginTop: 3,
                              fontSize: 12,
                              color: "var(--raven-text-secondary)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {annotation?.status || "draft"}
                            {annotation?.updated_at ? ` - updated ${annotation.updated_at}` : ""}
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              padding: "3px 7px",
                              borderRadius: 4,
                              backgroundColor: "#f3f4f6",
                              color: "#374151",
                              whiteSpace: "nowrap",
                            }}>
                            {documentDomainPathLabel(domainIndex, doc)}
                          </span>
                          {selected && (
                            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--raven-purple-800)" }}>
                              Selected
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {selectedAnnotationImportSource && (
                <div
                  style={{
                    border: targetHasAnnotations ? "1px solid #fed7aa" : "1px solid #bfdbfe",
                    backgroundColor: targetHasAnnotations ? "#fff7ed" : "var(--raven-purple-100)",
                    borderRadius: 8,
                    padding: "10px 12px",
                    color: targetHasAnnotations ? "#9a3412" : "var(--raven-purple-900)",
                    fontSize: 12,
                    display: "flex",
                    flexDirection: "column",
                    gap: 5,
                  }}>
                  <div style={{ fontWeight: 800 }}>
                    {targetHasAnnotations ? "Confirmation required before replacing target annotations" : "Ready to import annotations"}
                  </div>
                  <div>
                    <strong>Import from:</strong> {getDocumentPrimaryLabel(selectedAnnotationImportSource)} - {documentDomainPathLabel(domainIndex, selectedAnnotationImportSource)} - {diagramTypeLabel(diagramMode)}
                  </div>
                  <div>
                    <strong>Into target:</strong> {getDocumentPrimaryLabel(selectedDoc)} - {documentDomainPathLabel(domainIndex, selectedDoc)} - {diagramTypeLabel(diagramMode)}
                  </div>
                  <div style={{ color: targetHasAnnotations ? "#c2410c" : "var(--raven-purple-800)", fontWeight: 700 }}>
                    {targetHasAnnotations
                      ? `Will replace ${targetAnnotationSummary || "existing target annotations"} after confirmation. Target metadata will be retained.`
                      : "Target has no existing annotations, so this will copy directly. Target metadata will be retained."}
                  </div>
                </div>
              )}
            </div>

            <div
              style={{
                padding: "12px 16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}>
              <button
                type="button"
                onClick={() => setAnnotationImportOpen(false)}
                disabled={annotationImporting}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  color: "#374151",
                  cursor: annotationImporting ? "not-allowed" : "pointer",
                  fontWeight: 600,
                }}>
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleImportAnnotations()}
                disabled={annotationImportDisabled || !annotationImportSourceId}
                style={{
                  padding: "8px 14px",
                  borderRadius: 6,
                  border: "none",
                  backgroundColor:
                    annotationImportDisabled || !annotationImportSourceId
                      ? "#cbd5e1"
                      : "var(--raven-purple-900)",
                  color: annotationImportDisabled || !annotationImportSourceId ? "#475569" : "white",
                  cursor:
                    annotationImportDisabled || !annotationImportSourceId
                      ? "not-allowed"
                      : "pointer",
                  fontWeight: 700,
                }}>
                {annotationImporting ? "Importing..." : targetHasAnnotations ? "Review overwrite" : "Import"}
              </button>
            </div>
          </div>
        </div>
      )}

      <DrawingSearchPalette
        open={searchOpen}
        hits={shownSearchHits}
        query={searchQuery}
        onQueryChange={setSearchQuery}
        recent={recentSearches}
        onPick={handleSearchPick}
        onClose={closeSearch}
      />

      {/* Version History Modal */}
      <VersionHistoryModal
        isOpen={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        docId={selectedDoc?.id || null}
        currentUser={effectiveUserName || undefined}
        onRestore={(restoredState) => {
          setPnidState(restoredState);
          if (selectedDoc?.id) {
            void documentApi.get(selectedDoc.id).then((doc) => {
              onPatchDocument(doc.id, doc);
              setDiagramMode(doc.diagram_type === 'pfd' ? 'pfd' : 'pid');
            }).catch((error) => {
              console.error("Failed to refresh restored document:", error);
            });
          }
          setPreviewingVersion(null);
          setOriginalStateBeforePreview(null);
          showAlert(
            "Restored",
            "Successfully restored to the selected version.",
            "success"
          );
        }}
        onPreview={(previewState, versionNumber) => {
          // Store current state before preview (only if not already previewing)
          if (!previewingVersion && pnidState) {
            setOriginalStateBeforePreview(pnidState);
          }
          setPnidState(previewState);
          setPreviewingVersion(versionNumber);
        }}
        previewingVersion={previewingVersion}
        onExitPreview={() => {
          // Restore original state
          if (originalStateBeforePreview) {
            setPnidState(originalStateBeforePreview);
          }
          setPreviewingVersion(null);
          setOriginalStateBeforePreview(null);
        }}
      />

      {/* Alert Dialog */}
      <AlertDialog
        isOpen={alertDialog.isOpen}
        title={alertDialog.title}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={closeAlert}
      />

      {/* Confirm Dialog (for delete/change confirmations) */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type || "danger"}
        confirmText={confirmDialog.confirmText || "Confirm"}
        cancelText="Cancel"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />

      <ConfirmDialog
        isOpen={discardDraftConfirm.isOpen}
        title="Discard the unsaved draft?"
        message={`This discards ${(discardDraftConfirm.openDrawingOnConfirm ? drawingLeaveDraftItems() : unsavedDraftItems()).join(' and ')}.`}
        type="warning"
        confirmText="Discard"
        cancelText="Keep editing"
        onConfirm={confirmDiscardDraft}
        onCancel={discardDraftConfirm.openDrawingOnConfirm
          ? keepEditingOverPickedDrawing
          : () => setDiscardDraftConfirm({ isOpen: false })}
      />

      {/* EquipmentOverviewDialog removed - functionality consolidated into PnidMarkDialog */}

      {/* NodeOverviewDialog removed - consolidated into PnidMarkDialog */}

      {/* Comment Popover */}
      {selectedCommentId &&
        selectedCommentScreenPos &&
        (() => {
          const comment = comments.find((c) => c.id === selectedCommentId);
          if (!comment) return null;
          return (
            <CommentPopover
              comment={comment}
              position={selectedCommentScreenPos}
              currentUser={effectiveUserName || "Anonymous"}
              onResolve={handleResolveComment}
              onUnresolve={handleUnresolveComment}
              onDelete={handleDeleteComment}
              onClose={() => {
                setSelectedCommentId(null);
                setSelectedCommentScreenPos(null);
              }}
            />
          );
        })()}

      {/* Line Segment Review Popover */}
      {lineSegmentReviewPopover &&
        dockView?.kind === 'lineSegmentReview' &&
        pnidState &&
        (() => {
          const node = pnidState.nodes.find(
            (n) => n.id === lineSegmentReviewPopover.nodeId
          );
          if (!node) return null;
          return (
            <LineSegmentReviewPopover
              node={node}
              hoveredDistinctId={lineSegmentReviewPopover.hoveredDistinctId}
              selectedDistinctId={lineSegmentReviewPopover.selectedDistinctId}
              segmentCoordinateInfo={segmentCoordinateInfo}
              onHoverDistinct={(id) =>
                setLineSegmentReviewPopover((p) =>
                  p ? { ...p, hoveredDistinctId: id } : p
                )
              }
              onSelectDistinct={(id) =>
                setLineSegmentReviewPopover((p) =>
                  p ? { ...p, selectedDistinctId: id } : p
                )
              }
              onConfirmDistinct={(distinct) =>
                updateNodeSegmentsForDistinct(node.id, distinct, "confirmed")
              }
              onDeleteDistinct={(distinct) =>
                updateNodeSegmentsForDistinct(node.id, distinct, "deleted")
              }
              onConfirmAll={() => confirmAllNodeSegments(node.id)}
              onClose={() => setLineSegmentReviewPopover(null)}
            />
          );
        })()}

      {/* Unified Instrument Editor (right-click any instrument on canvas) */}
      {/*
        While its own regions are redrawn the editor unmounts, and the dock says
        what is happening; the editor comes back, updated, when the draft commits.
      */}
      {instrumentEditorState && boxDraft?.target && (
        <EditorDock title="Drawing regions…">
          <p style={{ margin: 0, fontSize: 13, color: "var(--raven-text-secondary)" }}>
            Draw this marking&apos;s regions on the sheet. Enter or Done saves them.
          </p>
        </EditorDock>
      )}
      {instrumentEditorState && !boxDraft?.target && (
        <InstrumentEditor
          key={`${instrumentEditorState.instrument.id}:${instrumentEditorState.isUsingLocalSnapshot ? 'local' : 'backend'}`}
          instrument={instrumentEditorState.instrument}
          findings={instrumentEditorState.findings}
          onValidateDraft={previewingVersion === null
            ? (updates) => assetApi.validateDraftInstrument(selectedDoc.id, instrumentEditorState.instrument.id, updates)
            : undefined}
          onSave={handleInstrumentEditorSave}
          onDirtyChange={reportInstrumentEditorEdited}
          onDelete={handleInstrumentEditorDelete}
          onBack={instrumentOnForm ? closeInstrumentEditor : undefined}
          onClose={instrumentOnForm ? () => { closeInstrumentEditor(); closeMarkForm(); } : closeInstrumentEditor}
          onHighlightEntity={setHighlightedEntityId}
          onOpenPnidHyperlink={handleOpenPnidHyperlink}
          docInstruments={pnidState?.instruments ?? []}
          allNodes={pnidState?.nodes ?? []}
          allEquipments={pnidState?.equipments ?? []}
          allInstruments={unifiedInstrumentRefs}
          allDocuments={documents}
          currentDocId={selectedDoc?.id ?? null}
          currentDomainId={selectedDoc?.domain_id ?? null}
          currentPlant={selectedDoc?.plant ?? null}
          diagramMode={diagramMode}
          linkedDocumentStates={linkedDocumentStates}
          onLoadLinkedDocumentState={loadLinkedDocumentState}
          onOpenLinkedDocument={handleOpenLinkedDocument}
          readOnly={readOnlyWizard || Boolean(instrumentEditorState.isUsingLocalSnapshot)}
          latestInstrumentLoading={Boolean(instrumentEditorState.isUsingLocalSnapshot && !instrumentEditorState.latestLoadError)}
          latestInstrumentError={instrumentEditorState.latestLoadError ?? null}
          identifyPrefill={instrumentEditorState.identifyPrefill ?? null}
          onEditRegions={(instrumentId) => {
            setBoxDraft(
              armDraft(
                'instrument',
                {
                  kind:
                    instrumentEditorState.instrument.context_type === 'node'
                      ? 'nodeInstrument'
                      : 'equipmentInstrument',
                  id: instrumentId,
                  op: 'replace',
                },
                instrumentRegions(instrumentEditorState.instrument),
              ),
            );
          }}
        />
      )}
    </div>
    </div>
    </EditorDockSlotContext.Provider>
  );
};

export default DrawingWorkspace;
